'use strict';

const libPictProvider = require('pict-provider');
// The app's first-class segmented CodeMirror markdown editor (same component the inline-doc + Email Notifications
// editors use) — powers the support-documentation runbook editor. CodeMirror bundle is vendored + loaded app-wide.
const libMarkdownEditor = require('pict-section-markdowneditor');
// The shared, HTML-ESCAPING user-markdown renderer (marked + escapeHTML + safe URLs) — the same one every screen uses
// to render a remark, so composer notes/acknowledgements/resolutions display identically and safely in the timeline.
const libUserMarkdown = require('../../../../lib/web/headlight-dynamic-inputs-bundle/source/providers/_UserMarkdown.js');

const RNI_HASH = 'Support-Call';           // the Support Call ReportNamedInstance / DocumentType
const ENTRY_STATUS = 'New';                // SupportCallWorkflow States[0].Status (a new doc enters here)
const FORM_MOUNT = '#Support-FormHost';    // where the pict-section-form-host paints the call form
const LIST_MOUNT = '#Support-List';        // the in-place-repainted list body
const DOC_CAP = 400;                       // newest-first cap on the call list
const LIST_PAGE_SIZE = 25;                 // calls per page in the triage list (client-side pagination of the filtered set)

// The lifecycle statuses (from the seeded SupportCallWorkflow) → display chrome. Unknown statuses fall back to neutral.
const STATUS_META =
{
	New:          { label: 'New',          cls: 'sup-st-new',  icon: 'fa-inbox' },
	Acknowledged: { label: 'Acknowledged', cls: 'sup-st-ack',  icon: 'fa-check-double' },
	Tier1:        { label: 'Tier 1',       cls: 'sup-st-t1',   icon: 'fa-signal' },
	Tier2:        { label: 'Tier 2',       cls: 'sup-st-t2',   icon: 'fa-signal' },
	Tier3:        { label: 'Tier 3',       cls: 'sup-st-t3',   icon: 'fa-signal' },
	Resolved:     { label: 'Resolved',     cls: 'sup-st-done', icon: 'fa-circle-check' },
	Void:         { label: 'Void (spam)',  cls: 'sup-st-void', icon: 'fa-ban' },
};

// The workflow verbs (POST DocumentApproval {Action}) → button chrome. The available set for the current
// user+state comes from GET DocumentApproval/:id/State (.Actions); this only styles what the server offers.
const ACTION_META =
{
	Acknowledge:   { label: 'Acknowledge',        cls: 'sup-act-primary', icon: 'fa-check-double' },
	Resolve:       { label: 'Resolve',            cls: 'sup-act-success', icon: 'fa-check' },
	EscalateTier1: { label: 'Escalate to Tier 1', cls: 'sup-act-warn',    icon: 'fa-angle-up' },
	EscalateTier2: { label: 'Escalate to Tier 2', cls: 'sup-act-warn',    icon: 'fa-angles-up' },
	EscalateTier3: { label: 'Escalate to Tier 3', cls: 'sup-act-warn',    icon: 'fa-arrow-up-wide-short' },
	Reopen:        { label: 'Reopen',             cls: 'sup-act-neutral', icon: 'fa-rotate-left' },
	Void:          { label: 'Mark as spam / Void', cls: 'sup-act-danger', icon: 'fa-ban' },
};

// Call-activity comment kinds (stored in Comment.Metadata.Kind) → composer + timeline chrome. Acknowledgement/Resolution
// are communications to the caller (RECORDED now; a NotifyCaller flag is written so email can be turned on later without
// a data migration). FollowUp carries {NeedByDate, WaitingOn, NextAction, Cleared}. Transitions come from the approval log.
const ACTIVITY_KIND =
{
	Note:            { label: 'Note',            comLabel: 'Internal note',        icon: 'fa-note-sticky',  cls: 'sup-ak-note', toCaller: false },
	Acknowledgement: { label: 'Acknowledged',    comLabel: 'Acknowledge the caller',icon: 'fa-paper-plane', cls: 'sup-ak-ack',  toCaller: true },
	Resolution:      { label: 'Resolution sent', comLabel: 'Send resolution',      icon: 'fa-circle-check', cls: 'sup-ak-res',  toCaller: true },
	FollowUp:        { label: 'Follow-up',       comLabel: 'Set a follow-up',       icon: 'fa-flag',         cls: 'sup-ak-fu',   toCaller: false },
	Stage:           { label: 'Stage',           comLabel: '',                      icon: 'fa-diagram-project', cls: 'sup-ak-stage', toCaller: false },
};

// The composer offers these kinds (Stage is set from the delivery bar, not typed as a comment).
const COMPOSER_KINDS = [ 'Note', 'Acknowledgement', 'Resolution', 'FollowUp' ];

// Optional ticket-handling stage for a support case — a lightweight lifecycle alongside the Open/…/Resolved approval
// status. Ordered; a case moves Acknowledge → Triage → Follow-up → Resolved.
const IMPL_STAGES =
[
	{ id: 'Acknowledge', label: 'Acknowledge', cls: 'sup-stg-ack' },
	{ id: 'Triage',      label: 'Triage',      cls: 'sup-stg-triage' },
	{ id: 'FollowUp',    label: 'Follow-up',   cls: 'sup-stg-fu' },
	{ id: 'Resolved',    label: 'Resolved',    cls: 'sup-stg-resolved' },
];
const IMPL_STAGE_MAP = IMPL_STAGES.reduce((pAcc, pStage) => { pAcc[pStage.id] = pStage; return pAcc; }, {});

// The canonical priority policy — the platform default, overridable per-customer (Configuration ns SupportPortal, key
// PriorityPolicy). `Rules` are evaluated against a call; the HIGHEST-ranked match wins, else `Default`. A rule matches on
// Category, App, and/or a Keyword regex over the summary + details. `SLA` maps a priority to a first-response target
// (surfaced as guidance on the form + in metrics later). Kept data-shaped so the settings editor can round-trip it.
const DEFAULT_PRIORITY_POLICY =
{
	Default: 'Normal',
	Rules:
	[
		{ Keyword: 'outage|down|cannot log|can.?t log|locked out|payroll|deadline|data loss|urgent|emergency', Priority: 'Urgent' },
		{ Category: 'Bug', Priority: 'High' },
		{ Category: 'Access', Priority: 'High' },
		{ Category: 'Data', Priority: 'Normal' },
		{ Category: 'PasswordReset', Priority: 'Normal' },
		{ Category: 'HowTo', Priority: 'Low' },
	],
	SLA: { Urgent: '4 hours', High: '1 business day', Normal: '3 business days', Low: '5 business days' },
};

const MONTHS = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ];
const MON_SHORT = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];

// Category id → label (mirrors the seeded manifest's Issue.Category SelectOptions), for the metrics breakdown + column.
const CATEGORY_LABELS =
{
	HowTo: 'How-to question', Bug: 'Something isn\'t working', PasswordReset: 'Password reset',
	Access: 'Access / permissions', Data: 'Data question', Device: 'Device', Other: 'Other', Uncategorized: 'Uncategorized',
};

// Priority id → sort rank (0 = most urgent, floats to the top ascending) + chip chrome. A manual lexical sort would be
// wrong (High < Low < Normal < Urgent), so the table sorts on `rank`.
const PRIORITY_META =
{
	Urgent: { label: 'Urgent', cls: 'sup-prio-urgent', rank: 0 },
	High:   { label: 'High',   cls: 'sup-prio-high',   rank: 1 },
	Normal: { label: 'Normal', cls: 'sup-prio-normal', rank: 2 },
	Low:    { label: 'Low',    cls: 'sup-prio-low',    rank: 3 },
};
// Status worklist order for sorting (New first → Resolved, then Void last). Also the source for the Status quick-filter
// chips and the Status filter facet, so Void appears there too — managers can pull up voided/spam calls to review.
const STATUS_ORDER = { New: 0, Acknowledged: 1, Tier1: 2, Tier2: 3, Tier3: 4, Resolved: 5, Void: 6 };

// Keyword THEME rules (ported from the runbook miner) — derive a coarse "what is this about" theme from a ticket's
// subject + issue text when it has no explicit Category (every legacy import does). First match wins, so order from
// most-specific to most-generic; "Error/Bug" is last so a concrete theme is preferred over the catch-all "not working".
const THEME_RULES =
[
	{ key: 'Sync', rx: /\b(sync|syncing|synchroniz|uploading|not uploading|won'?t sync|stuck (uploading|syncing)|spinning)\b/i },
	{ key: 'Login & Access', rx: /\b(log ?in|login|log ?on|password|reset|can'?t sign|sign in|signin|locked out|credential|forgot)\b/i },
	{ key: 'User & Permissions', rx: /\b(new user|add user|user level|permission|reviewer|deactivate|remove user|student worker|account request|access to)\b/i },
	{ key: 'Project Import', rx: /\b(project import|import request|bulk import|migrat|load(ing)? data|import the|spreadsheet import)\b/i },
	{ key: 'Reports & IDR', rx: /\b(report|pdf|\bidr\b|\bdwr\b|export|dossier|print(out|ed)?|generat)\b/i },
	{ key: 'Forms & Data', rx: /\b(form|observation|field ?book|drop.?list|dropdown|sample|material|test|narrative|journal|data entry)\b/i },
	{ key: 'Photos & Media', rx: /\b(photo|image|picture|media|download|not available|thumbnail|gallery)\b/i },
	{ key: 'iPad & Device', rx: /\b(ipad|tablet|device|reinstall|re-install|offline|app (crash|froze|freez|version|update))\b/i },
	{ key: 'Billing & Pay Items', rx: /\b(payment|progress payment|invoice|billing|pay ?item|bid item|change order|contract admin)\b/i },
	{ key: 'Error / Bug', rx: /\b(error|crash|bug|broken|glitch|failed|failure|not working|doesn'?t work|issue with)\b/i },
];

// App id → label — which HeadLight product a call is about. Mirrors the manifest Issue.App SelectOptions; the list
// column/filter/sort read from here. Existing calls with no App show as Unassigned.
const APP_LABELS =
{
	Fieldbook: 'Fieldbook', Bridge: 'Bridge', Home: 'Home', LIMS: 'LIMS',
	Configuration: 'Configuration', Academy: 'Academy', Mobile: 'Mobile', Other: 'Other',
};

// The base multi-select filter facets (drives the toolbar dropdowns generically). `valueFor` returns a call's value
// for that facet (compared as a string). A cross-customer Customer facet is appended at runtime for super-users.
const FILTER_FACETS =
[
	{ key: 'Status',   label: 'Status',   options: () => Object.keys(STATUS_ORDER),  labelFor: (v) => (STATUS_META[v] ? STATUS_META[v].label : v), valueFor: (c) => c.Status },
	{ key: 'Priority', label: 'Priority', options: () => Object.keys(PRIORITY_META), labelFor: (v) => (PRIORITY_META[v] ? PRIORITY_META[v].label : v), valueFor: (c) => c.Priority },
	{ key: 'Category', label: 'Category', options: () => Object.keys(CATEGORY_LABELS).filter((k) => k !== 'Uncategorized'), labelFor: (v) => (CATEGORY_LABELS[v] || v), valueFor: (c) => c.Category },
	{ key: 'App',      label: 'App',      options: () => Object.keys(APP_LABELS),    labelFor: (v) => (APP_LABELS[v] || v), valueFor: (c) => c.App },
];

/**
 * SupportProvider — the #/Support customer-service console. A support call is a Document of type `Support-Call`
 * (the seeded RNI form + SupportCallWorkflow), filed under the customer's revolving monthly Support Project;
 * attachments are Observations grouped as an ObservationSet on the document (the form's ObservationImageSet field).
 *
 * This provider owns the whole screen: the triage list of calls, the create/edit flow (rendered through the shared
 * SupportCallFormHost — a pict-section-form-host — with DataAddress AppData.DocumentData.ReportData.FormData so the
 * ObservationImageSet field and the production DocumentProvider auto-save agree on one FormData object), and the
 * approval-workflow actions (POST DocumentApproval). See [[config-app-support-portal]].
 */
class SupportProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._mount = '#Support-Host';
		this._manifest = null;              // the customer's Support-Call DefaultFormManifest (cached)
		this._manifestError = '';
		this._justCreatedIDDocument = 0;    // a doc created this session but not yet given content (discard-on-cancel)
		this._loadGen = 0;
		this._metrics = null;               // computed metrics rollup (Metrics view)
		this._metricsLoading = false;
		this._agentNames = {};              // IDUser -> display name (metrics + list resolver cache)
		this._trailingMonths = 6;
		this._portal = null;                // per-customer SupportPortal settings { SelfService, AllowedCategories, ... }
		this._customers = null;             // super-user cross-customer roster { IDCustomer: { Name, Code } }
		this._docsCustomerID = 0;           // Docs view: which customer's runbooks a super-user is managing (0 → session customer)
		this._capped = false;               // the load hit DOC_CAP (surfaced as a note)
		this._activity = { loading: false, items: [] };            // the open call's merged transition + comment timeline
		this._composer = { kind: 'Note', text: '', needBy: '', waitingOn: '' };   // the call-sheet comment composer (uncontrolled)
		// Close an open filter dropdown when clicking outside it (capture phase so it runs before row/button handlers).
		if (typeof document !== 'undefined')
		{
			document.addEventListener('click', (pEvent) =>
			{
				const tmpState = this.pict && this.pict.AppData && this.pict.AppData.Support;
				if (!tmpState || !tmpState.OpenDropdown || tmpState.Mode !== 'list') { return; }
				const tmpTarget = pEvent.target;
				if (tmpTarget && tmpTarget.closest && tmpTarget.closest('.sup-dd')) { return; }
				tmpState.OpenDropdown = '';
				this._paint();
			}, true);
		}
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get permissions() { return this.pict.fable.EffectivePermissionsService; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	get formHost() { return this.pict.providers.SupportCallFormHost; }
	get idCustomer() { return Number((this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0); }
	get idUser() { return Number((this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.UserID) || 0); }

	get state()
	{
		if (!this.pict.AppData.Support)
		{
			this.pict.AppData.Support =
			{
				Mode: 'list', View: 'calls', Loading: false, Error: '', Calls: [],
				Filter: '', Saving: false, Busy: false,
				// Multi-select filters (one array per facet; empty = all) + layered sort (first key primary) + which
				// facet dropdown is open. Default sort = triage order: open first, then most urgent, then newest.
				Filters: { Status: [], Priority: [], Category: [], App: [], Stage: [], Customer: [] },
				DateFilter: { from: '', to: '' },   // designated-date (email timestamp) range, YYYY-MM-DD inclusive; '' = unbounded
				Sort: [ { key: 'Status', dir: 'asc' }, { key: 'Priority', dir: 'asc' }, { key: 'Updated', dir: 'desc' } ],
				ListPage: 0,   // current page of the filtered triage list (client-side; reset to 0 whenever the filter/search/sort changes)
				OpenDropdown: '',
				Selected: {},   // bulk-merge selection: { IDDocument: true } for tickets ticked in the list (manager-only)
				// Single-select dashboard filters (metrics view): '' = all. Support-user = resolver; customer-user = opener.
				MetricsFilters: { Customer: '', App: '', Category: '', Resolver: '', Opener: '' },
				// Dashboard DATE-RANGE scope — the metrics load their OWN set for this range (not the 400-cap call list),
				// so analytics span the whole 2020-2026 archive. Preset: 'last12' | 'all' | a 4-digit year.
				MetricsRange: { preset: 'last12' },
				FormMode: 'create', FormTitle: '', Active: null, Approval: null,
				RunbookOpen: true, RunbookSel: null,   // the read-only runbook companion beside the call form
			};
			// Sticky filters: overlay the last-used facets / date range / search / sort (survives nav + reload).
			Object.assign(this.pict.AppData.Support, this._loadPersistedFilters());
		}
		return this.pict.AppData.Support;
	}

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }

	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'info' }); } }

	/** The console is for support staff + self-service users: holders of Support-Agent or Support-Manage (or super-users). */
	canUse()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return true; }
		// Support staff OR a runbook editor (Document-edit holder) may open the console — the latter sees only the Docs surface (_isDocsOnly).
		return tmpPermissions.canHash('Support-Agent') || tmpPermissions.canHash('Support-Manage') || this._canAccessDocs();
	}

	/** Has a genuine SUPPORT role (agent/manager) or is a super-user — i.e. gets the full console, not the docs-only surface. */
	_hasSupportRole()
	{
		if (this._isSuperUser()) { return true; }
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return true; }
		return tmpPermissions.canHash('Support-Agent') || tmpPermissions.canHash('Support-Manage');
	}

	/** A RUNBOOK-ONLY user: can edit runbooks (Document-edit) but has no support role — the console shows ONLY the runbook
	 *  manager (no triage list, no calls, no metrics/settings). */
	_isDocsOnly() { return this._canAccessDocs() && !this._hasSupportRole(); }

	/**
	 * Full support management — triage every call, all categories, metrics, run the workflow. Super-users get this
	 * automatically (canHash → true); customer support leads get the `Support-Manage` grant. A user who only holds
	 * `Support-Agent` is a TIER-1 self-service user (files + tracks their own calls, categories restricted per config).
	 */
	canManage()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return true; }
		return tmpPermissions.canHash('Support-Manage');
	}

	/** Who may EDIT runbooks (add / upload / edit / delete). Platform (global) scope = super-user, since DocsGlobal is
	 *  cross-customer platform config. Customer scope = a support manager OR anyone holding the Document edit capability
	 *  (Document-Update) for the session customer — the "edit permissions for Documents" gate Steven asked for. */
	canEditRunbooks(pScope)
	{
		if (pScope === 'global') { return this._isSuperUser(); }
		if (this.canManage()) { return true; }   // also covers the perms-not-ready permissive case
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.can !== 'function') || !tmpPermissions.currentUserReady()) { return false; }
		return tmpPermissions.can('Document', 'Update');
	}

	/** May open the runbook manager (Docs view): a customer- or platform-scope runbook editor. (Metrics/Settings stay
	 *  manager-only; this opens ONLY the docs/runbook surface. The read-only call-side runbook aside stays manager-only.) */
	_canAccessDocs() { return this.canEditRunbooks('customer') || this.canEditRunbooks('global'); }

	/** A tier-1 self-service user: can use the console but is not a manager. Drives category + own-calls scoping. */
	isSelfService() { return this.canUse() && !this.canManage(); }

	/** Category ids a self-service user may file under (empty/absent list = all). Managers are never restricted. */
	_allowedCategories()
	{
		if (this.canManage()) { return null; }
		const tmpAllowed = this._portal && Array.isArray(this._portal.AllowedCategories) ? this._portal.AllowedCategories : [];
		return tmpAllowed.length ? tmpAllowed : null;
	}

	/** App ids a self-service user may file under (per-customer, mirrors AllowedCategories). Managers unrestricted. */
	_allowedApps()
	{
		if (this.canManage()) { return null; }
		const tmpAllowed = this._portal && Array.isArray(this._portal.AllowedApps) ? this._portal.AllowedApps : [];
		return tmpAllowed.length ? tmpAllowed : null;
	}

	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	/** Render a comment/remark body as SAFE markdown HTML (HTML is escaped before markdown, so a `<script>` in a comment is inert). */
	_renderMd(pValue) { try { return libUserMarkdown.renderUserMarkdown(String(pValue == null ? '' : pValue)); } catch (pError) { return this._esc(pValue); } }

	_date(pValue)
	{
		const tmpString = String(pValue == null ? '' : pValue);
		if (!tmpString || tmpString.indexOf('0000-00-00') === 0) { return '—'; }
		// A bare YYYY-MM-DD (e.g. a follow-up need-by) parses as UTC midnight → renders a day early in western zones; read it as a local date.
		const tmpDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tmpString);
		const tmpDate = tmpDateOnly ? new Date(Number(tmpDateOnly[1]), Number(tmpDateOnly[2]) - 1, Number(tmpDateOnly[3])) : new Date(tmpString);
		return Number.isNaN(tmpDate.getTime()) ? tmpString : tmpDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}

	/** Normalize any date value — ISO, MySQL DATETIME, or a raw RFC822 email `Date` header — to an ISO-8601 string, or ''.
	 *  The designated date comes from mixed sources (DocumentDate is ISO; a mailbox ticket's stored Meta.ReceivedAt is the
	 *  raw email header), so normalizing here lets the column sort lexically and the YYYY-MM-DD range filter slice cleanly. */
	_isoDate(pValue)
	{
		const tmpString = String(pValue == null ? '' : pValue).trim();
		if (!tmpString || tmpString.indexOf('0000-00-00') === 0) { return ''; }
		const tmpDate = new Date(tmpString);
		return Number.isNaN(tmpDate.getTime()) ? '' : tmpDate.toISOString();
	}

	_statusMeta(pStatus) { return STATUS_META[pStatus] || { label: pStatus || 'New', cls: 'sup-st-new', icon: 'fa-circle' }; }
	_prioMeta(pPriority) { return PRIORITY_META[pPriority] || { label: pPriority || '—', cls: 'sup-prio-none', rank: 9 }; }
	_appLabel(pApp) { return pApp ? (APP_LABELS[pApp] || pApp) : '—'; }
	_catLabelList(pCat) { return pCat ? (CATEGORY_LABELS[pCat] || pCat) : '—'; }

	/**
	 * Platform super-user (HeadLight customer 1 + role 5) — reads CurrentUser synchronously so it never fails open like
	 * the capability gates. This is exactly the identity the server exempts from tenant scoping, so it (and ONLY it) can
	 * read Support-Call documents across every customer → the cross-customer support queue.
	 */
	_isSuperUser()
	{
		const tmpUser = (this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return Number(tmpUser.UserRoleIndex) === 5 && Number(tmpUser.CustomerID) === 1;
	}

	_customerName(pIDCustomer)
	{
		const tmpRow = this._customers && this._customers[pIDCustomer];
		return tmpRow ? (tmpRow.Name || tmpRow.Code || `Customer ${pIDCustomer}`) : (pIDCustomer ? `Customer ${pIDCustomer}` : '—');
	}

	/** The active facets — the base four, plus a cross-customer Customer facet for super-users (roster-driven). */
	_facets()
	{
		let tmpFacets = FILTER_FACETS;
		// The Delivery facet appears only once calls actually carry a stage, so it stays out of the way otherwise.
		if ((this.state.Calls || []).some((pC) => pC.Stage))
		{
			tmpFacets = tmpFacets.concat([ {
				key: 'Stage', label: 'Delivery',
				options: () => IMPL_STAGES.map((pS) => pS.id),
				labelFor: (v) => (IMPL_STAGE_MAP[v] ? IMPL_STAGE_MAP[v].label : v),
				valueFor: (c) => c.Stage || '',
			} ]);
		}
		if (!this._isSuperUser() || !this._customers) { return tmpFacets; }
		const tmpCustomers = this._customers;
		return tmpFacets.concat([ {
			key: 'Customer', label: 'Customer',
			options: () => Object.keys(tmpCustomers).map(Number).filter(Boolean)
				.sort((pA, pB) => String(this._customerName(pA)).localeCompare(String(this._customerName(pB)))).map(String),
			labelFor: (v) => this._customerName(v),
			valueFor: (c) => String(c.CallerCustomer || ''),
		} ]);
	}

	/** Load the customer roster once (super-user only) for the Customer column/filter — rides the super-user session. */
	async _loadCustomersAsync()
	{
		if (this._customers || !this._isSuperUser()) { return; }
		try
		{
			const tmpRows = await this._getJSON('Customers/0/2000');
			const tmpMap = {};
			(Array.isArray(tmpRows) ? tmpRows : []).forEach((pC) => { tmpMap[pC.IDCustomer] = { Name: pC.Name, Code: pC.Code }; });
			this._customers = tmpMap;
		}
		catch (pError) { this._customers = {}; }
	}

	// ── Searchable customer pickers (pict-section-picker, not native <select>) ──────────────────────────────────
	// The customer roster is hundreds long, so every customer selector in the console is a searchable
	// pict-section-picker. All are super-user-only. Each is mounted into its <span> AFTER a paint (createPicker
	// needs a live DOM node), the same mount-after-paint pattern the contractor-matrix pickers use.

	get pickerProvider() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }

	/** DataProvider over the in-memory customer roster. pIDs (optional) restricts to that id set (e.g. the metrics
	 *  filter's customers-with-calls); null = the whole roster. Returns {results:[{Value,Text}], hasMore}. */
	_customerDataProvider(pIDs)
	{
		const tmpAllow = Array.isArray(pIDs) ? new Set(pIDs.map(Number)) : null;
		return (pTerm) =>
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
			const tmpRows = Object.keys(this._customers || {}).map(Number).filter(Boolean)
				.filter((pID) => !tmpAllow || tmpAllow.has(pID))
				.map((pID) => ({ Value: pID, Text: this._customerName(pID) }))
				.filter((pRow) => !tmpTerm || String(pRow.Text).toLowerCase().indexOf(tmpTerm) >= 0)
				.sort((pA, pB) => String(pA.Text).localeCompare(String(pB.Text)));
			return Promise.resolve({ results: tmpRows.slice(0, 60), hasMore: tmpRows.length > 60 });
		};
	}

	_customerResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			return Promise.resolve(tmpID ? { Value: tmpID, Text: this._customerName(tmpID) } : null);
		};
	}

	/** Mount a searchable customer picker into pMountSel (a no-op if the span isn't in the DOM). pOnChange(idCustomer). */
	_mountCustomerPicker(pMountSel, pName, pCurrentID, pOnChange, pIDs, pOpts)
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		if (tmpCA.getElement(pMountSel).length === 0) { return; }
		const tmpOpts = pOpts || {};
		if (!this.pict.AppData.SupportPickers) { this.pict.AppData.SupportPickers = {}; }
		const tmpInstance = tmpPicker.createPicker(`Support-${pName}`,
			{
				DestinationAddress: pMountSel,
				ValueAddress: `AppData.SupportPickers.${pName}`,
				Searchable: true,
				AllowClear: !!tmpOpts.allowClear,
				ClearLabel: tmpOpts.clearLabel || '— all —',
				Placeholder: tmpOpts.placeholder || 'Search customers…',
				DataProvider: this._customerDataProvider(pIDs),
				ResolveValue: this._customerResolveValue(),
				OnChange: (pValue) => pOnChange(Number(pValue) || 0),
			});
		if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
		if (tmpInstance && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(Number(pCurrentID) || ''); }
	}

	/** Mount whichever customer picker(s) belong to the current view (each self-guards on its span). Call after a paint. */
	_mountViewPickers()
	{
		if (!this._isSuperUser()) { return; }
		this._mountCustomerPicker('#Support-Set-Cust-Pick', 'SettingsCustomer',
			(this._settings && this._settings.scopeCustomer) || this.idCustomer,
			(pID) => this.setSettingsCustomer(pID), null, { placeholder: 'Search customers…' });
		const tmpMetricIDs = [ ...new Set((this._metricsCalls || []).map((pC) => Number(pC.CallerCustomer)).filter(Boolean)) ];
		this._mountCustomerPicker('#Support-Metric-Cust-Pick', 'MetricCustomer',
			Number(this.state.MetricsFilters && this.state.MetricsFilters.Customer) || 0,
			(pID) => this.setMetricFilter('Customer', pID ? String(pID) : ''), tmpMetricIDs,
			{ allowClear: true, clearLabel: 'All customers', placeholder: 'Search customers…' });
		this._mountCustomerPicker('#Support-Docs-Cust-Pick', 'DocsCustomer',
			this._docsCustomerID || this.idCustomer,
			(pID) => this.setDocsCustomer(pID), null, { placeholder: 'Search customers…' });
	}

	// ── Data loading ───────────────────────────────────────────────────────────────────────────────────────────

	async loadAsync()
	{
		const gen = ++this._loadGen;
		const tmpState = this.state;
		tmpState.Mode = 'list';
		// Decide docs-only vs full console only against a READY permission map — the permissive-when-unready fallback would
		// otherwise briefly run the full triage load (+ fetch the call queue) for a runbook-only user on a reload race.
		// Retry (capped ~6s) until perms resolve; past the cap (a failed perms load) proceed permissively, like the rest of the app.
		const tmpPerms = this.permissions;
		if (tmpPerms && (typeof tmpPerms.currentUserReady === 'function') && !tmpPerms.currentUserReady())
		{
			tmpState.Loading = true; tmpState.Error = ''; this._paint();
			if ((this._permLoadWait = (this._permLoadWait || 0) + 1) <= 40)
			{
				setTimeout(() => { if (gen === this._loadGen) { this.loadAsync(); } }, 150);
				return;
			}
		}
		this._permLoadWait = 0;
		// Runbook-only user (Document-edit, no support role): no triage/manifest — just load the runbooks and show the
		// Docs surface (the shell forces View='docs'). Skips the Support-Call manifest + call list they can't use.
		if (this._isDocsOnly())
		{
			tmpState.View = 'docs'; tmpState.Loading = false; tmpState.Error = ''; this._manifestError = '';
			this._docsCustomerID = this.idCustomer; this._pendingApply = null;   // nothing to replay into a triage surface they don't have
			await this._loadDocsAsync();   // populates this._docs + paints the shell (docs-only branch)
			return;
		}
		this._metrics = null;               // the call set is changing — recompute metrics on next Metrics view
		tmpState.Loading = true; tmpState.Error = '';
		this._paint();
		try
		{
			// The customer's configured Support-Call form (cache once); also gates whether Support is set up here.
			if (!this._manifest) { await this._loadFormManifestAsync(); }
			if (!this._portal) { await this._loadPortalSettingsAsync(); }
			if (this._isSuperUser() && !this._customers) { await this._loadCustomersAsync(); }
			await this._loadCallsAsync();
			if (gen !== this._loadGen) { return; }
		}
		catch (pError)
		{
			if (gen !== this._loadGen) { return; }
			tmpState.Error = (pError && pError.message) ? pError.message : String(pError);
		}
		finally
		{
			if (gen === this._loadGen)
			{
				tmpState.Loading = false; this._paint();
				// A route arrived before the console had loaded (cold deep-link to /Support/Metrics, /Support/Call/:id, …) —
				// apply it now that the manifest + calls are in.
				if (this._pendingApply) { const tmpApply = this._pendingApply; this._pendingApply = null; try { tmpApply(this); } catch (pErr) { /* best-effort */ } }
			}
		}
	}

	async _loadFormManifestAsync()
	{
		// Prefer the customer's OWN Support-Call form if they've overridden it, else the platform-global one (IDCustomer 0).
		const tmpRows = await this._getJSON(`ReportNamedInstances/FilteredTo/FBV~Hash~EQ~${RNI_HASH}~FBL~IDCustomer~INN~${this.idCustomer},0~FBV~Deleted~EQ~0/0/5`);
		const tmpList = Array.isArray(tmpRows) ? tmpRows : [];
		const tmpRNI = tmpList.find((pR) => Number(pR.IDCustomer) === this.idCustomer) || tmpList.find((pR) => Number(pR.IDCustomer) === 0) || null;
		if (!tmpRNI) { this._manifestError = 'not-configured'; throw new Error('Support isn’t set up yet. Run the support-portal seeder (global platform seed) to create the Support Call form.'); }
		let tmpOptions = tmpRNI.Options;
		if (typeof tmpOptions === 'string') { try { tmpOptions = JSON.parse(tmpOptions); } catch (pError) { tmpOptions = {}; } }
		const tmpManifest = tmpOptions && tmpOptions.FormDefinition && tmpOptions.FormDefinition.DefaultFormManifest;
		if (!tmpManifest || !tmpManifest.Descriptors) { this._manifestError = 'bad-manifest'; throw new Error('The Support Call form is configured but its manifest is empty.'); }
		this._manifest = tmpManifest;
		this._manifestError = '';
	}

	/** Per-customer self-service settings (Configuration namespace SupportPortal, hash Settings). Absent → defaults. */
	async _loadPortalSettingsAsync()
	{
		let tmpSettings = null;
		let tmpPolicy = null;
		try
		{
			const tmpBlob = await this._getJSON(`Configuration/For/SupportPortal/In/Customer/${this.idCustomer}`);
			tmpSettings = tmpBlob && tmpBlob.Settings ? tmpBlob.Settings : (tmpBlob && typeof tmpBlob.SelfService !== 'undefined' ? tmpBlob : null);
			if (tmpBlob && tmpBlob.PriorityPolicy) { tmpPolicy = tmpBlob.PriorityPolicy; }
		}
		catch (pError) { /* no portal config → defaults below */ }
		this._portal = tmpSettings || { SelfService: false, AllowedCategories: [] };
		// Cascaded (Global → Customer) priority policy; fall back to the platform canonical when unset.
		this._priorityPolicy = (tmpPolicy && Array.isArray(tmpPolicy.Rules)) ? tmpPolicy : DEFAULT_PRIORITY_POLICY;
	}

	/** Evaluate the priority policy against a call's form → { priority, reason } (highest-ranked match wins), or null. */
	_suggestPriority(pFormData)
	{
		const tmpPolicy = this._priorityPolicy || DEFAULT_PRIORITY_POLICY;
		const tmpIssue = (pFormData && pFormData.Issue) || {};
		const tmpHay = `${tmpIssue.Summary || ''} ${tmpIssue.Details || ''} ${tmpIssue.StepsToReproduce || ''}`.toLowerCase();
		let tmpBest = null;
		(tmpPolicy.Rules || []).forEach((pRule) =>
		{
			let tmpMatch = true; let tmpReason = '';
			if (pRule.Category) { if (tmpIssue.Category !== pRule.Category) { tmpMatch = false; } else { tmpReason = this._catLabel(pRule.Category); } }
			if (tmpMatch && pRule.App) { if (tmpIssue.App !== pRule.App) { tmpMatch = false; } else { tmpReason = this._appLabel(pRule.App); } }
			if (tmpMatch && pRule.Keyword)
			{
				let tmpHit = false;
				try { tmpHit = new RegExp(pRule.Keyword, 'i').test(tmpHay); } catch (pErr) { tmpHit = tmpHay.indexOf(String(pRule.Keyword).toLowerCase()) >= 0; }
				if (!tmpHit) { tmpMatch = false; } else { tmpReason = 'keyword in the description'; }
			}
			if (!tmpMatch || !pRule.Priority) { return; }
			// rank is inverted (Urgent=0 is the most urgent) → the smallest rank is the highest priority.
			const tmpRank = (PRIORITY_META[pRule.Priority] || {}).rank;
			if (typeof tmpRank !== 'number') { return; }
			if (!tmpBest || tmpRank < tmpBest.rank) { tmpBest = { priority: pRule.Priority, reason: tmpReason, rank: tmpRank }; }
		});
		if (tmpBest) { return { priority: tmpBest.priority, reason: tmpBest.reason }; }
		return tmpPolicy.Default ? { priority: tmpPolicy.Default, reason: 'default' } : null;
	}

	async _loadCallsAsync()
	{
		// Super-users (customer-1 / role-5) read the WHOLE platform's support queue — the server injects no tenant filter
		// for them, so we omit the IDCustomer clause. Everyone else is hard-scoped to their own customer server-side.
		const tmpScope = this._isSuperUser() ? '' : `FBV~IDCustomer~EQ~${this.idCustomer}~`;
		// A tier-1 self-service user sees only the calls they filed; managers/staff see the whole (customer/platform) queue.
		const tmpOwn = this.isSelfService() ? `~FBV~CreatingIDUser~EQ~${this.idUser}` : '';
		const tmpFilter = `Documents/FilteredTo/${tmpScope}FBV~DocumentType~EQ~${RNI_HASH}~FBV~Deleted~EQ~0${tmpOwn}~FSF~IDDocument~DESC~0/0/${DOC_CAP}`;
		const tmpRows = await this._getJSON(tmpFilter);
		const tmpList = Array.isArray(tmpRows) ? tmpRows : [];
		this._capped = tmpList.length >= DOC_CAP;
		this.state.Calls = tmpList.map((pDoc) => this._callSummary(pDoc));
		// Resolve the "Last Updated By" display names (batched, cached) so the column is populated on first paint. A failed
		// lookup is a SECONDARY concern — degrade to the 'User <id>' fallback, never red-banner the whole (already-loaded) list.
		try { await this._resolveAgentNames(this.state.Calls.map((pC) => pC.UpdatedBy), this._loadGen); }
		catch (pError) { /* names are a non-critical overlay */ }
	}

	/** Distill a Document row into the list card model, reading the caller/issue fields out of FormData when present. */
	_callSummary(pDoc)
	{
		let tmpForm = pDoc.FormData;
		if (typeof tmpForm === 'string') { try { tmpForm = JSON.parse(tmpForm); } catch (pError) { tmpForm = {}; } }
		tmpForm = tmpForm || {};
		const tmpCaller = tmpForm.Caller || {};
		const tmpIssue = tmpForm.Issue || {};
		const tmpResolution = tmpForm.Resolution || {};
		// Legacy mailbox-import docs carry their TRUE dates in DocumentDate + FormData.Meta (the audit CreateDate/
		// UpdateDate and the approval-log CreateDate are all stamped at import time, which the API won't backdate). So
		// for these the list + metrics read the real email dates and the real caller, not the import stamps.
		const tmpMeta = tmpForm.Meta || {};
		const tmpLegacy = tmpMeta.Source === 'Mailbox-Legacy';
		// A support call still at the born 'Draft' status is conceptually Open (not yet worked) — normalize so the
		// list chips + badges group it under Open.
		const tmpStatus = STATUS_META[pDoc.Status] ? pDoc.Status : ENTRY_STATUS;
		return {
			IDDocument: Number(pDoc.IDDocument),
			IDProject: Number(pDoc.IDProject) || 0,
			IDCustomer: Number(pDoc.IDCustomer) || 0,   // the DOCUMENT's customer (= 1 for the central HeadLight queue)
			// The CALLER's customer (the org the ticket is ABOUT) — the meaningful dimension on the cross-customer queue,
			// where every doc's IDCustomer is 1. Drives the dashboard's By-customer card + Customer filter.
			CallerCustomer: Number(tmpCaller.IDCustomer) || 0,
			Status: tmpStatus,
			StateName: pDoc.StateName || '',
			Name: pDoc.Name || '',
			Summary: tmpIssue.Summary || pDoc.Name || '(no summary)',
			Caller: tmpCaller.Name || '',
			Category: tmpIssue.Category || '',
			// Derived subject/issue theme — real signal for the dashboard where legacy tickets carry no explicit Category.
			Theme: tmpIssue.Category || this._deriveTheme(tmpIssue.Summary, tmpIssue.Details),
			Priority: tmpIssue.Priority || '',
			App: tmpIssue.App || '',
			Stage: (tmpForm.Implementation && tmpForm.Implementation.Stage) || '',   // optional delivery lifecycle (Phase 4)
			// Phase-1 follow-up indicator: the existing Resolution.FollowUpNeeded boolean, surfaced in the list. Made
			// functional (need-by date / waiting-on) in the comms phase.
			FollowUp: tmpResolution.FollowUpNeeded === true || tmpResolution.FollowUpNeeded === 'true' || tmpResolution.FollowUpNeeded === 1,
			// Opened-by is the caller for legacy docs (the import ran as the operator, so CreatingIDUser is the operator).
			OpenedBy: tmpLegacy ? (Number(tmpCaller.IDUser) || 0) : (Number(pDoc.CreatingIDUser) || 0),
			// Who on the team last actioned the call (the Document's last writer). Name resolved on list load via _resolveAgentNames.
			UpdatedBy: Number(pDoc.UpdatingIDUser) || 0,
			Created: tmpLegacy ? (pDoc.DocumentDate || tmpMeta.FirstEmail || tmpMeta.OpenedAt || pDoc.CreateDate || '') : (pDoc.CreateDate || ''),
			Updated: tmpLegacy ? (tmpMeta.LastEmail || tmpMeta.ResolvedAt || pDoc.DocumentDate || pDoc.UpdateDate || '') : (pDoc.UpdateDate || pDoc.CreateDate || ''),
			// The "designated date" = the timestamp on the originating email, distinct from the audit Updated/UpdateDate.
			// Read the AUTHORITATIVE email timestamp the ingest stored on the ticket itself (legacy → Meta.FirstEmail;
			// live mailbox → Meta.ReceivedAt, the raw email Date header), falling back to DocumentDate then CreateDate.
			// Reading Meta here is what fixes the OLD tickets whose DocumentDate was stamped at import time, not send time —
			// no data migration needed. _isoDate normalizes the (possibly RFC822) value so sort + the range filter work.
			Designated: this._isoDate(tmpLegacy ? (tmpMeta.FirstEmail || pDoc.DocumentDate) : (tmpMeta.ReceivedAt || pDoc.DocumentDate || pDoc.CreateDate)),
			// Legacy overlay for the metrics resolve map (the approval log can't carry the real close date/agent).
			Legacy: tmpLegacy,
			ResolvedAt: tmpLegacy ? (tmpMeta.ResolvedAt || '') : '',
			ResolvedBy: tmpLegacy ? (Number(tmpMeta.ResolvedByIDUser) || 0) : 0,
		};
	}

	// ── The revolving monthly Support Project ──────────────────────────────────────────────────────────────────

	/** Find-or-create this month's per-customer Support Project by deterministic GUID (server stamps the customer). */
	async _ensureMonthlyProjectAsync()
	{
		const tmpNow = new Date();
		const tmpYM = `${tmpNow.getFullYear()}-${String(tmpNow.getMonth() + 1).padStart(2, '0')}`;
		const tmpGUID = `Support-${this.idCustomer}-${tmpYM}`;
		const tmpName = `Support — ${MONTHS[tmpNow.getMonth()]} ${tmpNow.getFullYear()}`;
		const tmpEnc = encodeURIComponent(tmpGUID);

		const tmpLive = await this._getJSON(`Projects/FilteredTo/FBV~GUIDProject~EQ~${tmpEnc}/0/2`);
		if (Array.isArray(tmpLive) && tmpLive[0]) { return tmpLive[0]; }

		// A soft-deleted row holds the GUID permanently (Meadow uniqueness sees deleted rows); fall back to a suffix.
		let tmpGUIDToUse = tmpGUID;
		try
		{
			const tmpAny = await this._getJSON(`Projects/FilteredTo/FBV~GUIDProject~EQ~${tmpEnc}/0/2?includeDeleted=true`);
			if (Array.isArray(tmpAny) && tmpAny[0]) { tmpGUIDToUse = `${tmpGUID}-2`; }
		}
		catch (pError) { /* probe best-effort; fall through to the canonical GUID */ }

		const tmpSaved = await this._postJSON('Project', { Name: tmpName, GUIDProject: tmpGUIDToUse, Status: 'Support', Description: 'Revolving monthly bucket for customer support calls.' });
		if (!tmpSaved || tmpSaved.ErrorCode || tmpSaved.Error || !tmpSaved.IDProject)
		{
			throw new Error((tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode)) ? String(tmpSaved.Error || tmpSaved.ErrorCode) : 'Could not create this month’s Support project.');
		}
		return tmpSaved;
	}

	/**
	 * Move a document into the SupportCallWorkflow entry state ('New'). Documents are created 'Draft' (the create
	 * ignores a client Status), which the custom workflow cannot resolve — a status-override PUT sets the declared
	 * entry state so the approval bar and its actions come alive. This is initialization, not an approval bypass;
	 * every real transition still goes through POST DocumentApproval (which logs it).
	 */
	async _ensureWorkflowEntryAsync(pIDDocument)
	{
		try { await this._putJSON('Document', { IDDocument: Number(pIDDocument), Status: ENTRY_STATUS, StateName: 'New', _StatusOverride: true }); }
		catch (pError) { /* best-effort; the action bar falls back to a status-only view */ }
	}

	// ── Create / open / save a call ────────────────────────────────────────────────────────────────────────────

	/**
	 * New call: ensure the monthly project, create the Document up-front (blank, Open) so the ObservationImageSet
	 * field has an IDDocument to attach to, then open the form editor on it.
	 */
	async startCreate()
	{
		const tmpState = this.state;
		if (this._isDocsOnly()) { return; }   // runbook-only user has no call-filing surface
		if (tmpState.Busy) { return; }
		if (!this._manifest) { this._toast('The Support Call form isn’t available.', 'error'); return; }
		tmpState.Busy = true; tmpState.Error = '';
		this._toast('Opening a new support call…', 'info');
		try
		{
			const tmpProject = await this._ensureMonthlyProjectAsync();
			const tmpNow = (new Date()).toISOString();
			const tmpDoc = await this._postJSON('Document',
				{ Name: 'New support call', DocumentDate: tmpNow, IDProject: Number(tmpProject.IDProject), DocumentType: RNI_HASH, Form: 1, FormData: {}, Status: ENTRY_STATUS, Observations: [] });
			if (!tmpDoc || tmpDoc.ErrorCode || tmpDoc.Error || !tmpDoc.IDDocument)
			{
				throw new Error((tmpDoc && (tmpDoc.Error || tmpDoc.ErrorCode)) ? String(tmpDoc.Error || tmpDoc.ErrorCode) : 'The server rejected the new call.');
			}
			this._justCreatedIDDocument = Number(tmpDoc.IDDocument);
			// A new Document is born 'Draft' (the create ignores a client Status); the SupportCallWorkflow has no
			// 'Draft' state, so move it to the workflow entry state 'New' before the approval bar reads its state.
			await this._ensureWorkflowEntryAsync(Number(tmpDoc.IDDocument));
			tmpState.Busy = false;
			this._openDoc({ IDDocument: Number(tmpDoc.IDDocument), IDProject: Number(tmpProject.IDProject), Status: ENTRY_STATUS, FormData: {} }, 'create', tmpProject);
			// The new draft now exists and has its own id, so the create form IS the call form — give it the call's route so
			// a reload / bookmark reopens THIS draft (instead of filing another). Use a REAL hash write, not replaceState:
			// routeApplyCall no-ops when the call is already the open form (Active.IDDocument matches, set by _openDoc above),
			// so this syncs the URL AND the router WITHOUT re-opening the form or dropping 'create' mode. replaceState would
			// leave the router's last-resolved path stale, so a later navigateView('calls') would look like a no-op to it.
			if ((typeof window !== 'undefined') && window.location) { window.location.hash = `#/Support/Call/${Number(tmpDoc.IDDocument)}`; }
		}
		catch (pError)
		{
			tmpState.Busy = false;
			this._toast(`Could not open a new call: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
	}

	/** Open an existing call from the list into the form editor. */
	async openCall(pIDDocument)
	{
		const tmpState = this.state;
		if (this._isDocsOnly()) { return; }   // runbook-only user can't open calls (no triage access)
		if (tmpState.Busy) { return; }
		// Leaving the inline merge view to open a call (e.g. browser Back to a /Support/Call/:ID hash): tear the wizard down
		// and drop out of 'merge' BEFORE the first paint, so a failed fetch can't strand the user on an empty merge shell
		// (_openDoc's own merge teardown only runs on fetch success, after this first paint has already wiped the accordion).
		if (tmpState.Mode === 'merge')
		{
			const tmpMerge = this.pict.providers.SupportMerge;
			if (tmpMerge && (typeof tmpMerge.teardown === 'function')) { tmpMerge.teardown(); }
			tmpState.Mode = 'list';
		}
		tmpState.Busy = true; tmpState.Error = '';
		this._paint();
		try
		{
			const tmpDoc = await this._getJSON(`Document/${Number(pIDDocument)}`);
			if (!tmpDoc || !tmpDoc.IDDocument) { throw new Error('Could not load that call.'); }
			// Self-heal any call still sitting at the born 'Draft' status (e.g. created before it was worked) so its
			// approval bar resolves against the SupportCallWorkflow.
			if (!STATUS_META[tmpDoc.Status]) { await this._ensureWorkflowEntryAsync(Number(tmpDoc.IDDocument)); tmpDoc.Status = ENTRY_STATUS; }
			this._justCreatedIDDocument = 0;
			tmpState.Busy = false;
			this._openDoc(tmpDoc, 'edit');
		}
		catch (pError)
		{
			tmpState.Busy = false;
			this._toast(`Could not open the call: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			this._paint();
		}
	}

	/**
	 * Set up the document context the form + attachments read (AppData.DocumentData.ReportData), paint the form
	 * shell, mount the manifest, and fetch the approval state for the action bar.
	 */
	_openDoc(pDoc, pMode, pProject)
	{
		const tmpState = this.state;
		// Opening a call while the inline merge view is up (e.g. a deep link) → drop the wizard's transient state first.
		if (tmpState.Mode === 'merge') { const tmpMerge = this.pict.providers.SupportMerge; if (tmpMerge && (typeof tmpMerge.teardown === 'function')) { tmpMerge.teardown(); } }
		let tmpFormData = pDoc.FormData;
		if (typeof tmpFormData === 'string') { try { tmpFormData = JSON.parse(tmpFormData); } catch (pError) { tmpFormData = {}; } }
		tmpFormData = tmpFormData || {};

		// The document context every media/attachment control + the DocumentProvider auto-save reads.
		this.pict.AppData.DocumentData =
		{
			ReportData:
			{
				IDDocument: Number(pDoc.IDDocument),
				IDProject: Number(pDoc.IDProject) || 0,
				DocumentType: RNI_HASH,
				FormData: tmpFormData,
				Status: pDoc.Status || ENTRY_STATUS,
				CanEdit: true,
				Locked: false,
				Observations: Array.isArray(pDoc.Observations) ? pDoc.Observations : [],
			},
		};

		tmpState.Mode = 'form';
		tmpState.FormMode = pMode;
		tmpState.Active =
		{
			IDDocument: Number(pDoc.IDDocument),
			IDProject: Number(pDoc.IDProject) || 0,
			Status: pDoc.Status || ENTRY_STATUS,
			ProjectName: (pProject && pProject.Name) || '',
			Summary: (tmpFormData.Issue && tmpFormData.Issue.Summary) || '',
		};
		tmpState.FormTitle = pMode === 'create' ? 'New support call' : (tmpState.Active.Summary || `Call #${tmpState.Active.IDDocument}`);
		tmpState.Approval = null;
		this._composer = { kind: 'Note', text: '', needBy: '', waitingOn: '' };
		this._activity = { loading: pMode !== 'create', items: [] };
		this._paint();
		this._mountForm();
		if (this.canManage()) { this._ensureRunbooksAsync(); this._loadCallerRunbooksAsync((tmpFormData.Caller && tmpFormData.Caller.IDCustomer) || 0); }   // read-only runbook aside: platform runbooks + THIS caller's customer playbook
		this._refreshApprovalAsync(Number(pDoc.IDDocument));
		if (pMode !== 'create') { this._loadActivityAsync(Number(pDoc.IDDocument)); }
	}

	/** The manifest to render — for a tier-1 self-service user with a restricted category list, a filtered clone. */
	_effectiveManifest()
	{
		const tmpAllowedCats = this._allowedCategories();
		const tmpAllowedApps = this._allowedApps();
		if ((!tmpAllowedCats && !tmpAllowedApps) || !this._manifest) { return this._manifest; }
		const tmpManifest = JSON.parse(JSON.stringify(this._manifest));
		const tmpRestrict = (pAddress, pAllowed) =>
		{
			if (!pAllowed) { return; }
			const tmpDesc = tmpManifest.Descriptors && tmpManifest.Descriptors[pAddress];
			if (tmpDesc && tmpDesc.PictForm && Array.isArray(tmpDesc.PictForm.SelectOptions))
			{
				tmpDesc.PictForm.SelectOptions = tmpDesc.PictForm.SelectOptions.filter((pOpt) => pAllowed.indexOf(pOpt.id) >= 0);
				// One allowed value → default it so a self-service caller doesn't have to choose.
				if (tmpDesc.PictForm.SelectOptions.length === 1) { tmpDesc.Default = tmpDesc.PictForm.SelectOptions[0].id; }
			}
		};
		tmpRestrict('Issue.Category', tmpAllowedCats);
		tmpRestrict('Issue.App', tmpAllowedApps);
		return tmpManifest;
	}

	_mountForm()
	{
		const tmpHost = this.formHost;
		if (!tmpHost || (typeof tmpHost.loadForm !== 'function'))
		{
			this.state.Error = 'The support-call form runner is not available.';
			this._paint();
			return;
		}
		try
		{
			tmpHost.loadForm(this._effectiveManifest(), { DestinationAddress: FORM_MOUNT, DataAddress: 'AppData.DocumentData.ReportData.FormData' });
		}
		catch (pError)
		{
			this.state.Error = `Could not open the support-call form: ${(pError && pError.message) ? pError.message : pError}`;
			this._paint();
		}
	}

	/** Compose a human list title from the form so the triage list reads well: "Summary — Caller". */
	_composeName(pFormData)
	{
		const tmpIssue = (pFormData && pFormData.Issue) || {};
		const tmpCaller = (pFormData && pFormData.Caller) || {};
		const tmpSummary = String(tmpIssue.Summary || '').trim();
		const tmpWho = String(tmpCaller.Name || '').trim();
		if (!tmpSummary) { return tmpWho ? `Support call — ${tmpWho}` : 'Support call'; }
		return tmpWho ? `${tmpSummary} — ${tmpWho}` : tmpSummary;
	}

	/** Marshal the form host and PUT the Document — no navigation, no toast. Shared by save() and performAction(). */
	async _persistFormDataAsync()
	{
		const tmpState = this.state;
		if (!tmpState.Active) { return null; }
		const tmpHost = this.formHost;
		if (tmpHost && (typeof tmpHost.save === 'function')) { try { tmpHost.save(); } catch (pError) { /* marshal best-effort */ } }
		const tmpFormData = (tmpHost && typeof tmpHost.getFormData === 'function')
			? (tmpHost.getFormData() || {})
			: ((this.pict.AppData.DocumentData && this.pict.AppData.DocumentData.ReportData && this.pict.AppData.DocumentData.ReportData.FormData) || {});
		if (!tmpFormData.Meta) { tmpFormData.Meta = {}; }
		tmpFormData.Meta.DocumentCreatorApp = 'Platform Configuration';
		// Priority policy: fill a blank priority from the policy (never override an explicit choice), and note why.
		const tmpIssue = tmpFormData.Issue || (tmpFormData.Issue = {});
		if (!tmpIssue.Priority)
		{
			const tmpSuggest = this._suggestPriority(tmpFormData);
			if (tmpSuggest && tmpSuggest.priority)
			{
				tmpIssue.Priority = tmpSuggest.priority;
				const tmpWhy = (tmpSuggest.reason && tmpSuggest.reason !== 'default') ? ` (${tmpSuggest.reason})` : '';
				this._toast(`Priority set to ${(PRIORITY_META[tmpSuggest.priority] || {}).label || tmpSuggest.priority} per policy${tmpWhy}.`, 'info');
			}
		}
		const tmpSaved = await this._putJSON('Document',
			{ IDDocument: tmpState.Active.IDDocument, IDProject: tmpState.Active.IDProject, DocumentType: RNI_HASH, Form: 1, FormData: tmpFormData, Name: this._composeName(tmpFormData) });
		if (!tmpSaved || tmpSaved.ErrorCode || tmpSaved.Error)
		{
			throw new Error((tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode)) ? String(tmpSaved.Error || tmpSaved.ErrorCode) : 'the server rejected the call.');
		}
		this._justCreatedIDDocument = 0;   // it now has content — no longer a discardable blank
		tmpState.Active.Summary = (tmpFormData.Issue && tmpFormData.Issue.Summary) || tmpState.Active.Summary;
		return tmpSaved;
	}

	async save()
	{
		const tmpState = this.state;
		if (tmpState.Saving || !tmpState.Active) { return; }
		tmpState.Saving = true;
		this._paintFormActions();
		try
		{
			await this._persistFormDataAsync();
			tmpState.Saving = false;
			this._toast('Support call saved.', 'success');
			// Stay on the call — do NOT loadAsync() (it sets Mode='list' and bounces back to the dashboard). Just reset the
			// Save button and refresh the status bar in place; the list reloads on its own when you navigate back (routeApply).
			this._paintFormActions();
			if (tmpState.Active) { this._refreshApprovalAsync(Number(tmpState.Active.IDDocument)); }
		}
		catch (pError)
		{
			tmpState.Saving = false;
			this._toast(`Could not save the call: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			this._paintFormActions();
		}
	}

	/** Back to the list. If the call was created this session and never given a summary, discard the blank draft. */
	async cancel()
	{
		// Don't lose an in-progress call-activity note — confirm, and scroll back to the composer if the user stays.
		if (this._hasUnsavedComposer())
		{
			const tmpOk = (typeof window !== 'undefined' && typeof window.confirm === 'function')
				? window.confirm('You have an unsaved note in Call activity. Leave without adding it?\n\n(Click Cancel to stay and add it.)')
				: true;
			if (!tmpOk) { this._scrollToComposer(); return; }
		}
		const tmpHost = this.formHost;
		const tmpState = this.state;
		let tmpDiscarded = false;
		if (this._justCreatedIDDocument && tmpState.Active && Number(tmpState.Active.IDDocument) === this._justCreatedIDDocument)
		{
			const tmpFormData = (tmpHost && typeof tmpHost.getFormData === 'function') ? (tmpHost.getFormData() || {}) : {};
			const tmpSummary = tmpFormData.Issue && String(tmpFormData.Issue.Summary || '').trim();
			const tmpHasAttachment = tmpFormData.Issue && tmpFormData.Issue.Attachments;
			if (!tmpSummary && !tmpHasAttachment)
			{
				try { await new Promise((resolve) => this.restClient.deleteEntity('Document', this._justCreatedIDDocument, () => resolve())); tmpDiscarded = true; }
				catch (pError) { /* best-effort discard */ }
			}
		}
		this._justCreatedIDDocument = 0;
		if (tmpHost && (typeof tmpHost.destroy === 'function')) { try { tmpHost.destroy(); } catch (pError) { /* best-effort */ } }
		if (tmpDiscarded) { this._toast('Discarded the empty call.', 'info'); }
		this.navigateView('calls');   // back to the list via the hash (router reloads it) so the URL + back-button stay in sync
	}

	// ── Approval workflow ──────────────────────────────────────────────────────────────────────────────────────

	async _refreshApprovalAsync(pIDDocument)
	{
		try
		{
			const tmpState = await this._getJSON(`DocumentApproval/${Number(pIDDocument)}/State`);
			if (!this.state.Active || Number(this.state.Active.IDDocument) !== Number(pIDDocument)) { return; }
			this.state.Approval =
			{
				Status: (tmpState && tmpState.Status) || this.state.Active.Status || ENTRY_STATUS,
				StateName: (tmpState && tmpState.StateName) || '',
				Locked: !!(tmpState && tmpState.Locked),
				Actions: (tmpState && Array.isArray(tmpState.Actions)) ? tmpState.Actions : [],
			};
			this.state.Active.Status = this.state.Approval.Status;
			this._syncCallStatus(Number(pIDDocument), this.state.Approval.Status);   // keep the in-memory list + status chips in step
			this._paintApproval();
		}
		catch (pError) { /* the action bar just stays minimal if the state read fails */ }
	}

	/** Reflect a workflow status change onto the loaded call in state.Calls (so the list + status chips are correct without
	 *  waiting for the return-to-list reload). */
	_syncCallStatus(pIDDocument, pStatus)
	{
		if (!pStatus) { return; }
		const tmpCall = (this.state.Calls || []).find((pCall) => Number(pCall.IDDocument) === Number(pIDDocument));
		if (tmpCall) { tmpCall.Status = pStatus; tmpCall.Updated = (new Date()).toISOString(); }
	}

	/** Advance the approval workflow to Resolved. In the tiered model Resolve is available directly from
	 *  Acknowledged / Tier1 / Tier2 / Tier3; from the New entry state it must Acknowledge first
	 *  (New→Acknowledge→Resolve). Escalations are never auto-taken here. Returns true once Resolved. */
	async _resolveViaWorkflowAsync(pIDDocument)
	{
		const tmpID = Number(pIDDocument);
		for (let i = 0; i < 3; i++)
		{
			const tmpState = await this._getJSON(`DocumentApproval/${tmpID}/State`);
			const tmpStatus = tmpState && tmpState.Status;
			if (tmpStatus === 'Resolved') { return true; }
			const tmpActions = (tmpState && Array.isArray(tmpState.Actions)) ? tmpState.Actions : [];
			const tmpNext = (tmpActions.indexOf('Resolve') >= 0) ? 'Resolve'
				: (tmpActions.indexOf('Acknowledge') >= 0) ? 'Acknowledge' : null;
			if (!tmpNext) { return false; }
			const tmpRes = await this._postJSON('DocumentApproval', { IDDocument: tmpID, Action: tmpNext });
			if (tmpRes && (tmpRes.ErrorCode || tmpRes.Error || (tmpRes.code && tmpRes.message))) { return false; }
		}
		return false;
	}

	async performAction(pAction)
	{
		const tmpState = this.state;
		if (tmpState.Busy || !tmpState.Active) { return; }
		tmpState.Busy = true;
		this._paintApproval();
		try
		{
			// Persist any in-progress form edits before the transition (a Resolve may lock the document) — but stay on
			// the form: a transition updates the status bar in place, it does not navigate back to the list.
			try { await this._persistFormDataAsync(); } catch (pSaveError) { /* the transition is the goal; a save hiccup shouldn't block it */ }
			const tmpRes = await this._postJSON('DocumentApproval', { IDDocument: tmpState.Active.IDDocument, Action: pAction });
			if (tmpRes && (tmpRes.ErrorCode || tmpRes.Error)) { throw new Error(String(tmpRes.Error || tmpRes.ErrorCode)); }
			tmpState.Busy = false;
			this._toast(`Marked: ${(ACTION_META[pAction] && ACTION_META[pAction].label) || pAction}.`, 'success');
			await this._refreshApprovalAsync(tmpState.Active.IDDocument);
			if (tmpState.Active) { tmpState.FormTitle = tmpState.Active.Summary || tmpState.FormTitle; }
		}
		catch (pError)
		{
			tmpState.Busy = false;
			this._toast(`Could not ${pAction}: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			this._paintApproval();
		}
	}

	// ── Call activity: comms (acknowledge/resolve), functional follow-up, and a who-did-what history ─────────────

	_dateTime(pValue)
	{
		const tmpS = String(pValue || ''); if (!tmpS) { return ''; }
		const tmpD = new Date(tmpS); if (Number.isNaN(tmpD.getTime())) { return tmpS; }
		return tmpD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + tmpD.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
	}

	/** Load the open call's timeline = DocumentApprovalLog transitions ⊕ DocumentComment stream, actor-resolved. */
	async _loadActivityAsync(pIDDocument)
	{
		this._activity = { loading: true, items: [] };
		this._paintActivity();
		try
		{
			const [ tmpLogs, tmpComments ] = await Promise.all([
				this._getJSON(`DocumentApprovalLogs/FilteredTo/FBV~IDDocument~EQ~${pIDDocument}~FBV~Deleted~EQ~0~FSF~CreateDate~DESC~0/0/200`).catch(() => []),
				this._getJSON(`DocumentComments/FilteredTo/FBV~IDDocument~EQ~${pIDDocument}~FBV~Deleted~EQ~0/0/200`).catch(() => []),
			]);
			if (!this.state.Active || Number(this.state.Active.IDDocument) !== Number(pIDDocument)) { return; }
			// Legacy docs: the approval-log CreateDate is import-stamped, so read the transition's true date from
			// FormData.Meta (Open → first email, Resolve → last email).
			let tmpMeta = {};
			try { const tmpRD = this.pict.AppData.DocumentData && this.pict.AppData.DocumentData.ReportData; const tmpFD = tmpRD && tmpRD.FormData; tmpMeta = ((typeof tmpFD === 'string') ? JSON.parse(tmpFD || '{}') : (tmpFD || {})).Meta || {}; } catch (pErr) { tmpMeta = {}; }
			const tmpIsLegacy = tmpMeta.Source === 'Mailbox-Legacy';
			const tmpItems = [];
			(Array.isArray(tmpLogs) ? tmpLogs : []).forEach((pL) =>
			{
				if (!pL.Action && !pL.AfterStatus) { return; }   // skip the silent born-Draft → Open status override
				let tmpAt = pL.CreateDate || '';
				if (tmpIsLegacy)
				{
					if (Number(pL.IsApproval) === 1 || /resolve/i.test(pL.Action || '')) { tmpAt = tmpMeta.ResolvedAt || tmpMeta.LastEmail || tmpAt; }
					else if (/open|acknowledge/i.test(pL.Action || '') || pL.AfterStatus === 'Open' || pL.AfterStatus === 'New' || pL.AfterStatus === 'Acknowledged') { tmpAt = tmpMeta.OpenedAt || tmpMeta.FirstEmail || tmpAt; }
				}
				tmpItems.push({ type: 'transition', action: pL.Action, status: pL.AfterStatus, isApproval: Number(pL.IsApproval) === 1, by: Number(pL.CreatingIDUser) || 0, at: tmpAt });
			});
			(Array.isArray(tmpComments) ? tmpComments : []).forEach((pC) =>
			{
				let tmpMeta = {}; try { tmpMeta = pC.Metadata ? JSON.parse(pC.Metadata) : {}; } catch (pErr) { tmpMeta = {}; }
				tmpItems.push({ type: 'comment', kind: tmpMeta.Kind || 'Note', text: pC.Comment || '', meta: tmpMeta, by: Number(pC.CreatingIDUser) || 0, at: pC.CreateDate || '' });
			});
			tmpItems.sort((pA, pB) => String(pB.at).localeCompare(String(pA.at)));
			await this._resolveAgentNames(tmpItems.map((pI) => pI.by), this._loadGen);
			if (!this.state.Active || Number(this.state.Active.IDDocument) !== Number(pIDDocument)) { return; }
			this._activity = { loading: false, items: tmpItems };
		}
		catch (pError) { this._activity = { loading: false, items: [], error: (pError && pError.message) || String(pError) }; }
		this._paintActivity();
	}

	setComposerKind(pKind) { this._captureComposerEditor(); this._composer.kind = ACTIVITY_KIND[pKind] ? pKind : 'Note'; this._paintComposer(); }
	setComposerText(pValue) { this._composer.text = pValue || ''; }
	setComposerField(pField, pValue) { this._composer[pField] = pValue || ''; }
	_paintComposer() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#Support-Composer').length) { tmpCA.assignContent('#Support-Composer', this._composerHTML()); this._mountComposerEditor(); } }
	_paintActivity() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#Support-Activity').length) { tmpCA.assignContent('#Support-Activity', this._activityHTML()); this._mountComposerEditor(); } }

	/** Mount (or re-render) the shared markdown editor into the composer, seeded with the in-progress message text. The
	 *  composer re-paints on kind switch / busy, so the editor is (re)mounted after every composer + activity paint and
	 *  its text is captured back into this._composer.text before any repaint (see _captureComposerEditor). */
	_mountComposerEditor()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !tmpCA.getElement('#Support-Composer-Editor-Mount').length) { return; }
		if (!this.pict.AppData.SupportComposerEditor) { this.pict.AppData.SupportComposerEditor = {}; }
		this.pict.AppData.SupportComposerEditor.Segments = [ { Content: this._composer.text || '' } ];
		if (!this.pict.views['Support-Composer-Editor'])
		{
			this.pict.addView('Support-Composer-Editor',
				{
					ViewIdentifier: 'Support-Composer-Editor',
					TargetElementAddress: '#Support-Composer-Editor-Mount',
					ContentDataAddress: 'AppData.SupportComposerEditor.Segments',
					AutoRender: false,
					EnableRichPreview: false,
					ReadOnly: false,
				}, libMarkdownEditor);
		}
		try { this.pict.views['Support-Composer-Editor'].render(); }
		catch (pError) { /* the composer just falls back to an empty mount if the markdown bundle hiccups */ }
	}

	/** Pull the markdown out of the composer editor into this._composer.text (before a repaint, a kind switch, or Add). */
	_captureComposerEditor()
	{
		// No live composer in the DOM (e.g. create mode, or not on the activity view) → nothing to marshal. Without this
		// guard the persistent CodeMirror view would surface a PRIOR call's text as a false "unsaved note" on the next call.
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !tmpCA.getElement('#Support-Composer-Editor-Mount').length) { return; }
		const tmpView = this.pict.views['Support-Composer-Editor'];
		if (!tmpView) { return; }
		if (typeof tmpView.marshalFromView === 'function') { try { tmpView.marshalFromView(); } catch (pError) { /* editor already gone */ } }
		const tmpSegs = (this.pict.AppData.SupportComposerEditor && this.pict.AppData.SupportComposerEditor.Segments) || [];
		this._composer.text = tmpSegs.map((pSeg) => (pSeg && pSeg.Content) || '').join('\n\n');
	}

	/** Mirror an open follow-up onto the document's FormData so the LIST follow-up flag reflects it cheaply. */
	async _mirrorFollowUpAsync(pNeeded)
	{
		const tmpRD = this.pict.AppData.DocumentData && this.pict.AppData.DocumentData.ReportData;
		if (!tmpRD || !this.state.Active) { return; }
		const tmpFD = tmpRD.FormData || {};
		tmpFD.Resolution = tmpFD.Resolution || {};
		tmpFD.Resolution.FollowUpNeeded = !!pNeeded;
		try { await this._putJSON('Document', { IDDocument: this.state.Active.IDDocument, IDProject: this.state.Active.IDProject, DocumentType: RNI_HASH, Form: 1, FormData: tmpFD }); }
		catch (pError) { /* best-effort mirror */ }
	}

	/**
	 * Add a call-activity comment: an internal note, a caller acknowledgement/resolution (RECORDED now; NotifyCaller flag
	 * written so email can be enabled later), or a follow-up (need-by + waiting-on). Records via the customer-stamped
	 * Comment entity + a DocumentCommentJoin (no email side-effect yet).
	 */
	async addComment()
	{
		const tmpState = this.state;
		if (!tmpState.Active || tmpState.Busy) { return; }
		this._captureComposerEditor();   // pull the latest markdown out of the editor before we read/submit it
		const tmpKind = this._composer.kind;
		const tmpText = (this._composer.text || '').trim();
		const tmpIsFollowUp = tmpKind === 'FollowUp';
		if (!tmpText && !tmpIsFollowUp) { this._toast('Add a message first.', 'info'); return; }
		if (tmpIsFollowUp && !tmpText && !this._composer.needBy && !this._composer.waitingOn) { this._toast('Add a need-by date, what it’s waiting on, or a note.', 'info'); return; }
		const tmpMeta = { Kind: tmpKind };
		if (ACTIVITY_KIND[tmpKind] && ACTIVITY_KIND[tmpKind].toCaller) { tmpMeta.NotifyCaller = false; }   // record now, email later
		if (tmpIsFollowUp) { tmpMeta.NeedByDate = this._composer.needBy || ''; tmpMeta.WaitingOn = this._composer.waitingOn || ''; tmpMeta.Cleared = false; }
		tmpState.Busy = true; this._paintComposer();
		try
		{
			const tmpComment = await this._postJSON('Comment', { Comment: tmpText, CommentHtml: this._esc(tmpText), Metadata: JSON.stringify(tmpMeta) });
			const tmpIDComment = tmpComment && tmpComment.IDComment;
			if (!tmpIDComment) { throw new Error((tmpComment && (tmpComment.Error || tmpComment.ErrorCode)) || 'the comment was not saved.'); }
			await this._postJSON('DocumentCommentJoins', [ { IDDocument: tmpState.Active.IDDocument, IDComment: tmpIDComment } ]);
			if (tmpIsFollowUp) { await this._mirrorFollowUpAsync(true); }
			// "Send resolution" also CLOSES the case — the message and the status move are one action (what agents expect).
			// It advances the approval workflow to Resolved (intermediate Start/Resume handled). A status hiccup doesn't
			// lose the message: the comment is already saved.
			let tmpResolved = false;
			if (tmpKind === 'Resolution')
			{
				try { tmpResolved = await this._resolveViaWorkflowAsync(tmpState.Active.IDDocument); if (tmpResolved) { await this._refreshApprovalAsync(tmpState.Active.IDDocument); } }
				catch (pResolveError) { /* the message is saved; the status simply didn't move */ }
			}
			tmpState.Busy = false;
			this._composer = { kind: 'Note', text: '', needBy: '', waitingOn: '' };
			this._toast(tmpIsFollowUp ? 'Follow-up set.' : (tmpKind === 'Resolution' ? (tmpResolved ? 'Resolution sent — case marked Resolved.' : 'Resolution recorded.') : 'Added to the call.'), 'success');
			this._loadActivityAsync(tmpState.Active.IDDocument);
		}
		catch (pError)
		{
			tmpState.Busy = false;
			this._toast(`Could not add: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			this._paintComposer();
		}
	}

	/** Close an open follow-up: record a cleared marker and drop the list follow-up flag. */
	async clearFollowUp()
	{
		const tmpState = this.state;
		if (!tmpState.Active || tmpState.Busy) { return; }
		tmpState.Busy = true; this._paintActivity();
		try
		{
			const tmpComment = await this._postJSON('Comment', { Comment: 'Follow-up cleared.', CommentHtml: 'Follow-up cleared.', Metadata: JSON.stringify({ Kind: 'FollowUp', Cleared: true }) });
			const tmpIDComment = tmpComment && tmpComment.IDComment;
			if (tmpIDComment) { await this._postJSON('DocumentCommentJoins', [ { IDDocument: tmpState.Active.IDDocument, IDComment: tmpIDComment } ]); }
			await this._mirrorFollowUpAsync(false);
			tmpState.Busy = false;
			this._toast('Follow-up cleared.', 'success');
			this._loadActivityAsync(tmpState.Active.IDDocument);
		}
		catch (pError)
		{
			tmpState.Busy = false;
			this._toast(`Could not clear the follow-up: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			this._paintActivity();
		}
	}


	// ── Delivery stage: an optional second lifecycle for calls that become development work ──────────────────────

	_paintImpl() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#Support-Impl').length) { tmpCA.assignContent('#Support-Impl', this._implBarHTML()); } }

	async setImplStage(pStage)
	{
		const tmpState = this.state;
		if (!tmpState.Active || tmpState.Busy) { return; }
		const tmpRD = this.pict.AppData.DocumentData && this.pict.AppData.DocumentData.ReportData;
		if (!tmpRD) { return; }
		const tmpFD = tmpRD.FormData || (tmpRD.FormData = {});
		const tmpPrev = (tmpFD.Implementation && tmpFD.Implementation.Stage) || '';
		if (tmpPrev === pStage) { return; }
		tmpFD.Implementation = tmpFD.Implementation || {};
		tmpFD.Implementation.Stage = pStage;
		tmpState.Busy = true; this._paintImpl();
		try
		{
			await this._putJSON('Document', { IDDocument: tmpState.Active.IDDocument, IDProject: tmpState.Active.IDProject, DocumentType: RNI_HASH, Form: 1, FormData: tmpFD });
			const tmpLabel = pStage ? (IMPL_STAGE_MAP[pStage] ? IMPL_STAGE_MAP[pStage].label : pStage) : 'not tracked';
			const tmpComment = await this._postJSON('Comment', { Comment: `Delivery stage → ${tmpLabel}`, CommentHtml: this._esc(`Delivery stage → ${tmpLabel}`), Metadata: JSON.stringify({ Kind: 'Stage', Stage: pStage }) });
			if (tmpComment && tmpComment.IDComment) { await this._postJSON('DocumentCommentJoins', [ { IDDocument: tmpState.Active.IDDocument, IDComment: tmpComment.IDComment } ]); }
			tmpState.Busy = false;
			const tmpRow = (tmpState.Calls || []).find((pC) => pC.IDDocument === tmpState.Active.IDDocument);
			if (tmpRow) { tmpRow.Stage = pStage; }
			this._toast('Delivery stage updated.', 'success');
			this._paintImpl();
			this._loadActivityAsync(tmpState.Active.IDDocument);
		}
		catch (pError)
		{
			tmpState.Busy = false;
			tmpFD.Implementation.Stage = tmpPrev;   // revert the optimistic write on failure
			this._toast(`Could not update the stage: ${(pError && pError.message) ? pError.message : pError}`, 'error');
			this._paintImpl();
		}
	}

	_implBarHTML()
	{
		const tmpRD = this.pict.AppData.DocumentData && this.pict.AppData.DocumentData.ReportData;
		const tmpStage = (tmpRD && tmpRD.FormData && tmpRD.FormData.Implementation && tmpRD.FormData.Implementation.Stage) || '';
		const tmpBusy = this.state.Busy;
		const tmpCollapsed = this.state.DeliveryCollapsed === true;
		const tmpSteps = IMPL_STAGES.map((pS) =>
			`<button class="sup-stg ${pS.cls} ${tmpStage === pS.id ? 'sup-stg-on' : ''}" ${tmpBusy ? 'disabled' : ''} onclick="_Pict.providers.Support.setImplStage('${pS.id}'); return false;">${this._esc(pS.label)}</button>`
		).join('<span class="sup-stg-sep">›</span>');
		// Collapsible "Delivery" section: thin header (title + current stage chip + chevron) over the stage track.
		const tmpChip = (tmpStage && IMPL_STAGE_MAP[tmpStage])
			? `<span class="sup-stg-chip ${IMPL_STAGE_MAP[tmpStage].cls}">${this._esc(IMPL_STAGE_MAP[tmpStage].label)}</span>`
			: `<span class="sup-sec-hint">optional</span>`;
		return `<div class="sup-sec${tmpCollapsed ? ' sup-sec-collapsed' : ''}">`
			+ `<button type="button" class="sup-sec-head" onclick="_Pict.providers.Support.toggleDeliveryCollapsed(); return false;">`
			+   `<span class="sup-sec-title">Delivery</span>${tmpChip}<i class="fas fa-chevron-down sup-sec-chev"></i></button>`
			+ `<div class="sup-sec-body"><div class="sup-stg-track">${tmpSteps}</div>`
			+   (tmpStage
				? `<button class="sup-stg-clear" ${tmpBusy ? 'disabled' : ''} onclick="_Pict.providers.Support.setImplStage(''); return false;" title="Clear delivery stage"><i class="fas fa-xmark"></i> Clear</button>`
				: `<span class="sup-stg-hint">optional — for calls that become work</span>`)
			+ `</div></div>`;
	}

	toggleApprovalCollapsed() { this.state.ApprovalCollapsed = !(this.state.ApprovalCollapsed === true); this._persistFilters(); this._paintApproval(); }
	toggleDeliveryCollapsed() { this.state.DeliveryCollapsed = !(this.state.DeliveryCollapsed === true); this._persistFilters(); this._paintImpl(); }

	_activityHTML()
	{
		let tmpBanner = '';
		// Follow-up is event-sourced: each FollowUp comment is a state change, the latest one (items are newest-first) wins.
		const tmpFU = this._activity.items.filter((pI) => pI.type === 'comment' && pI.kind === 'FollowUp' && pI.meta);
		if (tmpFU.length && !tmpFU[0].meta.Cleared)
		{
			const tmpF = tmpFU[0].meta;
			tmpBanner = `<div class="sup-fu-banner"><i class="fas fa-flag"></i> <b>Follow-up needed</b>`
				+ (tmpF.NeedByDate ? ` · due ${this._date(tmpF.NeedByDate)}` : '')
				+ (tmpF.WaitingOn ? ` · waiting on ${this._esc(tmpF.WaitingOn)}` : '')
				+ (tmpFU[0].text ? ` — ${this._esc(tmpFU[0].text)}` : '')
				+ ` <button class="sup-fu-done" ${this.state.Busy ? 'disabled' : ''} onclick="_Pict.providers.Support.clearFollowUp(); return false;"><i class="fas fa-check"></i> Mark done</button></div>`;
		}
		return `<div class="sup-act-h"><i class="fas fa-clock-rotate-left"></i> Call activity</div>`
			+ tmpBanner
			+ `<div id="Support-Composer" class="sup-composer">${this._composerHTML()}</div>`
			+ `<div id="Support-Timeline">${this._timelineHTML()}</div>`;
	}

	_composerHTML()
	{
		const tmpC = this._composer;
		const tmpBusy = this.state.Busy;
		const tmpKinds = COMPOSER_KINDS.map((pK) =>
		{
			const tmpM = ACTIVITY_KIND[pK];
			return `<button class="sup-ck ${tmpC.kind === pK ? 'sup-ck-on' : ''}" onclick="_Pict.providers.Support.setComposerKind('${pK}'); return false;"><i class="fas ${tmpM.icon}"></i> ${this._esc(tmpM.comLabel)}</button>`;
		}).join('');
		const tmpFollow = (tmpC.kind === 'FollowUp')
			? `<div class="sup-ck-fu"><label>Need by <input type="date" value="${this._esc(tmpC.needBy)}" onchange="_Pict.providers.Support.setComposerField('needBy', this.value)"></label>`
				+ `<label>Waiting on <input type="text" placeholder="e.g. customer reply, deploy" value="${this._esc(tmpC.waitingOn)}" oninput="_Pict.providers.Support.setComposerField('waitingOn', this.value)"></label></div>` : '';
		const tmpToCaller = ACTIVITY_KIND[tmpC.kind] && ACTIVITY_KIND[tmpC.kind].toCaller;
		const tmpHint = tmpToCaller ? `<span class="sup-ck-hint"><i class="fas fa-circle-info"></i> Recorded now; caller email is a later step.</span>` : '';
		const tmpPlaceholder = (tmpC.kind === 'FollowUp') ? 'What is the next follow-up? (optional)' : (tmpToCaller ? 'Message to the caller…' : 'Add a note…');
		// The message body is the app's first-class markdown editor (mounted after paint by _mountComposerEditor), not a
		// plain textarea — so notes, acknowledgements, and resolutions can be written in markdown like the runbooks.
		return `<div class="sup-ck-kinds">${tmpKinds}</div>${tmpFollow}`
			+ `<div class="sup-ck-ph">${this._esc(tmpPlaceholder)}</div>`
			+ `<div id="Support-Composer-Editor-Mount" class="sup-ck-editor"></div>`
			+ `<div class="sup-ck-actions">${tmpHint}<button class="sup-btn sup-btn-primary" ${tmpBusy ? 'disabled' : ''} onclick="_Pict.providers.Support.addComment(); return false;">${tmpBusy ? '<i class="fas fa-spinner fa-spin"></i> Adding…' : '<i class="fas fa-plus"></i> Add'}</button></div>`;
	}

	_timelineHTML()
	{
		const tmpA = this._activity;
		if (tmpA.loading) { return `<div class="sup-note"><i class="fas fa-spinner fa-spin"></i> Loading activity…</div>`; }
		if (!tmpA.items.length) { return `<div class="sup-tl-empty">No activity yet — add a note or acknowledge the caller.</div>`; }
		return `<div class="sup-tl">` + tmpA.items.map((pIt) =>
		{
			const tmpWho = this._agentNames[pIt.by] || (pIt.by ? `User ${pIt.by}` : 'System');
			const tmpWhen = this._dateTime(pIt.at);
			if (pIt.type === 'transition')
			{
				const tmpAct = ACTION_META[pIt.action] || { icon: 'fa-arrow-right' };
				const tmpSt = this._statusMeta(pIt.status);
				const tmpVerb = pIt.isApproval ? 'resolved' : (pIt.action ? this._esc(String(pIt.action).replace(/([A-Z])/g, ' $1').trim().toLowerCase()) : 'updated');
				return `<div class="sup-tl-item"><span class="sup-tl-icon sup-ak-tr"><i class="fas ${tmpAct.icon}"></i></span>`
					+ `<div class="sup-tl-body"><div class="sup-tl-head"><b>${this._esc(tmpWho)}</b> ${tmpVerb} → <span class="sup-badge ${tmpSt.cls}">${tmpSt.label}</span> <span class="sup-tl-when">${tmpWhen}</span></div></div></div>`;
			}
			const tmpKind = ACTIVITY_KIND[pIt.kind] || ACTIVITY_KIND.Note;
			const tmpExtra = (pIt.kind === 'FollowUp' && pIt.meta)
				? `<div class="sup-tl-fu">${pIt.meta.NeedByDate ? `Due ${this._date(pIt.meta.NeedByDate)}` : ''}${pIt.meta.WaitingOn ? `${pIt.meta.NeedByDate ? ' · ' : ''}waiting on ${this._esc(pIt.meta.WaitingOn)}` : ''}${pIt.meta.Cleared ? ' · <em>cleared</em>' : ''}</div>` : '';
			return `<div class="sup-tl-item"><span class="sup-tl-icon ${tmpKind.cls}"><i class="fas ${tmpKind.icon}"></i></span>`
				+ `<div class="sup-tl-body"><div class="sup-tl-head"><b>${this._esc(tmpWho)}</b> <span class="sup-tl-kind ${tmpKind.cls}">${tmpKind.label}</span> <span class="sup-tl-when">${tmpWhen}</span></div>`
				+ (pIt.text ? `<div class="sup-tl-text sup-md">${this._renderMd(pIt.text)}</div>` : '') + tmpExtra + `</div></div>`;
		}).join('') + `</div>`;
	}

	// ── List filters + sort + export (all client-side over the loaded call set) ──────────────────────────────────

	onSearch(pValue) { this.state.Filter = pValue || ''; this.state.ListPage = 0; this._persistFilters(); this._repaintList(); }

	/** Toggle a value in a multi-select facet (Status/Priority/Category/App); empty facet = all. */
	toggleFilter(pFacet, pValue)
	{
		const tmpSel = this.state.Filters[pFacet] || (this.state.Filters[pFacet] = []);
		const tmpIdx = tmpSel.indexOf(pValue);
		if (tmpIdx >= 0) { tmpSel.splice(tmpIdx, 1); } else { tmpSel.push(pValue); }
		this.state.ListPage = 0;
		this._persistFilters();
		this._paint();
	}

	setDateFilter(pWhich, pValue)
	{
		if (!this.state.DateFilter) { this.state.DateFilter = { from: '', to: '' }; }
		this.state.DateFilter[pWhich] = pValue || '';
		this.state.ListPage = 0;
		this._persistFilters();
		this._paint();
	}

	clearFilters()
	{
		this.state.Filters = { Status: [], Priority: [], Category: [], App: [], Stage: [], Customer: [] };
		this.state.DateFilter = { from: '', to: '' };
		this.state.Filter = '';
		this.state.OpenDropdown = '';
		this.state.ListPage = 0;
		this._persistFilters();
		this._paint();
	}

	/** Persist the triage filters (facets / date range / search / sort) so they survive navigation AND a reload. */
	_persistFilters()
	{
		try
		{
			if (typeof localStorage === 'undefined') { return; }
			const tmpState = this.state;
			localStorage.setItem('Support.TriageFilters', JSON.stringify({ Filters: tmpState.Filters, DateFilter: tmpState.DateFilter, Filter: tmpState.Filter, Sort: tmpState.Sort, PanelWidth: tmpState.PanelWidth, RunbookOpen: tmpState.RunbookOpen, ApprovalCollapsed: tmpState.ApprovalCollapsed, DeliveryCollapsed: tmpState.DeliveryCollapsed }));
		}
		catch (pError) { /* best-effort — a blocked/full localStorage must never break filtering */ }
	}

	/** Restore persisted triage filters (shape-guarded) to overlay onto the state defaults. {} if none/unavailable. */
	_loadPersistedFilters()
	{
		try
		{
			const tmpRaw = (typeof localStorage !== 'undefined') ? localStorage.getItem('Support.TriageFilters') : null;
			if (!tmpRaw) { return {}; }
			const tmpSaved = JSON.parse(tmpRaw) || {};
			const tmpOut = {};
			if (tmpSaved.Filters && typeof tmpSaved.Filters === 'object') { tmpOut.Filters = tmpSaved.Filters; }
			if (tmpSaved.DateFilter && typeof tmpSaved.DateFilter === 'object') { tmpOut.DateFilter = tmpSaved.DateFilter; }
			if (typeof tmpSaved.Filter === 'string') { tmpOut.Filter = tmpSaved.Filter; }
			if (Array.isArray(tmpSaved.Sort) && tmpSaved.Sort.length) { tmpOut.Sort = tmpSaved.Sort; }
			if (typeof tmpSaved.PanelWidth === 'number') { tmpOut.PanelWidth = tmpSaved.PanelWidth; }
			if (typeof tmpSaved.RunbookOpen === 'boolean') { tmpOut.RunbookOpen = tmpSaved.RunbookOpen; }
			if (typeof tmpSaved.ApprovalCollapsed === 'boolean') { tmpOut.ApprovalCollapsed = tmpSaved.ApprovalCollapsed; }
			if (typeof tmpSaved.DeliveryCollapsed === 'boolean') { tmpOut.DeliveryCollapsed = tmpSaved.DeliveryCollapsed; }
			return tmpOut;
		}
		catch (pError) { return {}; }
	}

	toggleDropdown(pFacet) { this.state.OpenDropdown = (this.state.OpenDropdown === pFacet) ? '' : pFacet; this._paint(); }

	/**
	 * Sort by a column. Plain click = make it the sole (primary) sort, toggling asc/desc if already primary; Shift-click
	 * = add/adjust it as a secondary key, for layered sort (status → priority → category …).
	 */
	setSort(pKey, pAppend)
	{
		const tmpSort = this.state.Sort;
		const tmpIdx = tmpSort.findIndex((pS) => pS.key === pKey);
		if (pAppend)
		{
			if (tmpIdx >= 0) { tmpSort[tmpIdx].dir = tmpSort[tmpIdx].dir === 'asc' ? 'desc' : 'asc'; }
			else { tmpSort.push({ key: pKey, dir: 'asc' }); }
		}
		else if (tmpIdx === 0 && tmpSort.length === 1) { tmpSort[0].dir = tmpSort[0].dir === 'asc' ? 'desc' : 'asc'; }
		else { this.state.Sort = [ { key: pKey, dir: (tmpIdx === 0 && tmpSort[0]) ? (tmpSort[0].dir === 'asc' ? 'desc' : 'asc') : 'asc' } ]; }
		this.state.ListPage = 0;
		this._persistFilters();
		this._paint();
	}

	/** Jump to a page of the triage list (client-side pagination of the filtered set); repaints only the list body. */
	setListPage(pPage)
	{
		this.state.ListPage = Math.max(0, Number(pPage) || 0);
		this._repaintList();
		const tmpEl = this.pict.ContentAssignment.getElement(LIST_MOUNT);
		if (tmpEl && tmpEl.length && tmpEl[0].scrollIntoView) { try { tmpEl[0].scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (pError) { /* */ } }
	}

	/** The comparable value for a call on a sort key — rank maps for Status/Priority (lexical order would be wrong). */
	_sortValue(pCall, pKey)
	{
		switch (pKey)
		{
			case 'Status': return (STATUS_ORDER[pCall.Status] != null) ? STATUS_ORDER[pCall.Status] : 9;
			case 'Priority': return this._prioMeta(pCall.Priority).rank;
			case 'Category': return this._catLabelList(pCall.Category).toLowerCase();
			case 'App': return this._appLabel(pCall.App).toLowerCase();
			case 'Customer': return this._customerName(pCall.CallerCustomer).toLowerCase();
			case 'Summary': return String(pCall.Summary || '').toLowerCase();
			case 'Caller': return String(pCall.Caller || '').toLowerCase();
			case 'Created': return String(pCall.Created || '');
			case 'Updated': return String(pCall.Updated || '');
			case 'Designated': return String(pCall.Designated || '');
			case 'UpdatedBy': return String(this._agentNames[pCall.UpdatedBy] || '').toLowerCase();
			default: return '';
		}
	}

	_compareCalls(pA, pB)
	{
		for (const tmpKey of this.state.Sort)
		{
			const tmpVA = this._sortValue(pA, tmpKey.key);
			const tmpVB = this._sortValue(pB, tmpKey.key);
			if (tmpVA < tmpVB) { return tmpKey.dir === 'asc' ? -1 : 1; }
			if (tmpVA > tmpVB) { return tmpKey.dir === 'asc' ? 1 : -1; }
		}
		return pB.IDDocument - pA.IDDocument;   // stable tiebreak
	}

	_visibleCalls()
	{
		const tmpState = this.state;
		const tmpNeedle = String(tmpState.Filter || '').trim().toLowerCase();
		const tmpFacets = this._facets();
		const tmpRows = tmpState.Calls.filter((pCall) =>
		{
			for (const tmpFacet of tmpFacets)
			{
				const tmpSel = tmpState.Filters[tmpFacet.key];
				if (tmpSel && tmpSel.length && tmpSel.indexOf(String(tmpFacet.valueFor(pCall))) < 0) { return false; }
			}
			const tmpDF = tmpState.DateFilter || {};
			if (tmpDF.from || tmpDF.to)
			{
				// Compare on the ISO calendar-day prefix (YYYY-MM-DD) — a pure string compare, matching how the sort
				// treats these fields and sidestepping the _date() local/UTC gotcha; string <= gives an inclusive end.
				const tmpDay = String(pCall.Designated || '').slice(0, 10);
				if (!tmpDay) { return false; }
				if (tmpDF.from && tmpDay < tmpDF.from) { return false; }
				if (tmpDF.to && tmpDay > tmpDF.to) { return false; }
			}
			if (!tmpNeedle) { return true; }
			return [ pCall.Summary, pCall.Caller, this._catLabelList(pCall.Category), this._appLabel(pCall.App), pCall.Priority,
				(this._isSuperUser() ? this._customerName(pCall.CallerCustomer) : ''), (this._agentNames[pCall.UpdatedBy] || ''), `#${pCall.IDDocument}` ]
				.some((pField) => String(pField || '').toLowerCase().indexOf(tmpNeedle) >= 0);
		});
		return tmpRows.sort((pA, pB) => this._compareCalls(pA, pB));
	}

	// ── CSV export (the current filtered view; manager-gated) ────────────────────────────────────────────────────

	_triggerBrowserDownload(pBlob, pFileName)
	{
		try
		{
			const tmpURL = URL.createObjectURL(pBlob);
			const tmpAnchor = document.createElement('a');
			tmpAnchor.href = tmpURL; tmpAnchor.download = pFileName;
			document.body.appendChild(tmpAnchor); tmpAnchor.click(); document.body.removeChild(tmpAnchor);
			setTimeout(() => { try { URL.revokeObjectURL(tmpURL); } catch (pError) { /* */ } }, 10000);
		}
		catch (pError) { this._toast('Could not start the download.', 'error'); }
	}

	/** One CSV cell — quote-escape commas/quotes/newlines + neutralize an Excel formula-injection lead char. */
	_csvCell(pValue)
	{
		let tmpString = String(pValue == null ? '' : pValue);
		if (/^[=+\-@]/.test(tmpString)) { tmpString = '\'' + tmpString; }
		if (/[",\r\n]/.test(tmpString)) { tmpString = '"' + tmpString.replace(/"/g, '""') + '"'; }
		return tmpString;
	}

	exportCallsCSV()
	{
		if (!this.canManage()) { return; }
		const tmpRows = this._visibleCalls();
		if (!tmpRows.length) { this._toast('No calls to export in the current view.', 'info'); return; }
		const tmpSuper = this._isSuperUser();
		const tmpHeader = [ '#', 'Status' ].concat(tmpSuper ? [ 'Customer' ] : []).concat([ 'Priority', 'Category', 'App', 'Summary', 'Caller', 'Follow-up', 'Opened', 'Updated', 'Designated', 'Updated By' ]);
		const tmpLines = [ tmpHeader.map((pH) => this._csvCell(pH)).join(',') ];
		tmpRows.forEach((pCall) =>
		{
			const tmpCells = [ pCall.IDDocument, this._statusMeta(pCall.Status).label ]
				.concat(tmpSuper ? [ this._customerName(pCall.CallerCustomer) ] : [])
				.concat([ this._prioMeta(pCall.Priority).label, this._catLabelList(pCall.Category), this._appLabel(pCall.App),
					pCall.Summary, pCall.Caller, pCall.FollowUp ? 'Yes' : '', this._date(pCall.Created), this._date(pCall.Updated), this._date(pCall.Designated),
					pCall.UpdatedBy ? (this._agentNames[pCall.UpdatedBy] || ('User ' + pCall.UpdatedBy)) : '' ]);
			tmpLines.push(tmpCells.map((pV) => this._csvCell(pV)).join(','));
		});
		const tmpCSV = '\uFEFF' + tmpLines.join('\r\n');   // BOM → Excel reads UTF-8 cleanly
		const tmpStamp = (new Date()).toISOString().slice(0, 10);
		// Filtered to exactly one customer → name the file after them (the HDOT bi-weekly download).
		const tmpCustFilter = this.state.Filters.Customer || [];
		const tmpWho = (tmpSuper && tmpCustFilter.length === 1) ? ('-' + String(this._customerName(Number(tmpCustFilter[0]))).replace(/[^A-Za-z0-9]+/g, '')) : '';
		this._triggerBrowserDownload(new Blob([ tmpCSV ], { type: 'text/csv;charset=utf-8' }), `Support-Calls${tmpWho}-${tmpStamp}.csv`);
		this._toast(`Exported ${tmpRows.length} call${tmpRows.length === 1 ? '' : 's'}.`, 'success');
	}

	// ── Metrics ────────────────────────────────────────────────────────────────────────────────────────────────

	setView(pView)
	{
		if (this._isDocsOnly()) { pView = 'docs'; }   // runbook-only user is pinned to the Docs surface
		const tmpView = ([ 'metrics', 'settings', 'docs' ].indexOf(pView) >= 0) ? pView : 'calls';
		// Metrics + settings stay manager-only; the docs/runbook view also opens to runbook editors (Document-edit holders).
		if (tmpView !== 'calls' && !this.canManage() && !(tmpView === 'docs' && this._canAccessDocs())) { return; }
		if (this.state.View === tmpView && this.state.Mode !== 'form') { return; }
		this.state.View = tmpView;
		if (tmpView === 'metrics' && !this._metrics) { this._loadMetricsAsync(); }
		else if (tmpView === 'settings') { this._loadSettingsDraftAsync(); }
		else if (tmpView === 'docs') { this._loadDocsAsync(); }
		else { this._paint(); }
	}

	// ── Stable routes ─────────────────────────────────────────────────────────────────────────────────────────────
	// The tab buttons / gears / call rows write the URL hash; the router (below) applies it. So every surface is
	// deep-linkable + bookmarkable + back/forward-able — no view is reached by a bare JS state mutation. See [[config-app-tab-stable-routes]].

	/** Write the hash for a top-level view; a preset click closes any open call. Falls back to setView with no window. */
	navigateView(pView)
	{
		const tmpView = ([ 'metrics', 'settings', 'docs' ].indexOf(pView) >= 0) ? pView : 'calls';
		if ((typeof window === 'undefined') || !window.location) { this.routeApply(tmpView); return; }
		window.location.hash = '#/Support' + (tmpView === 'calls' ? '' : '/' + tmpView);
	}

	/** Write the hash to open a specific call. */
	navigateCall(pIDDocument)
	{
		if (this._isDocsOnly()) { return; }   // runbook-only user has no call access
		if ((typeof window === 'undefined') || !window.location) { this.openCall(pIDDocument); return; }
		window.location.hash = `#/Support/Call/${Number(pIDDocument)}`;
	}

	/** Router entry: land on a top-level view. Guarded so a warm tab click is an in-place switch (no reload); returning
	 *  from an open call reloads the list once so it reflects any edits. */
	routeApply(pView)
	{
		const tmpView = ([ 'metrics', 'settings', 'docs' ].indexOf(pView) >= 0) ? pView : 'calls';
		if (this.state.Mode === 'form' || this.state.Mode === 'merge')
		{
			if (this.state.Mode === 'form' && this.formHost && (typeof this.formHost.destroy === 'function')) { try { this.formHost.destroy(); } catch (pErr) { /* best-effort */ } }
			if (this.state.Mode === 'merge') { const tmpMerge = this.pict.providers.SupportMerge; if (tmpMerge && (typeof tmpMerge.teardown === 'function')) { tmpMerge.teardown(); } }
			this.state.View = tmpView;
			this._pendingApply = (pProvider) => pProvider.setView(tmpView);   // applied after loadAsync resolves (list mode)
			this.loadAsync();
			return;
		}
		this.setView(tmpView);
	}

	/** Router entry: open a specific call (no-op if it is already the open one). */
	routeApplyCall(pIDDocument)
	{
		if (this._isDocsOnly()) { this.navigateView('docs'); return; }   // deep-link to a call → bounce a runbook-only user to Docs
		const tmpID = Number(pIDDocument);
		if (this.state.Mode === 'form' && this.state.Active && Number(this.state.Active.IDDocument) === tmpID) { return; }
		this.openCall(tmpID);
	}

	/** The dashboard's active date-range scope → { from, to (YYYY-MM-DD, half-open), label, startY, startM, months }.
	 *  Drives BOTH the scoped document load and the trend-chart month buckets, so the two always agree. */
	_metricsRangeBounds()
	{
		const tmpNow = new Date();
		const tmpPreset = (this.state.MetricsRange && this.state.MetricsRange.preset) || 'last12';
		const tmpYMD = (pD) => `${pD.getFullYear()}-${String(pD.getMonth() + 1).padStart(2, '0')}-01`;
		if (/^\d{4}$/.test(tmpPreset))
		{
			const tmpY = Number(tmpPreset);
			return { from: `${tmpY}-01-01`, to: `${tmpY + 1}-01-01`, label: tmpPreset, startY: tmpY, startM: 0, months: 12 };
		}
		if (tmpPreset === 'all')
		{
			const tmpEnd = new Date(tmpNow.getFullYear(), tmpNow.getMonth() + 1, 1);
			return { from: '2020-01-01', to: tmpYMD(tmpEnd), label: 'All', startY: 2020, startM: 0, months: (tmpNow.getFullYear() - 2020) * 12 + tmpNow.getMonth() + 1 };
		}
		if (tmpPreset === 'custom')
		{
			const tmpR = this.state.MetricsRange || {};
			const tmpFrom = tmpR.from || `${tmpNow.getFullYear()}-01-01`;
			const tmpToPicked = tmpR.to || tmpYMD(tmpNow);
			const tmpFromD = new Date(tmpFrom + 'T00:00:00');
			const tmpToPickedD = new Date(tmpToPicked + 'T00:00:00');
			const tmpToD = new Date(tmpToPicked + 'T00:00:00'); tmpToD.setDate(tmpToD.getDate() + 1);   // make the picked end date INCLUSIVE (LT is exclusive)
			const tmpToExcl = `${tmpToD.getFullYear()}-${String(tmpToD.getMonth() + 1).padStart(2, '0')}-${String(tmpToD.getDate()).padStart(2, '0')}`;
			// Trend months span the PICKED range (from-month … to-month), not the +1-day exclusive bound.
			const tmpMonths = Math.max(1, Math.min(96, (tmpToPickedD.getFullYear() - tmpFromD.getFullYear()) * 12 + (tmpToPickedD.getMonth() - tmpFromD.getMonth()) + 1));
			return { from: tmpFrom, to: tmpToExcl, label: `${tmpFrom} → ${tmpToPicked}`, startY: tmpFromD.getFullYear(), startM: tmpFromD.getMonth(), months: tmpMonths };
		}
		const tmpStart = new Date(tmpNow.getFullYear(), tmpNow.getMonth() - 11, 1);
		const tmpEnd = new Date(tmpNow.getFullYear(), tmpNow.getMonth() + 1, 1);
		return { from: tmpYMD(tmpStart), to: tmpYMD(tmpEnd), label: 'Last 12 months', startY: tmpStart.getFullYear(), startM: tmpStart.getMonth(), months: 12 };
	}

	_monthBuckets()
	{
		const tmpB = this._metricsRangeBounds();
		this._trendSpan = tmpB.months;
		const tmpOut = [];
		for (let i = 0; i < tmpB.months; i++)
		{
			const tmpM = new Date(tmpB.startY, tmpB.startM + i, 1);
			tmpOut.push({ key: `${tmpM.getFullYear()}-${String(tmpM.getMonth() + 1).padStart(2, '0')}`, y: tmpM.getFullYear(), m: tmpM.getMonth() });
		}
		return tmpOut;
	}

	/** Fetch the resolution log once (earliest IsApproval per doc = resolution + resolver), then compute the dashboard. */
	/** Load the documents in the active date-range scope (its OWN set, independent of the 400-cap call list), paged by
	 *  DocumentDate = the opened/first-email date. */
	async _loadMetricsCallsAsync(pFrom, pTo, pGen)
	{
		const tmpScope = this._isSuperUser() ? '' : `FBV~IDCustomer~EQ~${this.idCustomer}~`;
		const tmpOwn = this.isSelfService() ? `~FBV~CreatingIDUser~EQ~${this.idUser}` : '';
		const tmpBase = `${tmpScope}FBV~DocumentType~EQ~${RNI_HASH}~FBV~Deleted~EQ~0~FBV~DocumentDate~GE~${pFrom}~FBV~DocumentDate~LT~${pTo}${tmpOwn}`;
		// A Count first gives the exact page plan so the pages can be fetched CONCURRENTLY (sequential paging of the big
		// FormData docs is the slow part). Sort DESC — on this large table an ASC sort with offset paging is pathologically
		// slow; DESC is index-fast, and the metrics aggregate over the set so order doesn't matter.
		const tmpCount = await this._getJSON(`Documents/Count/FilteredTo/${tmpBase}`);
		if (pGen !== this._loadGen) { return []; }
		const tmpTotal = Math.min((tmpCount && Number(tmpCount.Count)) || 0, 30000);
		const tmpOffsets = []; for (let tmpOff = 0; tmpOff < tmpTotal; tmpOff += 500) { tmpOffsets.push(tmpOff); }
		const tmpOut = [];
		const tmpConcurrency = 8;
		for (let i = 0; i < tmpOffsets.length; i += tmpConcurrency)
		{
			const tmpBatch = tmpOffsets.slice(i, i + tmpConcurrency);
			const tmpResults = await Promise.all(tmpBatch.map((pOff) => this._getJSON(`Documents/FilteredTo/${tmpBase}~FSF~IDDocument~DESC~0/${pOff}/500`).catch(() => [])));
			if (pGen !== this._loadGen) { return tmpOut; }
			tmpResults.forEach((pRows) => { (Array.isArray(pRows) ? pRows : []).forEach((pDoc) => tmpOut.push(this._callSummary(pDoc))); });
		}
		return tmpOut;
	}

	async _loadMetricsAsync()
	{
		const tmpGen = ++this._loadGen;   // shared guard: a list reload or a view switch supersedes a stale metrics load
		this._metricsLoading = true;
		this._paint();
		try
		{
			const tmpBounds = this._metricsRangeBounds();
			const tmpCalls = await this._loadMetricsCallsAsync(tmpBounds.from, tmpBounds.to, tmpGen);
			if (tmpGen !== this._loadGen) { return; }
			this._metricsCalls = tmpCalls;
			// Resolve map: LEGACY docs carry their real close date/agent in FormData.Meta (approval-log CreateDate is
			// import-stamped) — take those with no query. Only LIVE docs need the DocumentApprovalLog lookup, so at
			// archive scale (~all legacy) we issue almost none.
			const tmpResolve = {};
			const tmpLiveIDs = tmpCalls.filter((pCall) => !pCall.Legacy).map((pCall) => pCall.IDDocument).filter(Boolean);
			for (let i = 0; i < tmpLiveIDs.length; i += 100)
			{
				const tmpChunk = tmpLiveIDs.slice(i, i + 100);
				const tmpRows = await this._getJSON(`DocumentApprovalLogs/FilteredTo/FBL~IDDocument~INN~${tmpChunk.join(',')}~FBV~Deleted~EQ~0~FSF~CreateDate~DESC~0/0/8000`);
				if (tmpGen !== this._loadGen) { return; }
				(Array.isArray(tmpRows) ? tmpRows : []).forEach((pLog) =>
				{
					if (Number(pLog.IsApproval) !== 1) { return; }
					const tmpHave = tmpResolve[pLog.IDDocument];
					if (!tmpHave || pLog.CreateDate < tmpHave.date) { tmpResolve[pLog.IDDocument] = { date: pLog.CreateDate, resolver: Number(pLog.CreatingIDUser) || 0 }; }
				});
			}
			tmpCalls.forEach((pCall) =>
			{
				if (!pCall.Legacy || pCall.Status !== 'Resolved') { return; }
				tmpResolve[pCall.IDDocument] = { date: pCall.ResolvedAt || pCall.Updated || pCall.Created || '', resolver: pCall.ResolvedBy || (tmpResolve[pCall.IDDocument] && tmpResolve[pCall.IDDocument].resolver) || 0 };
			});
			this._resolveMap = tmpResolve;
			// Resolve names for the resolver (support user) + opener (customer user) filters/breakdowns.
			const tmpNames = [];
			Object.keys(tmpResolve).forEach((pK) => { if (tmpResolve[pK].resolver) { tmpNames.push(tmpResolve[pK].resolver); } });
			tmpCalls.forEach((pCall) => { if (pCall.OpenedBy) { tmpNames.push(pCall.OpenedBy); } });
			await this._resolveAgentNames(tmpNames, tmpGen);
			if (tmpGen !== this._loadGen) { return; }
			this._recomputeMetrics();
		}
		catch (pError) { this._metrics = { error: (pError && pError.message) ? pError.message : String(pError) }; }
		// Only repaint the full shell when the list is on screen — a metrics load that resolves after the user has moved to a
		// call form or the inline merge wizard must NOT _paint() (it would tear down the mounted #Support-FormHost or the
		// #SupportMerge-Wizard accordion). The metrics dashboard is itself a list-mode sub-view, so this still repaints it.
		finally { if (tmpGen === this._loadGen) { this._metricsLoading = false; if (this.state.Mode === 'list') { this._paint(); } } }
	}

	/** Switch the dashboard date-range scope and reload its metrics. */
	setMetricsRange(pPreset)
	{
		if (!this.state.MetricsRange) { this.state.MetricsRange = {}; }
		this.state.MetricsRange.preset = pPreset || 'last12';
		this._showCustomRange = false;   // a preset click closes the custom From/To editor
		this._metrics = null;
		this._loadMetricsAsync();
		this._paintMetrics();
	}

	/** Does a call pass the current dashboard filters (customer / support-user / customer-user / type)? */
	_metricCallPasses(pCall)
	{
		const tmpF = this.state.MetricsFilters || {};
		if (tmpF.Customer && String(pCall.CallerCustomer || '') !== tmpF.Customer) { return false; }
		if (tmpF.Category && (pCall.Category || '') !== tmpF.Category) { return false; }
		if (tmpF.App && (pCall.App || '') !== tmpF.App) { return false; }
		if (tmpF.Opener && String(pCall.OpenedBy || '') !== tmpF.Opener) { return false; }
		if (tmpF.Resolver)
		{
			const tmpRes = (this._resolveMap || {})[pCall.IDDocument];
			if (!tmpRes || String(tmpRes.resolver || '') !== tmpF.Resolver) { return false; }
		}
		return true;
	}

	/** Derive every dashboard aggregate from the loaded calls ⊕ the cached resolve map, honoring the metrics filters. */
	_recomputeMetrics()
	{
		const tmpResolve = this._resolveMap || {};
		const tmpCalls = (this._metricsCalls || []).filter((pCall) => this._metricCallPasses(pCall));
		const tmpByCustomer = {};   // IDCustomer → { total, resolved, open } (dashboard-from-the-polyjoins, #3)
		const tmpByTheme = {};      // derived theme → { total, resolved }
		const tmpMonths = this._monthBuckets();
		const tmpIdx = {}; tmpMonths.forEach((pMo, i) => { tmpIdx[pMo.key] = i; });
		const tmpOpened = new Array(tmpMonths.length).fill(0);
		const tmpResolved = new Array(tmpMonths.length).fill(0);
		const tmpBugOpened = new Array(tmpMonths.length).fill(0);
		const tmpBugResolved = new Array(tmpMonths.length).fill(0);
		const tmpCycles = [];
		const tmpCounts = { New: 0, Acknowledged: 0, Tier1: 0, Tier2: 0, Tier3: 0, Resolved: 0, Void: 0 };
		const tmpByCat = {}; const tmpByPrio = {}; const tmpByApp = {}; const tmpByAgent = {}; const tmpByBugApp = {};
		let tmpBugTotal = 0; let tmpBugBacklog = 0; const tmpBugCycles = [];

		tmpCalls.forEach((pCall) =>
		{
			const tmpIsBug = pCall.Category === 'Bug';
			if (tmpCounts[pCall.Status] != null) { tmpCounts[pCall.Status]++; }
			// A voided (spam / not-a-real-call) ticket is counted in the status breakdown above, then excluded from every
			// volume, resolution, backlog and trend rollup below — it is not a real call, so it must never show as a
			// customer's open backlog, inflate the bug backlog, or pollute the opened/resolved series.
			if (pCall.Status === 'Void') { return; }
			if (pCall.Created) { const tmpMi = tmpIdx[String(pCall.Created).slice(0, 7)]; if (tmpMi != null) { tmpOpened[tmpMi]++; if (tmpIsBug) { tmpBugOpened[tmpMi]++; } } }
			const tmpCat = tmpByCat[pCall.Category || 'Uncategorized'] || (tmpByCat[pCall.Category || 'Uncategorized'] = { total: 0, resolved: 0 }); tmpCat.total++;
			const tmpPrio = tmpByPrio[pCall.Priority || 'Unset'] || (tmpByPrio[pCall.Priority || 'Unset'] = { total: 0, resolved: 0 }); tmpPrio.total++;
			const tmpApp = tmpByApp[pCall.App || 'Unspecified'] || (tmpByApp[pCall.App || 'Unspecified'] = { total: 0, resolved: 0 }); tmpApp.total++;
			if (pCall.CallerCustomer) { const tmpC = tmpByCustomer[pCall.CallerCustomer] || (tmpByCustomer[pCall.CallerCustomer] = { total: 0, resolved: 0, open: 0 }); tmpC.total++; if (pCall.Status === 'Resolved') { tmpC.resolved++; } else { tmpC.open++; } }
			{ const tmpTh = tmpByTheme[pCall.Theme || 'Other'] || (tmpByTheme[pCall.Theme || 'Other'] = { total: 0, resolved: 0 }); tmpTh.total++; if (pCall.Status === 'Resolved') { tmpTh.resolved++; } }
			if (tmpIsBug)
			{
				tmpBugTotal++;
				if (pCall.Status !== 'Resolved') { tmpBugBacklog++; }
				const tmpBA = tmpByBugApp[pCall.App || 'Unspecified'] || (tmpByBugApp[pCall.App || 'Unspecified'] = { total: 0, resolved: 0 }); tmpBA.total++;
			}
			const tmpRes = tmpResolve[pCall.IDDocument];
			if (tmpRes)
			{
				tmpCat.resolved++; tmpPrio.resolved++; tmpApp.resolved++;
				const tmpMi = tmpIdx[String(tmpRes.date).slice(0, 7)]; if (tmpMi != null) { tmpResolved[tmpMi]++; if (tmpIsBug) { tmpBugResolved[tmpMi]++; } }
				if (pCall.Created && tmpRes.date >= pCall.Created)
				{
					const tmpDelta = (new Date(tmpRes.date) - new Date(pCall.Created)) / 86400000;
					tmpCycles.push(tmpDelta); if (tmpIsBug) { tmpBugCycles.push(tmpDelta); }
				}
				if (tmpIsBug) { const tmpBA = tmpByBugApp[pCall.App || 'Unspecified']; if (tmpBA) { tmpBA.resolved++; } }
				if (tmpRes.resolver) { const tmpA = tmpByAgent[tmpRes.resolver] || (tmpByAgent[tmpRes.resolver] = { resolved: 0 }); tmpA.resolved++; }
			}
		});
		const tmpMedianOf = (pArr) => { if (!pArr.length) { return null; } const tmpS = pArr.slice().sort((pA, pB) => pA - pB); return tmpS.length % 2 ? tmpS[(tmpS.length - 1) / 2] : (tmpS[tmpS.length / 2 - 1] + tmpS[tmpS.length / 2]) / 2; };

		this._metrics =
		{
			total: tmpCalls.length,
			counts: tmpCounts,
			backlog: tmpCounts.New + tmpCounts.Acknowledged + tmpCounts.Tier1 + tmpCounts.Tier2 + tmpCounts.Tier3,
			resolved: tmpCounts.Resolved,
			medianDays: tmpMedianOf(tmpCycles),
			months: tmpMonths, opened: tmpOpened, resolvedSeries: tmpResolved,
			byCat: tmpByCat, byPrio: tmpByPrio, byApp: tmpByApp, byAgent: tmpByAgent, byCustomer: tmpByCustomer, byTheme: tmpByTheme,
			rangeLabel: this._metricsRangeBounds().label,
			bug: { total: tmpBugTotal, backlog: tmpBugBacklog, medianDays: tmpMedianOf(tmpBugCycles), byApp: tmpByBugApp, opened: tmpBugOpened, resolved: tmpBugResolved },
		};
	}

	_paintMetrics() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#Support-Metrics').length) { tmpCA.assignContent('#Support-Metrics', this._metricsHTML()); this._mountViewPickers(); } }

	setMetricFilter(pDim, pValue)
	{
		if (!this.state.MetricsFilters) { this.state.MetricsFilters = {}; }
		this.state.MetricsFilters[pDim] = pValue || '';
		this._recomputeMetrics();
		this._paintMetrics();
	}

	clearMetricFilters() { this.state.MetricsFilters = { Customer: '', App: '', Category: '', Resolver: '', Opener: '' }; this._recomputeMetrics(); this._paintMetrics(); }

	async _resolveAgentNames(pIDs, pGen)
	{
		const tmpNeed = [ ...new Set(pIDs.filter((pX) => pX && !this._agentNames[pX])) ];
		for (let i = 0; i < tmpNeed.length; i += 100)
		{
			const tmpChunk = tmpNeed.slice(i, i + 100);
			const tmpRows = await this._getJSON(`Users/FilteredTo/FBL~IDUser~INN~${tmpChunk.join(',')}/0/${tmpChunk.length}`);
			if (pGen !== this._loadGen) { return; }
			(Array.isArray(tmpRows) ? tmpRows : []).forEach((pU) => { this._agentNames[pU.IDUser] = [ pU.NameFirst, pU.NameLast ].filter(Boolean).join(' ') || pU.LoginID || `User ${pU.IDUser}`; });
		}
	}

	_days(pDays) { const tmpD = Math.round(pDays * 10) / 10; return tmpD < 1 ? `${Math.max(1, Math.round(pDays * 24))}h` : `${tmpD}d`; }
	_catLabel(pKey) { return CATEGORY_LABELS[pKey] || pKey; }

	// ── Render ─────────────────────────────────────────────────────────────────────────────────────────────────

	_paint()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement(this._mount).length === 0) { return; }
		tmpCA.assignContent(this._mount, this._shellHTML());
		this._mountViewPickers();   // re-mount the current view's searchable customer picker(s) after the shell repaint
		this._mountRunbookArticlePicker();   // + the call-entry runbook article picker, when the sidebar is present
	}

	_repaintList()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(LIST_MOUNT).length) { tmpCA.assignContent(LIST_MOUNT, this._listRowsHTML()); }
	}

	_paintFormActions()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#Support-Form-Actions').length) { tmpCA.assignContent('#Support-Form-Actions', this._formActionsHTML()); }
	}

	_paintApproval()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#Support-Approval').length) { tmpCA.assignContent('#Support-Approval', this._approvalHTML()); }
	}

	// ── Settings editor: self-service tiers + priority policy, at Global (platform default) or per-customer ───────

	_settingsGearHTML()
	{
		if (!this.canManage()) { return ''; }
		return `<button class="sup-gear ${this.state.View === 'settings' ? 'sup-gear-on' : ''}" title="Support settings" onclick="_Pict.providers.Support.navigateView('settings'); return false;"><i class="fas fa-gear"></i></button>`;
	}

	_paintSettings() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#Support-Settings').length) { tmpCA.assignContent('#Support-Settings', this._settingsHTML()); this._mountViewPickers(); } }

	async _loadSettingsDraftAsync()
	{
		const tmpSuper = this._isSuperUser();
		if (!this._settings) { this._settings = { scope: tmpSuper ? 'Global' : 'Customer', scopeCustomer: this.idCustomer }; }
		const tmpS = this._settings;
		tmpS.loading = true; tmpS.error = ''; this._paint();
		if (tmpSuper && !this._customers) { try { await this._loadCustomersAsync(); } catch (pErr) { /* names optional */ } }
		try
		{
			const tmpPath = (tmpS.scope === 'Global') ? 'Configuration/For/SupportPortal/In/Global/0' : `Configuration/For/SupportPortal/In/Customer/${tmpS.scopeCustomer}`;
			const tmpBlob = (await this._getJSON(tmpPath)) || {};
			tmpS.blob =
			{
				Settings: (tmpBlob.Settings && typeof tmpBlob.Settings === 'object') ? JSON.parse(JSON.stringify(tmpBlob.Settings)) : { SelfService: false, AllowedCategories: [] },
				PriorityPolicy: (tmpBlob.PriorityPolicy && Array.isArray(tmpBlob.PriorityPolicy.Rules)) ? JSON.parse(JSON.stringify(tmpBlob.PriorityPolicy)) : JSON.parse(JSON.stringify(DEFAULT_PRIORITY_POLICY)),
			};
			if (!Array.isArray(tmpS.blob.Settings.AllowedCategories)) { tmpS.blob.Settings.AllowedCategories = []; }
			if (!tmpS.blob.PriorityPolicy.SLA) { tmpS.blob.PriorityPolicy.SLA = {}; }
			tmpS.loading = false;
		}
		catch (pError) { tmpS.loading = false; tmpS.error = (pError && pError.message) || String(pError); }
		// Same guard as the metrics load: only repaint the shell in list mode, so a settings load resolving after the user
		// has moved to a call form or the inline merge wizard can't clobber the mounted sub-view.
		if (this.state.Mode === 'list') { this._paint(); }
	}

	setSettingsScope(pScope) { if (!this._settings) { return; } this._settings.scope = (pScope === 'Global') ? 'Global' : 'Customer'; this._loadSettingsDraftAsync(); }
	setSettingsCustomer(pID) { if (!this._settings) { return; } this._settings.scopeCustomer = Number(pID) || this.idCustomer; this._loadSettingsDraftAsync(); }
	toggleSelfService(pOn) { if (!this._settings) { return; } this._settings.blob.Settings.SelfService = !!pOn; this._paintSettings(); }
	toggleAllowedCategory(pKey)
	{
		const tmpCats = this._settings.blob.Settings.AllowedCategories;
		const tmpAt = tmpCats.indexOf(pKey);
		if (tmpAt >= 0) { tmpCats.splice(tmpAt, 1); } else { tmpCats.push(pKey); }
		this._paintSettings();
	}
	setPriorityDefault(pValue) { this._settings.blob.PriorityPolicy.Default = pValue; }
	setSLA(pPriority, pValue) { this._settings.blob.PriorityPolicy.SLA[pPriority] = pValue; }
	addPriorityRule() { this._settings.blob.PriorityPolicy.Rules.push({ Keyword: '', Priority: 'High' }); this._paintSettings(); }
	removePriorityRule(pIndex) { this._settings.blob.PriorityPolicy.Rules.splice(pIndex, 1); this._paintSettings(); }
	setRuleType(pIndex, pType)
	{
		const tmpRule = this._settings.blob.PriorityPolicy.Rules[pIndex];
		delete tmpRule.Category; delete tmpRule.App; delete tmpRule.Keyword;
		tmpRule[pType] = '';
		this._paintSettings();
	}
	setRuleValue(pIndex, pValue)
	{
		const tmpRule = this._settings.blob.PriorityPolicy.Rules[pIndex];
		const tmpType = tmpRule.Category !== undefined ? 'Category' : (tmpRule.App !== undefined ? 'App' : 'Keyword');
		tmpRule[tmpType] = pValue;
	}
	setRulePriority(pIndex, pValue) { this._settings.blob.PriorityPolicy.Rules[pIndex].Priority = pValue; }

	async saveSettings()
	{
		const tmpS = this._settings;
		if (!tmpS || tmpS.saving) { return; }
		tmpS.saving = true; this._paintSettings();
		const tmpLevel = (tmpS.scope === 'Global') ? 'Global' : `Customer/${tmpS.scopeCustomer}`;
		try
		{
			const tmpR1 = await this._postJSON(`Configuration/Put/SupportPortal/Settings/${tmpLevel}`, tmpS.blob.Settings);
			if (tmpR1 && (tmpR1.Error || tmpR1.ErrorCode)) { throw new Error(String(tmpR1.Error || tmpR1.ErrorCode)); }
			const tmpR2 = await this._postJSON(`Configuration/Put/SupportPortal/PriorityPolicy/${tmpLevel}`, tmpS.blob.PriorityPolicy);
			if (tmpR2 && (tmpR2.Error || tmpR2.ErrorCode)) { throw new Error(String(tmpR2.Error || tmpR2.ErrorCode)); }
			tmpS.saving = false;
			// If this is the config the current session actually rides, refresh the live copies so guidance/suggestions update.
			const tmpAffectsMe = (tmpS.scope === 'Global') || (tmpS.scope === 'Customer' && Number(tmpS.scopeCustomer) === this.idCustomer);
			if (tmpAffectsMe) { this._portal = tmpS.blob.Settings; this._priorityPolicy = tmpS.blob.PriorityPolicy; }
			this._toast('Support settings saved.', 'success');
			this._paintSettings();
		}
		catch (pError) { tmpS.saving = false; this._toast(`Could not save settings: ${(pError && pError.message) ? pError.message : pError}`, 'error'); this._paintSettings(); }
	}

	_settingsHTML()
	{
		const tmpS = this._settings;
		if (!tmpS || tmpS.loading) { return `<div class="sup-note"><i class="fas fa-spinner fa-spin"></i> Loading settings…</div>`; }
		if (tmpS.error) { return `<div class="sup-note sup-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpS.error)}</div>`; }
		const tmpSuper = this._isSuperUser();
		const tmpSettings = tmpS.blob.Settings;
		const tmpPolicy = tmpS.blob.PriorityPolicy;

		// Scope bar (super-user): platform default vs a specific customer's override.
		let tmpScopeBar = '';
		if (tmpSuper)
		{
			tmpScopeBar = `<div class="sup-set-scope"><span class="sup-set-scopelabel">Editing</span>`
				+ `<button class="sup-scope-b ${tmpS.scope === 'Global' ? 'sup-scope-on' : ''}" onclick="_Pict.providers.Support.setSettingsScope('Global'); return false;"><i class="fas fa-earth-americas"></i> Platform default</button>`
				+ `<button class="sup-scope-b ${tmpS.scope === 'Customer' ? 'sup-scope-on' : ''}" onclick="_Pict.providers.Support.setSettingsScope('Customer'); return false;"><i class="fas fa-building"></i> One customer</button>`
				+ (tmpS.scope === 'Customer' ? `<span id="Support-Set-Cust-Pick" class="sup-set-pick"></span>` : '')
				+ `</div>`;
		}
		const tmpScopeNote = (tmpS.scope === 'Global')
			? `Platform default — inherited by every customer that hasn’t set its own override.`
			: `Override for <b>${this._esc(this._customerName(tmpS.scopeCustomer))}</b> — falls back to the platform default for anything left unset.`;

		// Self-service section.
		const tmpCatBoxes = Object.keys(CATEGORY_LABELS).filter((pK) => pK !== 'Uncategorized').map((pK) =>
		{
			const tmpOn = tmpSettings.AllowedCategories.indexOf(pK) >= 0;
			return `<label class="sup-set-chk ${tmpSettings.SelfService ? '' : 'sup-set-disabled'}"><input type="checkbox" ${tmpOn ? 'checked' : ''} ${tmpSettings.SelfService ? '' : 'disabled'} onchange="_Pict.providers.Support.toggleAllowedCategory('${pK}')"> ${this._esc(CATEGORY_LABELS[pK])}</label>`;
		}).join('');
		const tmpSelfService = `<div class="sup-set-card"><div class="sup-set-h"><i class="fas fa-user-gear"></i> Self-service</div>`
			+ `<label class="sup-set-toggle"><input type="checkbox" ${tmpSettings.SelfService ? 'checked' : ''} onchange="_Pict.providers.Support.toggleSelfService(this.checked)"> Let this tier’s users file their own calls</label>`
			+ `<div class="sup-set-sub">Categories they may choose${tmpSettings.SelfService ? '' : ' (enable self-service first)'}:</div>`
			+ `<div class="sup-set-cats">${tmpCatBoxes}</div></div>`;

		// Priority policy section.
		const tmpPrioOpts = (pSel) => Object.keys(PRIORITY_META).map((pP) => `<option value="${pP}" ${pSel === pP ? 'selected' : ''}>${PRIORITY_META[pP].label}</option>`).join('');
		const tmpSLARows = Object.keys(PRIORITY_META).map((pP) =>
			`<label class="sup-set-sla"><span class="sup-prio ${PRIORITY_META[pP].cls}">${PRIORITY_META[pP].label}</span>`
			+ `<input type="text" placeholder="e.g. 1 business day" value="${this._esc((tmpPolicy.SLA || {})[pP] || '')}" oninput="_Pict.providers.Support.setSLA('${pP}', this.value)"></label>`).join('');
		const tmpRuleRows = (tmpPolicy.Rules || []).map((pRule, pIndex) =>
		{
			const tmpType = pRule.Category !== undefined ? 'Category' : (pRule.App !== undefined ? 'App' : 'Keyword');
			const tmpTypeSel = [ 'Category', 'App', 'Keyword' ].map((pT) => `<option value="${pT}" ${tmpType === pT ? 'selected' : ''}>${pT}</option>`).join('');
			let tmpValueControl = '';
			if (tmpType === 'Category')
			{
				const tmpOpts = Object.keys(CATEGORY_LABELS).filter((pK) => pK !== 'Uncategorized').map((pK) => `<option value="${pK}" ${pRule.Category === pK ? 'selected' : ''}>${this._esc(CATEGORY_LABELS[pK])}</option>`).join('');
				tmpValueControl = `<select onchange="_Pict.providers.Support.setRuleValue(${pIndex}, this.value)"><option value="">—</option>${tmpOpts}</select>`;
			}
			else if (tmpType === 'App')
			{
				const tmpOpts = Object.keys(APP_LABELS).map((pK) => `<option value="${pK}" ${pRule.App === pK ? 'selected' : ''}>${this._esc(APP_LABELS[pK])}</option>`).join('');
				tmpValueControl = `<select onchange="_Pict.providers.Support.setRuleValue(${pIndex}, this.value)"><option value="">—</option>${tmpOpts}</select>`;
			}
			else
			{
				tmpValueControl = `<input type="text" placeholder="regex, e.g. outage|down" value="${this._esc(pRule.Keyword || '')}" oninput="_Pict.providers.Support.setRuleValue(${pIndex}, this.value)">`;
			}
			return `<div class="sup-set-rule"><select class="sup-rule-type" onchange="_Pict.providers.Support.setRuleType(${pIndex}, this.value)">${tmpTypeSel}</select>`
				+ `<span class="sup-rule-val">${tmpValueControl}</span>`
				+ `<span class="sup-rule-arrow">→</span>`
				+ `<select class="sup-rule-prio" onchange="_Pict.providers.Support.setRulePriority(${pIndex}, this.value)">${tmpPrioOpts(pRule.Priority)}</select>`
				+ `<button class="sup-rule-x" title="Remove rule" onclick="_Pict.providers.Support.removePriorityRule(${pIndex}); return false;"><i class="fas fa-xmark"></i></button></div>`;
		}).join('');
		const tmpPriority = `<div class="sup-set-card"><div class="sup-set-h"><i class="fas fa-gauge-high"></i> Priority policy</div>`
			+ `<label class="sup-set-inline">Default priority <select onchange="_Pict.providers.Support.setPriorityDefault(this.value)">${tmpPrioOpts(tmpPolicy.Default)}</select></label>`
			+ `<div class="sup-set-sub">First-response targets</div><div class="sup-set-slas">${tmpSLARows}</div>`
			+ `<div class="sup-set-sub">Suggestion rules <span class="sup-set-hint">— highest-priority match wins; applied when a call is filed without a priority</span></div>`
			+ `<div class="sup-set-rules">${tmpRuleRows || '<div class="sup-set-empty">No rules — calls default to the priority above.</div>'}</div>`
			+ `<button class="sup-btn sup-set-addrule" onclick="_Pict.providers.Support.addPriorityRule(); return false;"><i class="fas fa-plus"></i> Add rule</button></div>`;

		return tmpScopeBar
			+ `<div class="sup-set-note"><i class="fas fa-circle-info"></i> ${tmpScopeNote}</div>`
			+ `<div class="sup-set-grid">${tmpSelfService}${tmpPriority}</div>`
			+ `<div class="sup-set-actions"><button class="sup-btn sup-btn-primary" ${tmpS.saving ? 'disabled' : ''} onclick="_Pict.providers.Support.saveSettings(); return false;">${tmpS.saving ? '<i class="fas fa-spinner fa-spin"></i> Saving…' : '<i class="fas fa-floppy-disk"></i> Save settings'}</button></div>`;
	}

	// ── Support documentation: a staff runbook — platform-wide (Global) + this customer's own, NOT in public Academy ──

	_docsGearHTML()
	{
		// Managers, platform (super-user) editors, and Document-edit holders can open the runbook manager.
		if (!this._canAccessDocs()) { return ''; }
		return `<button class="sup-gear ${this.state.View === 'docs' ? 'sup-gear-on' : ''}" title="Support documentation" onclick="_Pict.providers.Support.navigateView('docs'); return false;"><i class="fas fa-book"></i></button>`;
	}

	_paintDocs()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA.getElement('#Support-Docs').length) { return; }
		tmpCA.assignContent('#Support-Docs', this._docsHTML());
		this._mountViewPickers();   // mount the super-user customer scope picker (#Support-Docs-Cust-Pick)
		// If we just painted the editor, mount the first-class markdown editor into it, seeded with the article body.
		const tmpSel = this.state.DocsSel;
		if (this.state.DocsEditing && tmpSel && tmpCA.getElement('#Support-Doc-Editor-Mount').length)
		{
			const tmpArt = this._docArticle(tmpSel.scope, tmpSel.hash);
			this._mountDocEditor(tmpArt ? (tmpArt.Body || '') : '');
		}
	}

	/** Mount (or re-render) the shared markdown editor into the docs editor, seeded with the article body. */
	_mountDocEditor(pMarkdown)
	{
		if (!this.pict.AppData.SupportDocEditor) { this.pict.AppData.SupportDocEditor = {}; }
		this.pict.AppData.SupportDocEditor.Segments = [ { Content: (pMarkdown == null) ? '' : String(pMarkdown) } ];
		if (!this.pict.views['Support-Doc-Editor'])
		{
			this.pict.addView('Support-Doc-Editor',
				{
					ViewIdentifier: 'Support-Doc-Editor',
					TargetElementAddress: '#Support-Doc-Editor-Mount',
					ContentDataAddress: 'AppData.SupportDocEditor.Segments',
					AutoRender: false,
					EnableRichPreview: true,
					ReadOnly: false,
				}, libMarkdownEditor);
		}
		try { this.pict.views['Support-Doc-Editor'].render(); }
		catch (pError) { this._toast('The markdown editor could not load.', 'error'); }
	}

	/** Marshal the edited markdown back out of the editor into the selected article's Body. */
	_captureDocEditor()
	{
		const tmpView = this.pict.views['Support-Doc-Editor'];
		if (!tmpView) { return; }
		if (typeof tmpView.marshalFromView === 'function') { try { tmpView.marshalFromView(); } catch (pError) { /* editor already gone */ } }
		const tmpSegments = (this.pict.AppData.SupportDocEditor && this.pict.AppData.SupportDocEditor.Segments) || [];
		const tmpBody = tmpSegments.map((pSeg) => (pSeg && pSeg.Content) || '').join('\n\n');
		const tmpSel = this.state.DocsSel;
		const tmpArt = tmpSel ? this._docArticle(tmpSel.scope, tmpSel.hash) : null;
		if (tmpArt) { tmpArt.Body = tmpBody; }
	}

	async _loadDocsAsync()
	{
		this._docs = { loading: true, error: '', global: [], customer: [] };
		this._afterDocsRepaint();
		const tmpSort = (pArr) => (Array.isArray(pArr) ? pArr : []).slice().sort((pA, pB) => (Number(pA.Order) || 0) - (Number(pB.Order) || 0));
		try
		{
			// One endpoint read yields the global (platform) runbooks + this customer's own, newest version each.
			// The docs VIEW may be retargeted by a super-user to manage another customer's runbooks; the sidebar
			// always resets this back to the session customer (see _ensureRunbooksAsync). The global set is returned
			// regardless (the server scopes to IDCustomer IN (0, this customer)), so only the customer set changes.
			const tmpDocsCust = this._docsCustomerID || this.idCustomer;
			// Runbooks are per-row ManifestSchema records (Type='SupportRunbook'); the endpoint returns the newest
			// version of each, split into Global (IDCustomer 0) + this customer's own, already sorted by Order.
			const tmpBlob = (await this._getJSON(`SupportRunbook/For/Customer/${tmpDocsCust}`)) || {};
			this._docs = {
				loading: false, error: '', customerID: tmpDocsCust,
				// Platform (global) runbooks are HeadLight-internal support docs - only show them for an actual support role
				// (agent/manager/super-user), NOT for a customer's Document-editor (who gets only their own customer scope).
				global: this._hasSupportRole() ? tmpSort(tmpBlob.Global) : [],
				customer: tmpSort(tmpBlob.Customer),
			};
			// Default selection: first available article.
			if (!this.state.DocsSel)
			{
				if (this._docs.global.length) { this.state.DocsSel = { scope: 'global', hash: this._docs.global[0].Hash }; }
				else if (this._docs.customer.length) { this.state.DocsSel = { scope: 'customer', hash: this._docs.customer[0].Hash }; }
			}
		}
		catch (pError) { this._docs = { loading: false, error: (pError && pError.message) || String(pError), global: [], customer: [] }; }
		this._afterDocsRepaint();
	}

	/** After runbook data changes: repaint only the call-entry aside while a call is open, else the full docs shell. */
	// A docs/runbooks load can resolve while the inline merge wizard owns #Support-Host (e.g. open a call → _ensureRunbooksAsync
	// fires → click "Merge into another ticket…" before the GET returns). A full _paint() here would rebuild an EMPTY
	// #SupportMerge-Wizard and wipe the accordion, so skip it in merge mode — the merge view hosts no runbook surface, and
	// this._docs stays loaded for when the user returns to the list (exitMerge → loadAsync → _paint).
	_afterDocsRepaint() { if (this.state.Mode === 'merge') { return; } if (this.state.Mode === 'form') { this._paintRunbook(); } else { this._paint(); } }

	// ── Runbook companion: a read-only browse+view of the same runbooks (this._docs) beside the call-entry form ──

	/** Lazily load runbook articles for the sidebar; reuses this._docs if a manager already opened the Docs view. */
	_ensureRunbooksAsync()
	{
		// The sidebar always shows the SESSION customer's docs. If the Docs view retargeted _docs to another
		// customer (super-user managing that customer's runbooks), reload for the session customer first.
		if (this._docs && Number(this._docs.customerID || this.idCustomer) === Number(this.idCustomer)) { this._paintRunbook(); return; }
		this._docsCustomerID = this.idCustomer;
		this._loadDocsAsync();
	}

	/** Super-user: point the Docs view at another customer's runbooks (DocsCustomer at that customer's scope). */
	setDocsCustomer(pID)
	{
		const tmpID = Number(pID) || this.idCustomer;
		if (tmpID === Number(this._docsCustomerID || this.idCustomer)) { return; }
		this._docsCustomerID = tmpID;
		this.state.DocsSel = null; this.state.DocsEditing = false;   // article selection is per-customer
		this._loadDocsAsync();
	}

	/** Load the ACTIVE CALL's customer playbook (DocsCustomer at that customer's scope) so the caller-specific runbook
	 *  shows beside the call even though the console itself runs as the central HeadLight queue (customer 1). */
	async _loadCallerRunbooksAsync(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer) || 0;
		if (!tmpID || tmpID === Number(this.idCustomer)) { this._callerDocs = null; this._paintRunbook(); return; }
		if (this._callerDocs && this._callerDocs.idCustomer === tmpID && !this._callerDocs.loading) { return; }   // already loaded for this caller
		this._callerDocs = { idCustomer: tmpID, name: this._customerName(tmpID), loading: true, articles: [] };
		try
		{
			const tmpBlob = (await this._getJSON(`SupportRunbook/For/Customer/${tmpID}`)) || {};
			const tmpArts = tmpBlob.Customer || [];
			this._callerDocs = { idCustomer: tmpID, name: this._customerName(tmpID), loading: false, articles: (Array.isArray(tmpArts) ? tmpArts : []).slice().sort((pA, pB) => (Number(pA.Order) || 0) - (Number(pB.Order) || 0)) };
		}
		catch (pErr) { this._callerDocs = { idCustomer: tmpID, name: this._customerName(tmpID), loading: false, articles: [] }; }
		this._paintRunbook();
	}

	/** Repaint ONLY the runbook aside — never the shell (a full _paint would tear down the mounted #Support-FormHost). */
	_paintRunbook()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpEl = tmpCA.getElement('#Support-Runbook');
		if (!tmpEl || !tmpEl.length) { return; }
		tmpEl[0].classList.toggle('sup-runbook-collapsed', this.state.RunbookOpen === false);
		tmpEl[0].innerHTML = this._runbookHTML();
		this._mountRunbookArticlePicker();   // the article selector is a searchable picker, mounted after this repaint
	}

	toggleRunbook() { this.state.RunbookOpen = !(this.state.RunbookOpen !== false); this._persistFilters(); this._paintRunbook(); }
	selectRunbook(pScope, pHash) { this.state.RunbookSel = { scope: pScope, hash: pHash }; this._paintRunbook(); }

	_runbookHTML()
	{
		if (this.state.RunbookOpen === false)
		{
			return `<button class="sup-rb-tab" title="Show runbooks" onclick="_Pict.providers.Support.toggleRunbook(); return false;"><i class="fas fa-book"></i></button>`;
		}
		const tmpHead = `<div class="sup-rb-head"><span class="sup-rb-h"><i class="fas fa-book"></i> Runbooks</span>`
			+ `<button class="sup-rb-x" title="Hide" onclick="_Pict.providers.Support.toggleRunbook(); return false;"><i class="fas fa-angles-right"></i></button></div>`;
		const tmpD = this._docs;
		if (!tmpD || tmpD.loading) { return tmpHead + `<div class="sup-rb-msg"><i class="fas fa-spinner fa-spin"></i> Loading runbooks…</div>`; }
		if (tmpD.error) { return tmpHead + `<div class="sup-rb-msg sup-note-error">${this._esc(tmpD.error)}</div>`; }
		// The active caller's own customer playbook comes FIRST (most relevant to the call in hand), then platform-wide
		// runbooks, then the console customer's own.
		const tmpArts = this._runbookArticles();
		if (!tmpArts.length) { return tmpHead + `<div class="sup-rb-msg">No runbooks published yet — add them under the <i class="fas fa-book"></i> Docs view.</div>`; }
		const tmpSel = this.state.RunbookSel || { scope: tmpArts[0].scope, hash: tmpArts[0].Hash };
		// The article LIST is a searchable pict-section-picker (mounted after paint into #Support-Runbook-Pick), not a
		// long vertical nav — so a big runbook list no longer pushes the body down; you pick from the dropdown and read below.
		const tmpIcon = (pS) => (pS === 'global') ? 'fa-earth-americas' : (pS === 'caller' ? 'fa-building-user' : 'fa-building');
		const tmpWord = (pS) => (pS === 'global') ? 'Platform' : (pS === 'caller' ? 'Caller playbook' : 'Customer');
		const tmpArt = this._docArticle(tmpSel.scope, tmpSel.hash) || {};
		const tmpBody = `<div class="sup-rb-arth"><i class="fas ${tmpIcon(tmpSel.scope)} sup-rb-scope"></i> ${this._esc(tmpArt.Title || 'Untitled')} <span class="sup-rb-scopeword">${tmpWord(tmpSel.scope)}</span></div>`
			+ `<div class="sup-doc-content sup-rb-content">${this._md(tmpArt.Body)}</div>`;
		return tmpHead + `<div class="sup-rb-pick"><span id="Support-Runbook-Pick"></span></div><div class="sup-rb-body">${tmpBody}</div>`;
	}

	/** The runbook articles the sidebar offers, in priority order: the active caller's own playbook, then platform-wide,
	 *  then the console customer's own. Shared by _runbookHTML and the article picker. */
	_runbookArticles()
	{
		const tmpD = this._docs || {};
		const tmpCaller = this._callerDocs;
		return []
			.concat((tmpCaller && tmpCaller.articles || []).map((pA) => ({ scope: 'caller', Hash: pA.Hash, Title: pA.Title })))
			.concat((tmpD.global || []).map((pA) => ({ scope: 'global', Hash: pA.Hash, Title: pA.Title })))
			.concat((tmpD.customer || []).map((pA) => ({ scope: 'customer', Hash: pA.Hash, Title: pA.Title })));
	}

	/** Mount the searchable runbook-article picker into #Support-Runbook-Pick (a no-op if the span isn't present). */
	_mountRunbookArticlePicker()
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		if (tmpCA.getElement('#Support-Runbook-Pick').length === 0) { return; }
		const tmpArts = this._runbookArticles();
		if (!tmpArts.length) { return; }
		const tmpKey = (pA) => `${pA.scope}::${pA.Hash}`;
		const tmpWord = (pS) => (pS === 'global') ? 'Platform' : (pS === 'caller' ? 'Caller' : 'Customer');
		const tmpLabel = (pA) => `${tmpWord(pA.scope)} · ${pA.Title || 'Untitled'}`;
		if (!this.pict.AppData.SupportPickers) { this.pict.AppData.SupportPickers = {}; }
		const tmpInstance = tmpPicker.createPicker('Support-RunbookArticle',
			{
				DestinationAddress: '#Support-Runbook-Pick',
				ValueAddress: 'AppData.SupportPickers.RunbookArticle',
				Searchable: true, AllowClear: false, Placeholder: 'Find a runbook…',
				DataProvider: (pTerm) =>
				{
					const tmpT = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
					const tmpRows = tmpArts.map((pA) => ({ Value: tmpKey(pA), Text: tmpLabel(pA) })).filter((pR) => !tmpT || pR.Text.toLowerCase().indexOf(tmpT) >= 0);
					return Promise.resolve({ results: tmpRows, hasMore: false });
				},
				ResolveValue: (pVal) => { const tmpA = tmpArts.find((pA) => tmpKey(pA) === pVal); return Promise.resolve(tmpA ? { Value: pVal, Text: tmpLabel(tmpA) } : null); },
				OnChange: (pValue) => { const tmpA = tmpArts.find((pA) => tmpKey(pA) === pValue); if (tmpA) { this.selectRunbook(tmpA.scope, tmpA.Hash); } },
			});
		if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
		const tmpSel = this.state.RunbookSel || { scope: tmpArts[0].scope, hash: tmpArts[0].Hash };
		if (tmpInstance && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(`${tmpSel.scope}::${tmpSel.hash}`); }
	}

	_docArticle(pScope, pHash)
	{
		if (pScope === 'caller') { return ((this._callerDocs && this._callerDocs.articles) || []).find((pA) => pA.Hash === pHash) || null; }
		return (this._docs && this._docs[pScope] || []).find((pA) => pA.Hash === pHash) || null;
	}
	selectDoc(pScope, pHash) { if (this.state.DocsEditing) { this._captureDocEditor(); } this.state.DocsSel = { scope: pScope, hash: pHash }; this.state.DocsEditing = false; this._paintDocs(); }
	toggleDocsEdit() { if (this.state.DocsEditing) { this._captureDocEditor(); } this.state.DocsEditing = !this.state.DocsEditing; this._paintDocs(); }

	docDragStart(pScope, pHash) { this._docDrag = { scope: pScope, hash: pHash }; }
	/** Drop onto a runbook nav item: a dragged .md file uploads into this scope; a dragged item reorders in place. */
	async docDrop(pEvent, pScope, pTargetHash)
	{
		const tmpFiles = pEvent && pEvent.dataTransfer && pEvent.dataTransfer.files;
		if (tmpFiles && tmpFiles.length) { this._docDrag = null; return this.uploadDocs(pScope, tmpFiles); }   // a file dropped on an item still uploads
		const tmpDrag = this._docDrag; this._docDrag = null;
		if (!tmpDrag || tmpDrag.scope !== pScope || tmpDrag.hash === pTargetHash) { return; }
		if (!this.canEditRunbooks(pScope) || this.state.Busy) { return; }
		const tmpArr = (this._docs && this._docs[pScope]) || [];
		const tmpFrom = tmpArr.findIndex((pA) => pA.Hash === tmpDrag.hash);
		const tmpTo = tmpArr.findIndex((pA) => pA.Hash === pTargetHash);
		if (tmpFrom < 0 || tmpTo < 0) { return; }
		const tmpMoved = tmpArr.splice(tmpFrom, 1)[0];
		tmpArr.splice(tmpTo, 0, tmpMoved);
		tmpArr.forEach((pA, pIdx) => { pA.Order = pIdx; });   // renumber locally to mirror the server reorder
		this.state.Busy = true; this._paintDocs();
		try
		{
			const tmpRes = await this._putJSON('SupportRunbook/Reorder', { IDCustomer: this._scopeIDCustomer(pScope), Order: tmpArr.map((pA) => pA.Hash) });
			if (tmpRes && (tmpRes.Error || tmpRes.ErrorCode)) { throw new Error(String(tmpRes.Error || tmpRes.ErrorCode)); }
			this.state.Busy = false; this._paintDocs();
		}
		catch (pError) { this.state.Busy = false; this._toast(`Reorder failed: ${(pError && pError.message) ? pError.message : pError}`, 'error'); await this._loadDocsAsync(); }
	}

	addDoc(pScope)
	{
		if (!this._docs) { return; }
		const tmpHash = 'doc-' + Date.now().toString(36);
		const tmpMax = (this._docs[pScope] || []).reduce((pN, pA) => Math.max(pN, Number(pA.Order) || 0), 0);
		this._docs[pScope].push({ Hash: tmpHash, Title: 'Untitled article', Body: '', Order: tmpMax + 1 });
		this.state.DocsSel = { scope: pScope, hash: tmpHash };
		this.state.DocsEditing = true;
		this._paintDocs();
	}
	setDocField(pField, pValue)
	{
		const tmpSel = this.state.DocsSel; if (!tmpSel) { return; }
		const tmpArt = this._docArticle(tmpSel.scope, tmpSel.hash); if (!tmpArt) { return; }
		tmpArt[pField] = pValue;
	}

	/** The IDCustomer a runbook scope writes to: global -> 0, customer -> the viewed (or session) customer. */
	_scopeIDCustomer(pScope)
	{
		return (pScope === 'global') ? 0 : (Number(this._docsCustomerID || this.idCustomer) || 0);
	}

	/** Persist ONE runbook as a new version row via the scoped SupportRunbook endpoint (server enforces the edit gate). */
	async _persistRunbookAsync(pScope, pArticle)
	{
		const tmpRes = await this._putJSON('SupportRunbook', {
			IDCustomer: this._scopeIDCustomer(pScope),
			Hash: pArticle.Hash,
			Title: pArticle.Title || '',
			Body: pArticle.Body || '',
			Order: Number(pArticle.Order) || 0,
		});
		if (tmpRes && (tmpRes.Error || tmpRes.ErrorCode)) { throw new Error(String(tmpRes.Error || tmpRes.ErrorCode)); }
	}
	async saveDoc()
	{
		const tmpSel = this.state.DocsSel; if (!tmpSel || this.state.Busy) { return; }
		this._captureDocEditor();   // marshal the markdown editor's content into the article before persisting
		const tmpArt = this._docArticle(tmpSel.scope, tmpSel.hash); if (!tmpArt) { return; }
		this.state.Busy = true; this._paintDocs();
		try { await this._persistRunbookAsync(tmpSel.scope, tmpArt); this.state.Busy = false; this.state.DocsEditing = false; this._toast('Documentation saved.', 'success'); this._paintDocs(); }
		catch (pError) { this.state.Busy = false; this._toast(`Could not save: ${(pError && pError.message) ? pError.message : pError}`, 'error'); this._paintDocs(); }
	}
	async deleteDoc()
	{
		const tmpSel = this.state.DocsSel; if (!tmpSel || this.state.Busy) { return; }
		const tmpArt = this._docArticle(tmpSel.scope, tmpSel.hash);
		if (!tmpArt || (typeof window !== 'undefined' && !window.confirm(`Delete “${tmpArt.Title || 'this article'}”?`))) { return; }
		const tmpScope = tmpSel.scope, tmpHash = tmpSel.hash;
		this._docs[tmpScope] = this._docs[tmpScope].filter((pA) => pA.Hash !== tmpHash);
		this.state.DocsSel = null; this.state.DocsEditing = false;
		this.state.Busy = true; this._paintDocs();
		try { const tmpRes = await this._putJSON('SupportRunbook/Delete', { IDCustomer: this._scopeIDCustomer(tmpScope), Hash: tmpHash }); if (tmpRes && (tmpRes.Error || tmpRes.ErrorCode)) { throw new Error(String(tmpRes.Error || tmpRes.ErrorCode)); } this.state.Busy = false; this._toast('Article deleted.', 'info'); this._paintDocs(); }
		catch (pError) { this.state.Busy = false; this._toast(`Could not delete: ${(pError && pError.message) ? pError.message : pError}`, 'error'); await this._loadDocsAsync(); }
	}

	/** Minimal safe markdown → HTML (escape first) for support-doc bodies: headings, bold/italic/code, lists, links. */
	_md(pText)
	{
		// Render authored article markdown with the shared marked-based renderer (_renderMd → libUserMarkdown):
		// blockquotes, numbered + nested lists, tables and code fences all render, HTML-escaped + safe-URL, identically
		// to the composer/timeline and the editor's rich preview. The old hand-rolled parser only handled #/-/*/`/links,
		// so the blockquote-heavy runbooks (the acknowledgement email templates are `>` callouts) rendered every quoted
		// line as a literal "> " paragraph — the "markdown isn't rendering properly" the runbook sidebar showed.
		const tmpSource = String(pText == null ? '' : pText);
		if (!tmpSource.trim()) { return '<p class="sup-doc-empty">This article is empty.</p>'; }
		return this._renderMd(tmpSource);
	}

	_docNavHTML(pScope, pLabel, pCanEdit)
	{
		const tmpArts = (this._docs && this._docs[pScope]) || [];
		const tmpSel = this.state.DocsSel || {};
		const tmpItems = tmpArts.map((pA) =>
		{
			// Editors can drag to reorder these items; a .md file dropped onto an item still uploads into this scope
			// (docDrop inspects dataTransfer to tell a file-drop from an item-reorder).
			const tmpDrag = pCanEdit
				? ` draggable="true" ondragstart="_Pict.providers.Support.docDragStart('${pScope}','${this._esc(pA.Hash)}');" ondragover="event.preventDefault();" ondrop="event.preventDefault(); event.stopPropagation(); _Pict.providers.Support.docDrop(event,'${pScope}','${this._esc(pA.Hash)}'); return false;"`
				: '';
			return `<button class="sup-doc-nav-i ${tmpSel.scope === pScope && tmpSel.hash === pA.Hash ? 'sup-doc-nav-on' : ''}"${tmpDrag} onclick="_Pict.providers.Support.selectDoc('${pScope}','${this._esc(pA.Hash)}'); return false;">${this._esc(pA.Title || 'Untitled')}</button>`;
		}).join('')
			|| `<div class="sup-doc-nav-empty">None yet</div>`;
		// Editors (customer = manager, platform = super-user) get Add + Upload, plus the whole group is a drag-drop target
		// for bulk .md upload into THIS scope. The onchange clears value so re-picking the same file fires again.
		let tmpTools = '';
		let tmpDropAttrs = '';
		let tmpHint = '';
		if (pCanEdit)
		{
			const tmpInputId = `Support-Doc-Upload-${pScope}`;
			tmpTools = `<button class="sup-doc-add" onclick="_Pict.providers.Support.addDoc('${pScope}'); return false;"><i class="fas fa-plus"></i> Add</button>`
				+ `<button class="sup-doc-add" title="Upload markdown file(s)" ${this.state.Busy ? 'disabled' : ''} onclick="document.getElementById('${tmpInputId}').click(); return false;"><i class="fas fa-upload"></i> Upload</button>`
				+ `<input type="file" id="${tmpInputId}" accept=".md,.markdown,.txt,text/markdown" multiple style="display:none" onchange="_Pict.providers.Support.uploadDocs('${pScope}', this.files); this.value='';">`;
			tmpDropAttrs = ` ondragover="event.preventDefault(); this.classList.add('sup-doc-drag');" ondragleave="this.classList.remove('sup-doc-drag');" ondrop="event.preventDefault(); this.classList.remove('sup-doc-drag'); _Pict.providers.Support.uploadDocs('${pScope}', event.dataTransfer.files); return false;"`;
			tmpHint = `<div class="sup-doc-drophint"><i class="fas fa-cloud-arrow-up"></i> Drop .md files to upload</div>`;
		}
		return `<div class="sup-doc-navgroup"${tmpDropAttrs}><div class="sup-doc-navh">${this._esc(pLabel)}<span class="sup-doc-navtools">${tmpTools}</span></div>${tmpItems}${tmpHint}</div>`;
	}

	/** Read a File as text (Promise wrapper over FileReader). */
	_readFileText(pFile)
	{
		return new Promise((resolve, reject) =>
		{
			try { const tmpReader = new FileReader(); tmpReader.onload = () => resolve(String(tmpReader.result || '')); tmpReader.onerror = () => reject(tmpReader.error || new Error('read failed')); tmpReader.readAsText(pFile); }
			catch (pError) { reject(pError); }
		});
	}

	/** An article title from an uploaded file: its first markdown `# heading`, else the filename without extension. */
	_titleFromFile(pName, pText)
	{
		const tmpHeading = /^\s*#\s+(.+?)\s*$/m.exec(String(pText || ''));
		if (tmpHeading && tmpHeading[1].trim()) { return tmpHeading[1].trim().slice(0, 120); }
		return String(pName || 'Untitled').replace(/\.(md|markdown|txt)$/i, '').replace(/[-_]+/g, ' ').trim() || 'Untitled';
	}

	/** Bulk-upload markdown file(s) into a scope's runbooks: one article per file, then persist the scope once. */
	async uploadDocs(pScope, pFileList)
	{
		if (!this.canEditRunbooks(pScope)) { this._toast(`You don’t have permission to upload ${pScope === 'global' ? 'platform' : 'customer'} runbooks.`, 'error'); return; }
		if (!this._docs || !Array.isArray(this._docs[pScope])) { return; }
		const tmpFiles = Array.from(pFileList || []).filter((pF) => pF && /\.(md|markdown|txt)$/i.test(pF.name));
		if (!tmpFiles.length) { this._toast('Drop or choose markdown (.md) file(s).', 'info'); return; }
		if (this.state.Busy) { return; }
		this.state.Busy = true; this._paintDocs();
		try
		{
			const tmpBaseOrder = (this._docs[pScope] || []).reduce((pN, pA) => Math.max(pN, Number(pA.Order) || 0), 0);
			const tmpStamp = Date.now().toString(36);
			// Read ALL files first, then persist one runbook row per file. Each file is an independent row now, so a
			// mid-batch failure leaves the already-saved rows intact - report how many landed and resync from the server.
			const tmpBatch = [];
			for (let tmpI = 0; tmpI < tmpFiles.length; tmpI++)
			{
				const tmpText = await this._readFileText(tmpFiles[tmpI]);
				tmpBatch.push({ Hash: `doc-${tmpStamp}-${tmpI}`, Title: this._titleFromFile(tmpFiles[tmpI].name, tmpText), Body: tmpText, Order: tmpBaseOrder + 1 + tmpI });
			}
			let tmpSaved = 0;
			for (const tmpArticle of tmpBatch)
			{
				await this._persistRunbookAsync(pScope, tmpArticle);
				this._docs[pScope].push(tmpArticle);   // only reflect locally AFTER the row is persisted
				tmpSaved++;
			}
			this.state.Busy = false;
			if (tmpSaved) { this.state.DocsSel = { scope: pScope, hash: tmpBatch[tmpSaved - 1].Hash }; this.state.DocsEditing = false; }   // land on the last uploaded article
			this._toast(`Uploaded ${tmpSaved} runbook${tmpSaved === 1 ? '' : 's'} to ${pScope === 'global' ? 'Platform' : 'this customer'}.`, 'success');
			this._paintDocs();
		}
		catch (pError) { this.state.Busy = false; this._toast(`Upload failed after saving some files: ${(pError && pError.message) ? pError.message : pError}`, 'error'); await this._loadDocsAsync(); }
	}

	/** Download the selected runbook article as a .md file. */
	downloadCurrentDoc()
	{
		const tmpSel = this.state.DocsSel; if (!tmpSel) { return; }
		if (tmpSel.scope === 'global' && !this._hasSupportRole()) { return; }   // platform runbooks are support-staff-only
		const tmpArt = this._docArticle(tmpSel.scope, tmpSel.hash); if (!tmpArt) { return; }
		const tmpName = (String(tmpArt.Title || 'runbook').replace(/[^A-Za-z0-9 _-]+/g, '').trim().replace(/\s+/g, '-') || 'runbook').slice(0, 80);
		this._triggerBrowserDownload(new Blob([ String(tmpArt.Body || '') ], { type: 'text/markdown;charset=utf-8' }), `${tmpName}.md`);
	}

	_docsHTML()
	{
		const tmpD = this._docs;
		if (!tmpD || tmpD.loading) { return `<div class="sup-note"><i class="fas fa-spinner fa-spin"></i> Loading documentation…</div>`; }
		if (tmpD.error) { return `<div class="sup-note sup-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpD.error)}</div>`; }
		const tmpSuper = this._isSuperUser();
		const tmpDocsCust = this._docsCustomerID || this.idCustomer;
		// Super-users manage ANY customer's runbooks (pick via the searchable picker); non-super-users see only their own.
		const tmpCustNavLabel = tmpSuper ? (this._customerName(tmpDocsCust) || `Customer ${tmpDocsCust}`) : 'This customer';
		// The Platform (global) runbook section is visible only to support staff/super-users — not a customer Document-editor.
		const tmpNav = (this._hasSupportRole() ? this._docNavHTML('global', 'Platform', this.canEditRunbooks('global')) : '') + this._docNavHTML('customer', tmpCustNavLabel, this.canEditRunbooks('customer'));

		const tmpSel = this.state.DocsSel;
		const tmpArt = tmpSel ? this._docArticle(tmpSel.scope, tmpSel.hash) : null;
		let tmpBody;
		if (!tmpArt)
		{
			tmpBody = `<div class="sup-doc-placeholder"><i class="fas fa-book-open"></i><div>Pick an article, or add one.</div>`
				+ `<p class="sup-doc-note">Platform articles are the shared support runbook (every customer). “This customer” articles are private to this organization. Neither appears in the public Academy.</p></div>`;
		}
		else
		{
			// Customer docs: a manager OR a Document-edit (Document-Update) holder; platform docs: super-user only.
			const tmpCanEdit = this.canEditRunbooks(tmpSel.scope);
			if (this.state.DocsEditing && tmpCanEdit)
			{
				// The article body uses the app's first-class markdown editor (mounted into #Support-Doc-Editor-Mount
				// after this paint); the value is marshaled back on Preview/Save, not via an input binding.
				tmpBody = `<div class="sup-doc-editor">`
					+ `<input class="sup-doc-title-in" type="text" placeholder="Article title" value="${this._esc(tmpArt.Title || '')}" oninput="_Pict.providers.Support.setDocField('Title', this.value)">`
					+ `<div id="Support-Doc-Editor-Mount" class="sup-doc-mde"></div>`
					+ `<div class="sup-doc-ed-actions"><button class="sup-doc-del" ${this.state.Busy ? 'disabled' : ''} onclick="_Pict.providers.Support.deleteDoc(); return false;"><i class="fas fa-trash"></i> Delete</button>`
					+ `<span class="sup-doc-ed-right"><button class="sup-btn" onclick="_Pict.providers.Support.toggleDocsEdit(); return false;">Preview</button>`
					+ `<button class="sup-btn sup-btn-primary" ${this.state.Busy ? 'disabled' : ''} onclick="_Pict.providers.Support.saveDoc(); return false;">${this.state.Busy ? '<i class="fas fa-spinner fa-spin"></i>' : '<i class="fas fa-floppy-disk"></i> Save'}</button></span></div></div>`;
			}
			else
			{
				const tmpEditBtn = tmpCanEdit ? `<button class="sup-doc-editbtn" onclick="_Pict.providers.Support.toggleDocsEdit(); return false;"><i class="fas fa-pen"></i> Edit</button>` : '';
				const tmpDownloadBtn = `<button class="sup-doc-editbtn" title="Download this runbook as markdown" onclick="_Pict.providers.Support.downloadCurrentDoc(); return false;"><i class="fas fa-download"></i> .md</button>`;
				const tmpScopeTag = tmpSel.scope === 'global' ? `<span class="sup-doc-scope sup-doc-scope-g"><i class="fas fa-earth-americas"></i> Platform</span>` : `<span class="sup-doc-scope sup-doc-scope-c"><i class="fas fa-building"></i> This customer</span>`;
				tmpBody = `<div class="sup-doc-article"><div class="sup-doc-arth"><h2 class="sup-doc-title">${this._esc(tmpArt.Title || 'Untitled')}</h2>${tmpScopeTag}${tmpDownloadBtn}${tmpEditBtn}</div>`
					+ `<div class="sup-doc-content">${this._md(tmpArt.Body)}</div></div>`;
			}
		}
		// Super-user scope bar: choose which customer's runbooks to manage (searchable picker mounted after paint).
		const tmpScopeBar = tmpSuper
			? `<div class="sup-doc-scopebar"><span class="sup-doc-scopelabel"><i class="fas fa-building"></i> Customer runbooks for</span><span id="Support-Docs-Cust-Pick" class="sup-doc-cust-pick"></span></div>`
			: '';
		return `${tmpScopeBar}<div class="sup-docs"><aside class="sup-doc-nav">${tmpNav}</aside><section class="sup-doc-main">${tmpBody}</section></div>`;
	}

	_shellHTML()
	{
		const tmpState = this.state;
		if (!this.canUse()) { return `<div class="sup-note sup-note-locked"><i class="fas fa-lock"></i> You don’t have access to the Support console.</div>`; }
		if (this._manifestError) { return this._notConfiguredHTML(); }
		// A tier-1 user whose organization hasn't enabled self-service can't file here.
		// A runbook-ONLY user (no support role) falls through to the Docs surface (handled by the _isDocsOnly branch below)
		// even when self-service filing is off. A tier-1 AGENT (has a support role) still gets the blocking notice — exempting
		// them here would wrongly drop them onto the full triage list + New-call in a non-self-service customer.
		if (this.isSelfService() && !this._isDocsOnly() && this._portal && !this._portal.SelfService)
		{
			return `<div class="sup-note sup-note-empty"><i class="fas fa-headset"></i>`
				+ `<div><b>Support isn’t self-service for your organization.</b><br>Please reach your HeadLight support contact to open a call.</div></div>`;
		}
		// Runbook-only user: the entire console is the runbook manager — no triage list, calls, metrics or settings.
		if (this._isDocsOnly())
		{
			if (tmpState.View !== 'docs') { tmpState.View = 'docs'; }
			return `<div class="sup-toolbar"><div class="sup-toolbar-left"><span class="sup-docsonly-h"><i class="fas fa-book"></i> Support runbooks</span></div></div>`
				+ `<div id="Support-Docs">${this._docsHTML()}</div>`;
		}
		if (tmpState.Mode === 'merge') { return this._mergeViewHTML(); }
		if (tmpState.Mode === 'form') { return this._formHTML(); }
		return this._listHTML();
	}

	/** The inline merge-wizard view (Mode 'merge') — a first-class console surface, NOT a modal. It paints the container
	 *  the SupportMerge provider builds its accordion into; the page scrolls normally (no overlay, no nested scrollbox). */
	_mergeViewHTML()
	{
		return `<div class="sup-form-head">`
			+ `<div class="sup-form-head-left"><a class="sup-back" onclick="_Pict.providers.Support.exitMerge(); return false;"><i class="fas fa-arrow-left"></i> All calls</a>`
			+ `<h2 class="sup-form-title"><i class="fas fa-code-merge"></i> Merge tickets</h2></div></div>`
			+ `<div class="supmg-view"><div id="SupportMerge-Wizard" class="supmg-wizard"></div></div>`;
	}

	_notConfiguredHTML()
	{
		return `<div class="sup-note sup-note-empty"><i class="fas fa-headset"></i>`
			+ `<div><b>Support isn’t set up for this customer yet.</b><br>`
			+ `${this._esc(this.state.Error || 'Run the support-portal seeder to create the Support Call form and workflow.')}</div></div>`;
	}

	_listHTML()
	{
		const tmpState = this.state;
		const tmpCounts = { New: 0, Acknowledged: 0, Tier1: 0, Tier2: 0, Tier3: 0, Resolved: 0, Void: 0 };
		tmpState.Calls.forEach((pCall) => { if (tmpCounts[pCall.Status] != null) { tmpCounts[pCall.Status]++; } });
		const tmpNewBtn = `<button class="sup-btn sup-btn-primary" onclick="_Pict.providers.Support.startCreate(); return false;"><i class="fas fa-plus"></i> New support call</button>`;
		const tmpBusy = tmpState.Busy ? `<span class="sup-inline-busy"><i class="fas fa-spinner fa-spin"></i></span>` : '';
		const tmpTools = `${this._segHTML()}${this._docsGearHTML()}${this._settingsGearHTML()}`;
		if (tmpState.View === 'docs' && this._canAccessDocs())
		{
			return `<div class="sup-toolbar"><div class="sup-toolbar-left">${tmpTools}</div>`
				+ `<div class="sup-toolbar-right">${tmpNewBtn}</div></div>`
				+ `<div id="Support-Docs">${this._docsHTML()}</div>`;
		}
		if (tmpState.View === 'settings' && this.canManage())
		{
			return `<div class="sup-toolbar"><div class="sup-toolbar-left">${tmpTools}</div>`
				+ `<div class="sup-toolbar-right">${tmpNewBtn}</div></div>`
				+ `<div id="Support-Settings">${this._settingsHTML()}</div>`;
		}
		if (tmpState.View === 'metrics' && this.canManage())
		{
			return `<div class="sup-toolbar"><div class="sup-toolbar-left">${tmpTools}</div>`
				+ `<div class="sup-toolbar-right">${tmpNewBtn}</div></div>`
				+ `<div id="Support-Metrics">${this._metricsHTML()}</div>`;
		}
		const tmpExport = this.canManage() ? `<button class="sup-btn" onclick="_Pict.providers.Support.exportCallsCSV(); return false;"><i class="fas fa-file-csv"></i> Export</button>` : '';
		return `<div class="sup-toolbar">`
			+ `<div class="sup-toolbar-left">${this._segHTML()}${this._docsGearHTML()}${this._settingsGearHTML()}${this._statusChipsHTML(tmpCounts)}</div>`
			+ `<div class="sup-toolbar-right">`
			+ `<div class="sup-search"><i class="fas fa-magnifying-glass"></i><input type="text" placeholder="Search calls…" value="${this._esc(tmpState.Filter)}" oninput="_Pict.providers.Support.onSearch(this.value)" /></div>`
			+ `${tmpExport}${tmpNewBtn}${tmpBusy}</div></div>`
			+ `<div class="sup-filterbar">${this._filterBarHTML()}</div>`
			+ (this.canManage() ? `<div id="Support-MergeBar">${this._mergeBarHTML()}</div>` : '')
			+ (tmpState.Error ? `<div class="sup-note sup-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Error)}</div>` : '')
			+ `<div id="Support-List" class="sup-listwrap">${this._listRowsHTML()}</div>`;
	}

	/** Status quick-filter chips (multi-select into Filters.Status) with live counts. */
	_statusChipsHTML(pCounts)
	{
		const tmpSel = this.state.Filters.Status || [];   // a stale/foreign persisted filter blob may lack this facet key
		return `<div class="sup-chips">` + Object.keys(STATUS_ORDER).map((pStatus) =>
		{
			const tmpMeta = this._statusMeta(pStatus);
			const tmpActive = tmpSel.indexOf(pStatus) >= 0 ? ' sup-chip-active' : '';
			return `<button class="sup-chip ${tmpMeta.cls}${tmpActive}" onclick="_Pict.providers.Support.toggleFilter('Status','${pStatus}'); return false;">`
				+ `<i class="fas ${tmpMeta.icon}"></i> ${tmpMeta.label} <span class="sup-chip-n">${pCounts[pStatus] || 0}</span></button>`;
		}).join('') + `</div>`;
	}

	/** Priority / Category / App multi-select dropdowns + a Clear + a "showing N of M" count. */
	_filterBarHTML()
	{
		const tmpState = this.state;
		const tmpFacets = this._facets();
		const tmpActive = tmpFacets.reduce((pN, pF) => pN + ((tmpState.Filters[pF.key] || []).length), 0);
		const tmpDrops = tmpFacets.filter((pF) => pF.key !== 'Status').map((pF) => this._facetDropdownHTML(pF)).join('');
		const tmpDF = tmpState.DateFilter || { from: '', to: '' };
		const tmpDateActive = !!(tmpDF.from || tmpDF.to);
		const tmpDateRange = `<span class="sup-dd sup-dd-daterange${tmpDateActive ? ' sup-dd-on' : ''}">`
			+ `<span class="sup-dd-dr-label"><i class="fas fa-calendar-day"></i> Designated</span>`
			+ `<input type="date" class="sup-date-in" value="${this._esc(tmpDF.from)}" max="${this._esc(tmpDF.to || '')}" onchange="_Pict.providers.Support.setDateFilter('from', this.value)" title="Designated date — from" />`
			+ `<span class="sup-date-sep">→</span>`
			+ `<input type="date" class="sup-date-in" value="${this._esc(tmpDF.to)}" min="${this._esc(tmpDF.from || '')}" onchange="_Pict.providers.Support.setDateFilter('to', this.value)" title="Designated date — to" /></span>`;
		const tmpCapNote = this._capped ? `<span class="sup-fb-cap" title="Showing the ${DOC_CAP} most recent — narrow with filters"><i class="fas fa-circle-info"></i> capped at ${DOC_CAP}</span>` : '';
		return `<span class="sup-fb-label"><i class="fas fa-filter"></i> Filter</span>${tmpDrops}${tmpDateRange}`
			+ ((tmpActive || tmpState.Filter || tmpDateActive) ? `<button class="sup-fb-clear" onclick="_Pict.providers.Support.clearFilters(); return false;"><i class="fas fa-xmark"></i> Clear</button>` : '')
			+ `${tmpCapNote}<span class="sup-fb-count">${this._visibleCalls().length} of ${tmpState.Calls.length}</span>`;
	}

	_facetDropdownHTML(pFacet)
	{
		const tmpSel = this.state.Filters[pFacet.key] || [];
		const tmpOpen = this.state.OpenDropdown === pFacet.key;
		const tmpBadge = tmpSel.length ? `<span class="sup-dd-badge">${tmpSel.length}</span>` : '';
		const tmpItems = pFacet.options().map((pOpt) =>
		{
			const tmpOn = tmpSel.indexOf(pOpt) >= 0;
			return `<div class="sup-dd-item ${tmpOn ? 'sup-dd-on' : ''}" onclick="_Pict.providers.Support.toggleFilter('${pFacet.key}','${this._esc(pOpt)}'); return false;">`
				+ `<i class="fas ${tmpOn ? 'fa-square-check' : 'fa-square'}"></i> ${this._esc(pFacet.labelFor(pOpt))}</div>`;
		}).join('');
		return `<div class="sup-dd ${tmpOpen ? 'sup-dd-open' : ''}">`
			+ `<button class="sup-dd-btn ${tmpSel.length ? 'sup-dd-active' : ''}" onclick="_Pict.providers.Support.toggleDropdown('${pFacet.key}'); return false;">${this._esc(pFacet.label)}${tmpBadge} <i class="fas fa-chevron-down"></i></button>`
			+ (tmpOpen ? `<div class="sup-dd-panel">${tmpItems}</div>` : '')
			+ `</div>`;
	}

	_segHTML()
	{
		if (!this.canManage()) { return ''; }   // tier-1 self-service users get the Calls view only (no Metrics)
		const tmpMetrics = this.state.View === 'metrics';
		return `<div class="sup-seg">`
			+ `<button class="sup-seg-b ${tmpMetrics ? '' : 'sup-seg-on'}" onclick="_Pict.providers.Support.navigateView('calls'); return false;"><i class="fas fa-list-ul"></i> Calls</button>`
			+ `<button class="sup-seg-b ${tmpMetrics ? 'sup-seg-on' : ''}" onclick="_Pict.providers.Support.navigateView('metrics'); return false;"><i class="fas fa-chart-column"></i> Metrics</button>`
			+ `</div>`;
	}

	_metricsHTML()
	{
		const tmpRange = this._metricsRangeControlHTML();   // always show the range selector, even while a range loads
		if (this._metricsLoading && !this._metrics) { return tmpRange + `<div class="sup-note"><i class="fas fa-spinner fa-spin"></i> Loading ${this._esc(this._metricsRangeBounds().label)}…</div>`; }
		const tmpM = this._metrics;
		if (!tmpM) { return tmpRange + `<div class="sup-note sup-note-empty">No metrics yet.</div>`; }
		if (tmpM.error) { return tmpRange + `<div class="sup-note sup-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpM.error)}</div>`; }
		const tmpRate = tmpM.total ? Math.round(100 * tmpM.resolved / tmpM.total) : 0;
		const tmpStats = `<div class="sup-kpis">`
			+ this._kpi(tmpM.total, 'Total calls', '')
			+ this._kpi(tmpM.backlog, 'Open backlog', '#3a44b8')
			+ this._kpi(tmpM.resolved, 'Resolved', '#1f7a44')
			+ this._kpi(tmpM.medianDays == null ? '—' : this._days(tmpM.medianDays), 'Median time to resolve', '')
			+ this._kpi(tmpRate + '%', 'Resolve rate', '')
			+ `</div>`;
		return tmpRange
			+ this._metricsFilterBarHTML()
			+ tmpStats
			+ `<div class="sup-mgrid">`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">By status</div>${this._byStatusHTML(tmpM.counts, tmpM.total)}</div>`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">By priority</div>${this._breakdownHTML(tmpM.byPrio)}</div>`
			+ `</div>`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">Opened vs resolved · ${this._esc(tmpM.rangeLabel || '')}`
			+ ` <span class="sup-mlegend"><span class="sup-mdot" style="background:#3a44b8"></span>opened <span class="sup-mdot" style="background:#1f7a44"></span>resolved</span></div>`
			+ `${this._openedResolvedChart(tmpM)}</div>`
			+ this._bugWatchHTML(tmpM.bug)
			+ `<div class="sup-mgrid">`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">By customer</div>${this._customerBreakdownHTML(tmpM.byCustomer)}</div>`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">By theme <span class="sup-mcard-sub">derived from subject</span></div>${this._breakdownHTML(tmpM.byTheme, (pK) => pK)}</div>`
			+ `</div>`
			+ `<div class="sup-mgrid">`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">By application</div>${this._breakdownHTML(tmpM.byApp, this._appLabel.bind(this))}</div>`
			+ `<div class="sup-mcard"><div class="sup-mcard-h">Resolved by</div>${this._agentBreakdownHTML(tmpM.byAgent)}</div>`
			+ `</div>`;
	}

	/** Coarse "what is this about" theme from a ticket's subject + issue text (legacy tickets carry no explicit Category). */
	_deriveTheme(pSummary, pDetails)
	{
		const tmpText = `${pSummary || ''} ${String(pDetails || '').slice(0, 400)}`;
		for (let i = 0; i < THEME_RULES.length; i++) { if (THEME_RULES[i].rx.test(tmpText)) { return THEME_RULES[i].key; } }
		return 'Other';
	}

	/** Top customers in scope by ticket volume, with their open backlog — dashboards straight off the Caller/Customer join. */
	_customerBreakdownHTML(pByCustomer)
	{
		const tmpRows = Object.keys(pByCustomer || {}).map((pK) => ({ id: pK, total: pByCustomer[pK].total, resolved: pByCustomer[pK].resolved, open: pByCustomer[pK].open }))
			.sort((pA, pB) => pB.total - pA.total).slice(0, 10);
		if (!tmpRows.length) { return `<div class="sup-mempty">No customer-matched calls in scope.</div>`; }
		const tmpMax = Math.max(1, ...tmpRows.map((pR) => pR.total));
		const tmpBody = tmpRows.map((pR) => `<tr onclick="_Pict.providers.Support.setMetricFilter('Customer','${this._esc(pR.id)}'); return false;" class="sup-dim-click" title="Filter dashboard to this customer">`
			+ `<td class="sup-dim-n">${this._esc(this._customerName(pR.id))}</td>`
			+ `<td class="sup-dim-bar"><div class="sup-dimbar"><span style="width:${Math.round(100 * pR.total / tmpMax)}%"></span></div></td>`
			+ `<td class="sup-dim-v">${pR.total}</td>`
			+ `<td class="sup-dim-v" style="color:${pR.open ? '#b4324b' : '#8894a4'}" title="open backlog">${pR.open || '—'}</td></tr>`).join('');
		return `<table class="sup-dimtable"><thead><tr><th>Customer</th><th></th><th>Calls</th><th>Open</th></tr></thead><tbody>${tmpBody}</tbody></table>`;
	}

	/** Dashboard filter bar: customer (super-user) · type (category/app) · support user (resolver) · customer user (opener). */
	/** Date-range scope selector — the years present in the archive plus rolling/all shortcuts and a custom From/To. */
	_metricsRangeControlHTML()
	{
		const tmpR = this.state.MetricsRange || {};
		const tmpCur = tmpR.preset || 'last12';
		const tmpNow = new Date();
		const tmpYears = []; for (let y = 2020; y <= tmpNow.getFullYear(); y++) { tmpYears.push(String(y)); }
		const tmpBtn = (pVal, pLabel) => `<button class="sup-rng-b ${tmpCur === pVal ? 'sup-rng-on' : ''}" onclick="_Pict.providers.Support.setMetricsRange('${pVal}'); return false;">${this._esc(pLabel)}</button>`;
		let tmpHTML = `<div class="sup-rng"><span class="sup-rng-l"><i class="fas fa-calendar-range"></i> Range</span>`
			+ tmpBtn('last12', 'Last 12 mo') + tmpYears.map((pY) => tmpBtn(pY, pY)).join('') + tmpBtn('all', 'All time')
			+ `<button class="sup-rng-b ${tmpCur === 'custom' ? 'sup-rng-on' : ''}" onclick="_Pict.providers.Support.toggleCustomRange(); return false;"><i class="fas fa-sliders"></i> Custom</button></div>`;
		if (tmpCur === 'custom' || this._showCustomRange)
		{
			const tmpYMD = (pD) => `${pD.getFullYear()}-${String(pD.getMonth() + 1).padStart(2, '0')}-${String(pD.getDate()).padStart(2, '0')}`;
			const tmpFrom = tmpR.from || `${tmpNow.getFullYear()}-01-01`;
			const tmpTo = tmpR.to || tmpYMD(tmpNow);
			tmpHTML += `<div class="sup-rng sup-rng-custom"><span class="sup-rng-l">From</span><input type="date" class="sup-rng-in" id="Support-Range-From" value="${this._esc(tmpFrom)}" max="${this._esc(tmpYMD(tmpNow))}">`
				+ `<span class="sup-rng-l">To</span><input type="date" class="sup-rng-in" id="Support-Range-To" value="${this._esc(tmpTo)}" max="${this._esc(tmpYMD(tmpNow))}">`
				+ `<button class="sup-rng-b sup-rng-apply" onclick="_Pict.providers.Support.applyCustomRange(); return false;"><i class="fas fa-check"></i> Apply</button></div>`;
		}
		return tmpHTML;
	}

	toggleCustomRange() { this._showCustomRange = !this._showCustomRange; this._paintMetrics(); }

	applyCustomRange()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpFromEl = tmpCA.getElement('#Support-Range-From'); const tmpToEl = tmpCA.getElement('#Support-Range-To');
		const tmpFrom = tmpFromEl && tmpFromEl[0] && tmpFromEl[0].value;
		const tmpTo = tmpToEl && tmpToEl[0] && tmpToEl[0].value;
		if (!tmpFrom || !tmpTo || tmpFrom > tmpTo) { this._toast('Pick a valid range — From must be on or before To.', 'error'); return; }
		this.state.MetricsRange = { preset: 'custom', from: tmpFrom, to: tmpTo };
		this._showCustomRange = true;
		this._metrics = null;
		this._loadMetricsAsync();
		this._paintMetrics();
	}

	_metricsFilterBarHTML()
	{
		const tmpF = this.state.MetricsFilters || {};
		const tmpCalls = this._metricsCalls || [];
		const tmpSelect = (pDim, pLabel, pOptions) =>
		{
			if (!pOptions.length) { return ''; }
			const tmpOpts = `<option value="">All</option>` + pOptions.map((pO) => `<option value="${this._esc(pO.value)}" ${tmpF[pDim] === pO.value ? 'selected' : ''}>${this._esc(pO.label)}</option>`).join('');
			return `<label class="sup-mf"><span class="sup-mf-l">${this._esc(pLabel)}</span><select class="sup-mf-sel ${tmpF[pDim] ? 'sup-mf-on' : ''}" onchange="_Pict.providers.Support.setMetricFilter('${pDim}', this.value)">${tmpOpts}</select></label>`;
		};
		// Option lists built from what's present in the loaded data (so no empty filters).
		const tmpUniq = (pFn) => { const tmpSet = {}; tmpCalls.forEach((pC) => { const tmpV = pFn(pC); if (tmpV) { tmpSet[tmpV] = true; } }); return Object.keys(tmpSet); };
		// Customer is a searchable pict-section-picker (mounted after paint into #Support-Metric-Cust-Pick), scoped to
		// the customers that actually have calls in the loaded range; only shown for super-users with >0 such customers.
		const tmpCustIDs = this._isSuperUser() ? tmpUniq((pC) => pC.CallerCustomer && String(pC.CallerCustomer)) : [];
		const tmpCustSel = tmpCustIDs.length
			? `<label class="sup-mf"><span class="sup-mf-l">Customer</span><span id="Support-Metric-Cust-Pick" class="sup-mf-pick ${tmpF.Customer ? 'sup-mf-on' : ''}"></span></label>`
			: '';
		const tmpCatSel = tmpSelect('Category', 'Category', tmpUniq((pC) => pC.Category).map((pV) => ({ value: pV, label: this._catLabel(pV) })));
		const tmpAppSel = tmpSelect('App', 'Application', tmpUniq((pC) => pC.App).map((pV) => ({ value: pV, label: this._appLabel(pV) })));
		const tmpResolvers = Object.keys(this._resolveMap || {}).map((pK) => this._resolveMap[pK].resolver).filter(Boolean);
		const tmpResSel = tmpSelect('Resolver', 'Support user', [ ...new Set(tmpResolvers) ].map((pV) => ({ value: String(pV), label: this._agentNames[pV] || ('User ' + pV) })).sort((pA, pB) => pA.label.localeCompare(pB.label)));
		const tmpOpenSel = tmpSelect('Opener', 'Customer user', tmpUniq((pC) => pC.OpenedBy && String(pC.OpenedBy)).map((pV) => ({ value: pV, label: this._agentNames[pV] || ('User ' + pV) })).sort((pA, pB) => pA.label.localeCompare(pB.label)));
		const tmpActive = Object.keys(tmpF).some((pK) => tmpF[pK]);
		const tmpClear = tmpActive ? `<button class="sup-fb-clear" onclick="_Pict.providers.Support.clearMetricFilters(); return false;"><i class="fas fa-xmark"></i> Clear</button>` : '';
		return `<div class="sup-mfilter"><span class="sup-fb-label"><i class="fas fa-filter"></i> Dashboard</span>${tmpCustSel}${tmpCatSel}${tmpAppSel}${tmpResSel}${tmpOpenSel}${tmpClear}</div>`;
	}

	/** Horizontal proportion bar of the backlog by status. */
	_byStatusHTML(pCounts, pTotal)
	{
		if (!pTotal) { return `<div class="sup-mempty">No calls in scope.</div>`; }
		const tmpBar = Object.keys(STATUS_ORDER).map((pS) =>
		{
			const tmpN = pCounts[pS] || 0; if (!tmpN) { return ''; }
			const tmpMeta = this._statusMeta(pS);
			return `<span class="sup-stbar-seg ${tmpMeta.cls}" style="width:${100 * tmpN / pTotal}%" title="${tmpMeta.label}: ${tmpN}"></span>`;
		}).join('');
		const tmpLegend = Object.keys(STATUS_ORDER).map((pS) =>
		{
			const tmpN = pCounts[pS] || 0; const tmpMeta = this._statusMeta(pS);
			return `<div class="sup-stleg"><span class="sup-badge ${tmpMeta.cls}"><i class="fas ${tmpMeta.icon}"></i> ${tmpMeta.label}</span><span class="sup-stleg-n">${tmpN}</span><span class="sup-stleg-p">${pTotal ? Math.round(100 * tmpN / pTotal) : 0}%</span></div>`;
		}).join('');
		return `<div class="sup-stbar">${tmpBar}</div><div class="sup-stlegwrap">${tmpLegend}</div>`;
	}

	/** "Something isn't working" watch — bug volume, backlog, cycle time + a by-application drill-in. */
	_bugWatchHTML(pBug)
	{
		if (!pBug || !pBug.total)
		{
			return `<div class="sup-mcard sup-bugcard"><div class="sup-mcard-h"><i class="fas fa-bug"></i> Something isn’t working</div>`
				+ `<div class="sup-mempty">No “Something isn’t working” calls in scope.</div></div>`;
		}
		const tmpMini = pBug.opened.map((pO, i) =>
		{
			const tmpMax = Math.max(1, ...pBug.opened, ...pBug.resolved);
			const tmpR = pBug.resolved[i] || 0; const tmpMo = this._metrics.months[i];
			return `<div class="sup-bar" title="${MON_SHORT[tmpMo.m]} ${tmpMo.y}: ${pO} opened, ${tmpR} resolved"><div class="sup-bar-pair">`
				+ `<span class="sup-bar-c" style="height:${Math.round(100 * pO / tmpMax)}%;background:#b4324b"><span class="sup-bar-n">${pO || ''}</span></span>`
				+ `<span class="sup-bar-c" style="height:${Math.round(100 * tmpR / tmpMax)}%;background:#1f7a44"><span class="sup-bar-n">${tmpR || ''}</span></span>`
				+ `</div><span class="sup-bar-x">${MON_SHORT[tmpMo.m]}</span></div>`;
		}).join('');
		const tmpFocused = this.state.MetricsFilters && this.state.MetricsFilters.Category === 'Bug';
		const tmpFocusBtn = tmpFocused
			? `<button class="sup-bug-focus sup-bug-focus-on" onclick="_Pict.providers.Support.setMetricFilter('Category',''); return false;"><i class="fas fa-xmark"></i> Clear bug focus</button>`
			: `<button class="sup-bug-focus" onclick="_Pict.providers.Support.setMetricFilter('Category','Bug'); return false;"><i class="fas fa-magnifying-glass"></i> Focus dashboard on bugs</button>`;
		return `<div class="sup-mcard sup-bugcard"><div class="sup-mcard-h"><i class="fas fa-bug"></i> Something isn’t working${tmpFocusBtn}</div>`
			+ `<div class="sup-bug-grid">`
			+ `<div class="sup-bug-kpis">`
			+ this._kpi(pBug.total, 'Bug calls', '#b4324b')
			+ this._kpi(pBug.backlog, 'Bug backlog', '#b4324b')
			+ this._kpi(pBug.medianDays == null ? '—' : this._days(pBug.medianDays), 'Median fix time', '')
			+ `</div>`
			+ `<div class="sup-bug-trend"><div class="sup-mcard-h">Bug trend · ${this._trailingMonths}mo <span class="sup-mlegend"><span class="sup-mdot" style="background:#b4324b"></span>opened <span class="sup-mdot" style="background:#1f7a44"></span>resolved</span></div><div class="sup-chart sup-chart-sm">${tmpMini}</div></div>`
			+ `</div>`
			+ `<div class="sup-mcard-h" style="margin-top:0.6rem">By application</div>${this._breakdownHTML(pBug.byApp, this._appLabel.bind(this))}</div>`;
	}

	_kpi(pValue, pLabel, pColor)
	{
		return `<div class="sup-kpi"><div class="sup-kpi-v"${pColor ? ` style="color:${pColor}"` : ''}>`
			+ `${typeof pValue === 'number' ? pValue.toLocaleString() : this._esc(pValue)}</div>`
			+ `<div class="sup-kpi-l">${this._esc(pLabel)}</div></div>`;
	}

	_openedResolvedChart(pM)
	{
		let tmpMax = 1;
		pM.months.forEach((pMo, i) => { tmpMax = Math.max(tmpMax, pM.opened[i] || 0, pM.resolvedSeries[i] || 0); });
		if (!pM.opened.some(Boolean) && !pM.resolvedSeries.some(Boolean)) { return `<div class="sup-mempty">No calls in this window.</div>`; }
		// Over a wide (multi-year) span, per-month labels get illegible — label only the January bars with the year
		// ('20, '21, …) as year markers; the hover title still names the exact month. Bar counts are hidden when dense.
		const tmpWide = pM.months.length > 14;
		const tmpBars = pM.months.map((pMo, i) =>
		{
			const tmpO = pM.opened[i] || 0; const tmpR = pM.resolvedSeries[i] || 0;
			const tmpLabel = tmpWide ? (pMo.m === 0 ? `’${String(pMo.y).slice(2)}` : '') : MON_SHORT[pMo.m];
			const tmpShowN = !tmpWide;
			return `<div class="sup-bar${tmpWide ? ' sup-bar-dense' : ''}" title="${MON_SHORT[pMo.m]} ${pMo.y}: ${tmpO} opened, ${tmpR} resolved"><div class="sup-bar-pair">`
				+ `<span class="sup-bar-c" style="height:${Math.round(100 * tmpO / tmpMax)}%;background:#3a44b8">${tmpShowN ? `<span class="sup-bar-n">${tmpO || ''}</span>` : ''}</span>`
				+ `<span class="sup-bar-c" style="height:${Math.round(100 * tmpR / tmpMax)}%;background:#1f7a44">${tmpShowN ? `<span class="sup-bar-n">${tmpR || ''}</span>` : ''}</span>`
				+ `</div><span class="sup-bar-x${pMo.m === 0 && tmpWide ? ' sup-bar-x-year' : ''}">${tmpLabel}</span></div>`;
		}).join('');
		return `<div class="sup-chart${tmpWide ? ' sup-chart-wide' : ''}">${tmpBars}</div>`;
	}

	_breakdownHTML(pGroups, pLabelFn)
	{
		const tmpLabel = pLabelFn || ((pK) => this._catLabel(pK));
		const tmpRows = Object.keys(pGroups).map((pK) => ({ key: pK, total: pGroups[pK].total, resolved: pGroups[pK].resolved })).sort((pA, pB) => pB.total - pA.total).slice(0, 10);
		if (!tmpRows.length) { return `<div class="sup-mempty">No data.</div>`; }
		const tmpMax = Math.max(1, ...tmpRows.map((pR) => pR.total));
		const tmpBody = tmpRows.map((pR) => `<tr><td class="sup-dim-n">${this._esc(tmpLabel(pR.key))}</td>`
			+ `<td class="sup-dim-bar"><div class="sup-dimbar"><span style="width:${Math.round(100 * pR.total / tmpMax)}%"></span></div></td>`
			+ `<td class="sup-dim-v">${pR.total}</td><td class="sup-dim-v" style="color:#1f7a44">${pR.resolved}</td></tr>`).join('');
		return `<table class="sup-dimtable"><thead><tr><th>Group</th><th></th><th>Calls</th><th>Res.</th></tr></thead><tbody>${tmpBody}</tbody></table>`;
	}

	_agentBreakdownHTML(pAgents)
	{
		const tmpRows = Object.keys(pAgents).map((pK) => ({ key: Number(pK), resolved: pAgents[pK].resolved })).sort((pA, pB) => pB.resolved - pA.resolved).slice(0, 10);
		if (!tmpRows.length) { return `<div class="sup-mempty">No resolutions yet.</div>`; }
		const tmpMax = Math.max(1, ...tmpRows.map((pR) => pR.resolved));
		const tmpBody = tmpRows.map((pR) => `<tr><td class="sup-dim-n">${this._esc(this._agentNames[pR.key] || ('User ' + pR.key))}</td>`
			+ `<td class="sup-dim-bar"><div class="sup-dimbar"><span style="width:${Math.round(100 * pR.resolved / tmpMax)}%;background:#1f7a44"></span></div></td>`
			+ `<td class="sup-dim-v">${pR.resolved}</td></tr>`).join('');
		return `<table class="sup-dimtable"><thead><tr><th>Agent</th><th></th><th>Res.</th></tr></thead><tbody>${tmpBody}</tbody></table>`;
	}

	_listRowsHTML()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return `<div class="sup-note"><i class="fas fa-spinner fa-spin"></i> Loading support calls…</div>`; }
		const tmpHead = this._tableHeadHTML();
		const tmpAll = this._visibleCalls();
		if (!tmpAll.length)
		{
			const tmpEmpty = tmpState.Calls.length ? 'No calls match these filters.' : 'No support calls yet. Start one with “New support call”.';
			return `<table class="sup-table">${tmpHead}<tbody><tr><td class="sup-empty-cell" colspan="${this._rowColSpan() + (this.canManage() ? 1 : 0)}"><i class="fas fa-inbox"></i> ${tmpEmpty}</td></tr></tbody></table>`;
		}
		// Client-side pagination of the already-loaded + faceted set (we page the rendered rows, not the fetch). Clamp so a
		// stale page left over from a wider filter can't blank the list after the set narrows.
		const tmpPageCount = Math.max(1, Math.ceil(tmpAll.length / LIST_PAGE_SIZE));
		const tmpPage = Math.min(Math.max(0, Number(tmpState.ListPage) || 0), tmpPageCount - 1);
		const tmpRows = tmpAll.slice(tmpPage * LIST_PAGE_SIZE, (tmpPage + 1) * LIST_PAGE_SIZE);
		// A pager at BOTH ends — the top one is visible immediately (the list is long: 25 two-row call groups), the bottom
		// one is there after you scroll. Each call renders as its OWN <tbody> (a metadata + a full-width summary row).
		const tmpTopPager = this._listPagerHTML(tmpAll.length, tmpPage, tmpPageCount, 'top');
		const tmpBottomPager = this._listPagerHTML(tmpAll.length, tmpPage, tmpPageCount, 'bottom');
		return `${tmpTopPager}<table class="sup-table">${tmpHead}${tmpRows.map((pCall) => this._callRowHTML(pCall)).join('')}</table>${tmpBottomPager}`;
	}

	/** Prev/Next pager + "Showing X–Y of N" for the triage list; hidden when everything fits on one page. pPos = 'top'|'bottom'. */
	_listPagerHTML(pTotal, pPage, pPageCount, pPos)
	{
		if (pTotal <= LIST_PAGE_SIZE) { return ''; }
		const tmpStart = pPage * LIST_PAGE_SIZE + 1;
		const tmpEnd = Math.min(pTotal, (pPage + 1) * LIST_PAGE_SIZE);
		return `<div class="sup-pager${pPos === 'top' ? ' sup-pager-top' : ''}">`
			+ `<button class="sup-pg" ${pPage <= 0 ? 'disabled' : ''} onclick="_Pict.providers.Support.setListPage(${pPage - 1}); return false;"><i class="fas fa-chevron-left"></i> Prev</button>`
			+ `<span class="sup-pg-info">Showing <b>${tmpStart.toLocaleString()}</b>–<b>${tmpEnd.toLocaleString()}</b> of <b>${pTotal.toLocaleString()}</b> · page ${pPage + 1} of ${pPageCount}</span>`
			+ `<button class="sup-pg" ${pPage >= pPageCount - 1 ? 'disabled' : ''} onclick="_Pict.providers.Support.setListPage(${pPage + 1}); return false;">Next <i class="fas fa-chevron-right"></i></button>`
			+ `</div>`;
	}

	_tableHeadHTML()
	{
		const tmpSort = this.state.Sort;
		const tmpTh = (pKey, pLabel, pClass) =>
		{
			const tmpIdx = tmpSort.findIndex((pS) => pS.key === pKey);
			let tmpCaret = '';
			if (tmpIdx >= 0)
			{
				tmpCaret = ` <i class="fas fa-caret-${tmpSort[tmpIdx].dir === 'asc' ? 'up' : 'down'} sup-th-caret"></i>`
					+ (tmpSort.length > 1 ? `<span class="sup-th-ord">${tmpIdx + 1}</span>` : '');
			}
			return `<th class="sup-th ${pClass || ''} ${tmpIdx >= 0 ? 'sup-th-sorted' : ''}" title="Click to sort · Shift-click to add a level" onclick="_Pict.providers.Support.setSort('${pKey}', event.shiftKey); return false;">${this._esc(pLabel)}${tmpCaret}</th>`;
		};
		const tmpSelCount = this._selectedIDs().length;
		const tmpSelTh = this.canManage()
			? `<th class="sup-th sup-col-sel" title="Select tickets to merge"><input type="checkbox" class="sup-cb" ${tmpSelCount ? 'checked' : ''} onclick="_Pict.providers.Support.toggleSelectAllVisible(this.checked); return false;" /></th>`
			: '';
		return `<thead><tr>`
			+ tmpSelTh
			+ tmpTh('Status', 'Status', 'sup-col-status')
			+ (this._isSuperUser() ? tmpTh('Customer', 'Customer', 'sup-col-cust') : '')
			+ tmpTh('Priority', 'Priority', 'sup-col-prio')
			+ tmpTh('Category', 'Category', 'sup-col-cat')
			+ tmpTh('App', 'App', 'sup-col-app')
			+ tmpTh('Caller', 'Caller', 'sup-col-caller')
			+ tmpTh('Updated', 'Updated', 'sup-col-date')
			+ tmpTh('Designated', 'Designated', 'sup-col-date')
			+ tmpTh('UpdatedBy', 'Updated By', 'sup-col-updby')
			+ `<th class="sup-th sup-col-fu" title="Follow-up needed"><i class="fas fa-flag"></i></th>`
			+ `<th class="sup-th sup-col-go"></th>`
			+ `</tr></thead>`;
	}

	/** Number of columns in a call's top (metadata) row — the summary row spans all of them. */
	_rowColSpan() { return this._isSuperUser() ? 11 : 10; }

	/**
	 * One call = a two-row group: a compact metadata row (status/customer/priority/category/app/caller/updated) and a
	 * full-width summary line underneath, so the metadata columns aren't cramped by a wide summary column.
	 */
	_callRowHTML(pCall)
	{
		const tmpStatus = this._statusMeta(pCall.Status);
		const tmpPrio = this._prioMeta(pCall.Priority);
		const tmpMuted = '<span class="sup-muted">—</span>';
		const tmpCust = this._isSuperUser() ? `<td class="sup-col-cust"><span class="sup-cust">${pCall.CallerCustomer ? this._esc(this._customerName(pCall.CallerCustomer)) : tmpMuted}</span></td>` : '';
		const tmpStage = (pCall.Stage && IMPL_STAGE_MAP[pCall.Stage]) ? ` <span class="sup-stg-chip ${IMPL_STAGE_MAP[pCall.Stage].cls}">${this._esc(IMPL_STAGE_MAP[pCall.Stage].label)}</span>` : '';
		const tmpSelCell = this.canManage()
			? `<td class="sup-col-sel" rowspan="2" onclick="event.stopPropagation();"><input type="checkbox" class="sup-cb" ${this._isSelected(pCall.IDDocument) ? 'checked' : ''} title="Select to merge" onclick="event.stopPropagation(); _Pict.providers.Support.toggleSelect(${pCall.IDDocument}, this.checked);" /></td>`
			: '';
		return `<tbody class="sup-callgroup${this._isSelected(pCall.IDDocument) ? ' sup-callgroup-sel' : ''}" onclick="_Pict.providers.Support.navigateCall(${pCall.IDDocument}); return false;">`
			+ `<tr class="sup-tr sup-tr-meta">`
			+ tmpSelCell
			+ `<td class="sup-col-status"><span class="sup-badge ${tmpStatus.cls}"><i class="fas ${tmpStatus.icon}"></i> ${tmpStatus.label}</span></td>`
			+ tmpCust
			+ `<td class="sup-col-prio">${pCall.Priority ? `<span class="sup-prio ${tmpPrio.cls}">${this._esc(tmpPrio.label)}</span>` : tmpMuted}</td>`
			+ `<td class="sup-col-cat">${pCall.Category ? `<span class="sup-tag">${this._esc(this._catLabelList(pCall.Category))}</span>` : tmpMuted}</td>`
			+ `<td class="sup-col-app">${pCall.App ? `<span class="sup-tag sup-tag-app">${this._esc(this._appLabel(pCall.App))}</span>` : tmpMuted}</td>`
			+ `<td class="sup-col-caller">${pCall.Caller ? this._esc(pCall.Caller) : tmpMuted}</td>`
			+ `<td class="sup-col-date">${this._date(pCall.Updated)}</td>`
			+ `<td class="sup-col-date" title="Designated date (email timestamp)">${this._date(pCall.Designated)}</td>`
			+ `<td class="sup-col-updby" title="Last team member to action this call">${pCall.UpdatedBy ? this._esc(this._agentNames[pCall.UpdatedBy] || ('User ' + pCall.UpdatedBy)) : tmpMuted}</td>`
			+ `<td class="sup-col-fu">${pCall.FollowUp ? '<i class="fas fa-flag sup-fu-on" title="Follow-up needed"></i>' : ''}</td>`
			+ `<td class="sup-col-go"><i class="fas fa-chevron-right"></i></td>`
			+ `</tr>`
			+ `<tr class="sup-tr sup-tr-sum">`
			+ `<td class="sup-sumcell" colspan="${this._rowColSpan()}"><span class="sup-summary-txt">${this._esc(pCall.Summary)}</span>`
			+ `<span class="sup-row-id">#${pCall.IDDocument}${tmpStage}</span></td>`
			+ `</tr>`
			+ `</tbody>`;
	}

	// ── Bulk-merge selection (manager-only) ──────────────────────────────────────────────────────────────────────

	_isSelected(pID) { const tmpSel = this.state.Selected || {}; return !!tmpSel[Number(pID)]; }
	_selectedIDs() { const tmpSel = this.state.Selected || {}; return Object.keys(tmpSel).filter((pK) => tmpSel[pK]).map((pK) => Number(pK)); }

	/** The IDDocuments on the current (client-paged) list page — the scope of "select all". */
	_visiblePageIDs()
	{
		const tmpAll = this._visibleCalls();
		const tmpPageCount = Math.max(1, Math.ceil(tmpAll.length / LIST_PAGE_SIZE));
		const tmpPage = Math.min(Math.max(0, Number(this.state.ListPage) || 0), tmpPageCount - 1);
		return tmpAll.slice(tmpPage * LIST_PAGE_SIZE, (tmpPage + 1) * LIST_PAGE_SIZE).map((pCall) => Number(pCall.IDDocument));
	}

	toggleSelect(pID, pChecked)
	{
		if (!this.state.Selected) { this.state.Selected = {}; }
		if (pChecked) { this.state.Selected[Number(pID)] = true; } else { delete this.state.Selected[Number(pID)]; }
		// Repaint the list too (not just the bar): refreshes the row highlight + the header select-all box, and re-syncs
		// the rendered checkbox if a concurrent repaint (e.g. closing a facet dropdown) re-rendered the row from old state.
		this._paintList();
		this._paintMergeBar();
	}

	toggleSelectAllVisible(pChecked)
	{
		if (!this.state.Selected) { this.state.Selected = {}; }
		this._visiblePageIDs().forEach((pID) => { if (pChecked) { this.state.Selected[pID] = true; } else { delete this.state.Selected[pID]; } });
		this._paintList();
		this._paintMergeBar();
	}

	clearSelection() { this.state.Selected = {}; this._paintList(); this._paintMergeBar(); }

	_paintList() { const tmpCA = this.pict.ContentAssignment; if (tmpCA && tmpCA.getElement('#Support-List').length) { tmpCA.assignContent('#Support-List', this._listRowsHTML()); } }
	_paintMergeBar() { const tmpCA = this.pict.ContentAssignment; if (tmpCA && tmpCA.getElement('#Support-MergeBar').length) { tmpCA.assignContent('#Support-MergeBar', this._mergeBarHTML()); } }

	/** The sticky selection bar shown above the list when one or more tickets are ticked. */
	_mergeBarHTML()
	{
		const tmpN = this._selectedIDs().length;
		if (!tmpN) { return ''; }
		return `<div class="sup-mergebar">`
			+ `<span class="sup-mergebar-n"><i class="fas fa-check-double"></i> ${tmpN} selected</span>`
			+ `<button class="sup-btn sup-btn-sm" onclick="_Pict.providers.Support.clearSelection(); return false;"><i class="fas fa-xmark"></i> Clear</button>`
			+ `<button class="sup-btn sup-btn-primary sup-mergebar-go" onclick="_Pict.providers.Support.openMergeSelected(); return false;"><i class="fas fa-code-merge"></i> Merge ${tmpN} into…</button>`
			+ `</div>`;
	}

	/** Launch the merge wizard for the ticked tickets (bulk). Routes to #/Support/Merge/<ids> so the wizard is deep-linkable. */
	openMergeSelected()
	{
		const tmpIDs = this._selectedIDs();
		if (!tmpIDs.length) { return; }
		this.navigateMerge(tmpIDs);
	}

	/** Launch the merge wizard for the single call currently open in the detail view. */
	openMergeCurrent()
	{
		const tmpActive = this.state.Active;
		if (!tmpActive || !tmpActive.IDDocument) { return; }
		// Coming from an open call with an unsaved Call-activity note: confirm BEFORE we route away (the route change tears
		// the form down). The guard lives here, not in enterMerge, because the router drives enterMerge AFTER the hash has
		// already changed — declining there would strand the URL on the merge route. Mirrors cancel() + the beforeunload guard.
		if (this.state.Mode === 'form' && this._hasUnsavedComposer())
		{
			const tmpOk = (typeof window !== 'undefined' && typeof window.confirm === 'function')
				? window.confirm('You have an unsaved note in Call activity. Leave without adding it?\n\n(Click Cancel to stay and add it.)')
				: true;
			if (!tmpOk) { this._scrollToComposer(); return; }
		}
		this.navigateMerge([ Number(tmpActive.IDDocument) ]);
	}

	// ── Merge wizard — a deep-linkable view (#/Support/Merge/<id,id,…>) ──────────────────────────────────────────

	/** Write the hash for the merge wizard so it is bookmarkable + reload-safe; the router applies it via routeApplyMerge.
	 *  No window (tests/SSR) → apply directly. */
	navigateMerge(pIDs)
	{
		const tmpIDs = (Array.isArray(pIDs) ? pIDs : [ pIDs ]).map((pX) => Number(pX)).filter(Boolean);
		if (!tmpIDs.length) { return; }
		if ((typeof window === 'undefined') || !window.location) { this.routeApplyMerge(tmpIDs); return; }
		window.location.hash = '#/Support/Merge/' + tmpIDs.join(',');
	}

	/** Router entry: open the merge wizard for the children named in the route. Parses the id list (tolerant of any
	 *  non-digit separator / url-encoding), and bounces a non-manager / docs-only user or an empty list to the queue. */
	routeApplyMerge(pIDs)
	{
		if (this._isDocsOnly()) { this.navigateView('docs'); return; }
		if (!this.canManage()) { this.navigateView('calls'); return; }
		const tmpRaw = (pIDs == null) ? '' : (Array.isArray(pIDs) ? pIDs.join(',') : String(pIDs));
		const tmpIDs = [ ...new Set(tmpRaw.split(/[^0-9]+/).map((pX) => Number(pX)).filter(Boolean)) ];
		if (!tmpIDs.length) { this.navigateView('calls'); return; }
		this.enterMerge(tmpIDs);
	}

	/** Enter the inline merge-wizard view (Mode 'merge') for the given child tickets. It is a VIEW, not a modal: we paint
	 *  the shell (so #SupportMerge-Wizard exists), then hand off to SupportMerge to build its accordion into that container.
	 *  Reached via the router (routeApplyMerge); the unsaved-composer confirm already ran in openMergeCurrent. Leave via exitMerge. */
	enterMerge(pIDs)
	{
		const tmpMerge = this.pict.providers.SupportMerge;
		if (!tmpMerge || (typeof tmpMerge.open !== 'function')) { this._toast('The merge wizard is unavailable.', 'error'); return; }
		if (!this.canManage()) { this._toast('You do not have permission to merge tickets.', 'error'); return; }
		const tmpIDs = (Array.isArray(pIDs) ? pIDs : [ pIDs ]).map((pX) => Number(pX)).filter(Boolean);
		if (!tmpIDs.length) { return; }
		// Coming from an open call (Mode 'form'): tear down the mounted form view so it doesn't leak (mirrors routeApply).
		if (this.state.Mode === 'form' && this.formHost && (typeof this.formHost.destroy === 'function')) { try { this.formHost.destroy(); } catch (pErr) { /* best-effort */ } }
		this.state.Mode = 'merge';
		this._paint();                 // paints _mergeViewHTML → #SupportMerge-Wizard now exists in the DOM
		tmpMerge.open(tmpIDs);         // builds the accordion into it (async child resolve; the container is already present)
	}

	/** Leave the merge view for the triage list via the hash (router reloads it, dropping any just-merged tickets). Mode
	 *  stays 'merge' so routeApply('calls') takes its merge branch (teardown + loadAsync). Mirrors cancel()'s exit. */
	exitMerge()
	{
		const tmpMerge = this.pict.providers.SupportMerge;
		if (tmpMerge && (typeof tmpMerge.teardown === 'function')) { tmpMerge.teardown(); }
		this.navigateView('calls');
	}

	_formHTML()
	{
		const tmpState = this.state;
		const tmpError = tmpState.Error ? `<div class="sup-note sup-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Error)}</div>` : '';
		// Everything actionable — Save (+ ⌘/Ctrl-S), the status/approval + delivery actions, and the runbook/content
		// library — lives in a width-resizable, top-sticky side panel that travels with you as you scroll, so you never
		// scroll to the top to save. The form + activity (the left column) scroll the page normally.
		this._ensureFormKeyHandlers();
		return `<div class="sup-form-head">`
			+ `<div class="sup-form-head-left"><a class="sup-back" onclick="_Pict.providers.Support.cancel(); return false;"><i class="fas fa-arrow-left"></i> All calls</a>`
			+ `<h2 class="sup-form-title">${this._esc(tmpState.FormTitle || 'Support call')}</h2></div></div>`
			+ tmpError
			+ `<div class="sup-work" style="--sup-panel-w:${this._panelWidthCss()};">`
			+   `<div class="sup-work-main">`
			+     `<div id="Support-FormHost" class="sup-form-host"></div>`
			+     (tmpState.FormMode === 'create' ? '' : `<div id="Support-Activity" class="sup-activity">${this._activityHTML()}</div>`)
			+   `</div>`
			+   `<aside class="sup-panel">`
			+     `<div class="sup-panel-grip" title="Drag to resize" onmousedown="_Pict.providers.Support.startPanelResize(event); return false;"><span class="sup-panel-grip-bar"></span></div>`
			+     `<div class="sup-panel-inner">`
			+       `<div id="Support-Form-Actions" class="sup-panel-actions">${this._formActionsHTML()}</div>`
			+       `<div id="Support-Approval">${this._approvalHTML()}</div>`
			+       (tmpState.FormMode === 'create' ? '' : `<div id="Support-Impl">${this._implBarHTML()}</div>`)
			+       (this.canManage() ? `<div id="Support-Runbook" class="sup-runbook${this.state.RunbookOpen === false ? ' sup-runbook-collapsed' : ''}">${this._runbookHTML()}</div>` : '')
			+     `</div>`
			+   `</aside>`
			+ `</div>`;
	}

	/** The persisted side-panel width as a CSS length (px), or the default. */
	_panelWidthCss() { const tmpW = Number(this.state.PanelWidth) || 0; return (tmpW >= 260 && tmpW <= 680) ? `${tmpW}px` : '22rem'; }

	/** Drag-resize the action panel (grip on its left edge; dragging left widens it). Persisted per-user. */
	startPanelResize(pEvent)
	{
		if (typeof document === 'undefined') { return; }
		const tmpCA = this.pict.ContentAssignment;
		const tmpWorkEls = tmpCA.getElement('.sup-work');
		if (!tmpWorkEls || !tmpWorkEls.length) { return; }
		const tmpWork = tmpWorkEls[0];
		const tmpPanel = tmpWork.querySelector('.sup-panel');
		const tmpStartX = pEvent.clientX;
		const tmpStartW = tmpPanel ? tmpPanel.getBoundingClientRect().width : 352;
		const tmpMove = (pMove) =>
		{
			const tmpW = Math.max(260, Math.min(680, Math.round(tmpStartW + (tmpStartX - pMove.clientX))));
			tmpWork.style.setProperty('--sup-panel-w', `${tmpW}px`);
			this.state.PanelWidth = tmpW;
		};
		const tmpUp = () =>
		{
			document.removeEventListener('mousemove', tmpMove);
			document.removeEventListener('mouseup', tmpUp);
			try { document.body.style.userSelect = ''; } catch (pErr) { /* */ }
			this._persistFilters();   // the prefs blob carries PanelWidth too
		};
		try { document.body.style.userSelect = 'none'; } catch (pErr) { /* */ }
		document.addEventListener('mousemove', tmpMove);
		document.addEventListener('mouseup', tmpUp);
	}

	/** Attach the one-time form-mode key handlers: ⌘/Ctrl-S saves without bouncing; beforeunload warns on an unsaved note. */
	_ensureFormKeyHandlers()
	{
		if (this._formKeyHandlersAttached || typeof document === 'undefined') { return; }
		this._formKeyHandlersAttached = true;
		document.addEventListener('keydown', (pEvent) =>
		{
			if ((pEvent.key === 's' || pEvent.key === 'S') && (pEvent.metaKey || pEvent.ctrlKey) && !pEvent.shiftKey && !pEvent.altKey
				&& this._isFormLive() && !this.state.Saving)
			{
				pEvent.preventDefault();
				this.save();
			}
		});
		if (typeof window !== 'undefined')
		{
			window.addEventListener('beforeunload', (pEvent) =>
			{
				if (this.state.Mode === 'form' && this._hasUnsavedComposer()) { pEvent.preventDefault(); pEvent.returnValue = ''; return ''; }
			});
		}
	}

	/** The support CALL FORM is the live, mounted view right now (Mode='form' is a stale AppData flag once you leave via
	 *  top-nav, which runs no Support teardown — so also require the form's own container to still be in the DOM). */
	_isFormLive() { return this.state.Mode === 'form' && !!this.state.Active && this.pict.ContentAssignment.getElement('#Support-Host').length > 0; }

	/** True when there's an in-progress call-activity note the user hasn't added yet (so leaving would lose it). */
	_hasUnsavedComposer()
	{
		if (!this._isFormLive()) { return false; }
		try { this._captureComposerEditor(); } catch (pError) { /* editor may be gone */ }
		const tmpText = String((this._composer && this._composer.text) || '').trim();
		const tmpFU = this._composer && this._composer.kind === 'FollowUp' && (this._composer.needBy || this._composer.waitingOn);
		return !!(tmpText || tmpFU);
	}

	/** Scroll the call-activity composer into view (when the user chooses to stay and finish a note). */
	_scrollToComposer()
	{
		const tmpEl = (typeof document !== 'undefined') && document.getElementById('Support-Composer');
		if (tmpEl && tmpEl.scrollIntoView) { try { tmpEl.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (pError) { /* */ } }
	}

	_formActionsHTML()
	{
		const tmpState = this.state;
		const tmpSaving = tmpState.Saving;
		return `<button class="sup-btn sup-btn-primary" ${tmpSaving ? 'disabled' : ''} onclick="_Pict.providers.Support.save(); return false;">`
			+ `${tmpSaving ? '<i class="fas fa-spinner fa-spin"></i> Saving…' : '<i class="fas fa-floppy-disk"></i> Save'}</button>`
			+ `<button class="sup-btn" onclick="_Pict.providers.Support.cancel(); return false;">Close</button>`;
	}

	_approvalHTML()
	{
		const tmpState = this.state;
		const tmpApproval = tmpState.Approval;
		const tmpStatus = (tmpApproval && tmpApproval.Status) || (tmpState.Active && tmpState.Active.Status) || ENTRY_STATUS;
		const tmpMeta = this._statusMeta(tmpStatus);
		let tmpActions = '';
		if (tmpApproval && tmpApproval.Actions && tmpApproval.Actions.length)
		{
			tmpActions = tmpApproval.Actions
				// Void (mark-as-spam) is a manager-only terminal action in the UI. The server workflow also gates it to
				// the Support lead / Admin, but the action bar otherwise renders every verb the server offers, so hide it
				// client-side from non-managers. (The seeded SupportCallWorkflow only ever offers it to the gated actors.)
				.filter((pAction) => (pAction !== 'Void') || this.canManage())
				.map((pAction) =>
				{
					const tmpA = ACTION_META[pAction] || { label: pAction, cls: 'sup-act-neutral', icon: 'fa-arrow-right' };
					return `<button class="sup-act ${tmpA.cls}" ${tmpState.Busy ? 'disabled' : ''} onclick="_Pict.providers.Support.performAction('${this._esc(pAction)}'); return false;">`
						+ `<i class="fas ${tmpA.icon}"></i> ${this._esc(tmpA.label)}</button>`;
				}).join('');
		}
		const tmpBusy = tmpState.Busy ? `<span class="sup-inline-busy"><i class="fas fa-spinner fa-spin"></i></span>` : '';
		// Managers can fold this ticket into another (duplicate / misfiled reply) — launches the merge wizard with this
		// call as the child. Hidden in create mode and once the call is itself voided.
		const tmpMergeBtn = (this.canManage() && tmpState.Active && tmpState.FormMode !== 'create' && tmpStatus !== 'Void')
			? `<button class="sup-act sup-act-neutral" ${tmpState.Busy ? 'disabled' : ''} onclick="_Pict.providers.Support.openMergeCurrent(); return false;"><i class="fas fa-code-merge"></i> Merge into another ticket…</button>`
			: '';
		// Collapsible "Status" section: thin header (title + live status badge + lock + chevron) over the action buttons.
		const tmpCollapsed = this.state.ApprovalCollapsed === true;
		return `<div class="sup-sec${tmpCollapsed ? ' sup-sec-collapsed' : ''}">`
			+ `<button type="button" class="sup-sec-head" onclick="_Pict.providers.Support.toggleApprovalCollapsed(); return false;">`
			+   `<span class="sup-sec-title">Status</span>`
			+   `<span class="sup-badge ${tmpMeta.cls}"><i class="fas ${tmpMeta.icon}"></i> ${tmpMeta.label}</span>`
			+   (tmpApproval && tmpApproval.Locked ? `<span class="sup-lock" title="Locked"><i class="fas fa-lock"></i></span>` : '')
			+   `<i class="fas fa-chevron-down sup-sec-chev"></i></button>`
			+ `<div class="sup-sec-body"><div class="sup-approval-actions">${tmpActions}${tmpMergeBtn}${tmpBusy}</div></div>`
			+ `</div>`;
	}
}

module.exports = SupportProvider;
module.exports.default_configuration = { ProviderIdentifier: 'Support' };
